// node_modules/@11ty/is-land/is-land.js
var Island = class _Island extends HTMLElement {
  static tagName = "is-land";
  static prefix = "is-land--";
  static attr = {
    template: "data-island",
    ready: "ready",
    defer: "defer-hydration"
  };
  static onceCache = /* @__PURE__ */ new Map();
  static onReady = /* @__PURE__ */ new Map();
  static fallback = {
    ":not(is-land,:defined,[defer-hydration])": (readyPromise, node, prefix2) => {
      let cloned = document.createElement(prefix2 + node.localName);
      for (let attr of node.getAttributeNames()) {
        cloned.setAttribute(attr, node.getAttribute(attr));
      }
      let shadowroot = node.shadowRoot;
      if (!shadowroot) {
        let tmpl = node.querySelector(":scope > template:is([shadowrootmode], [shadowroot])");
        if (tmpl) {
          let mode = tmpl.getAttribute("shadowrootmode") || tmpl.getAttribute("shadowroot") || "closed";
          shadowroot = node.attachShadow({ mode });
          shadowroot.appendChild(tmpl.content.cloneNode(true));
        }
      }
      if (shadowroot) {
        cloned.attachShadow({ mode: shadowroot.mode }).append(...shadowroot.childNodes);
      }
      cloned.append(...node.childNodes);
      node.replaceWith(cloned);
      return readyPromise.then(() => {
        if (cloned.shadowRoot) {
          node.shadowRoot.append(...cloned.shadowRoot.childNodes);
        }
        node.append(...cloned.childNodes);
        cloned.replaceWith(node);
      });
    }
  };
  constructor() {
    super();
    this.ready = new Promise((resolve) => {
      this.readyResolve = resolve;
    });
  }
  // any parents of `el` that are <is-land> (with conditions)
  static getParents(el, stopAt = false) {
    let nodes = [];
    while (el) {
      if (el.matches && el.matches(_Island.tagName)) {
        if (stopAt && el === stopAt) {
          break;
        }
        if (Conditions.hasConditions(el)) {
          nodes.push(el);
        }
      }
      el = el.parentNode;
    }
    return nodes;
  }
  static async ready(el, parents) {
    if (!parents) {
      parents = _Island.getParents(el);
    }
    if (parents.length === 0) {
      return;
    }
    let imports = await Promise.all(parents.map((p) => p.wait()));
    if (imports.length) {
      return imports[0];
    }
  }
  forceFallback() {
    if (window.Island) {
      Object.assign(_Island.fallback, window.Island.fallback);
    }
    for (let selector in _Island.fallback) {
      let components = Array.from(this.querySelectorAll(selector)).reverse();
      for (let node of components) {
        if (!node.isConnected) {
          continue;
        }
        let parents = _Island.getParents(node);
        if (parents.length === 1) {
          let p = _Island.ready(node, parents);
          _Island.fallback[selector](p, node, _Island.prefix);
        }
      }
    }
  }
  wait() {
    return this.ready;
  }
  async connectedCallback() {
    if (Conditions.hasConditions(this)) {
      this.forceFallback();
    }
    await this.hydrate();
  }
  getTemplates() {
    return this.querySelectorAll(`template[${_Island.attr.template}]`);
  }
  replaceTemplates(templates) {
    for (let node of templates) {
      if (_Island.getParents(node, this).length > 0) {
        continue;
      }
      let value = node.getAttribute(_Island.attr.template);
      if (value === "replace") {
        let children2 = Array.from(this.childNodes);
        for (let child2 of children2) {
          this.removeChild(child2);
        }
        this.appendChild(node.content);
        break;
      } else {
        let html = node.innerHTML;
        if (value === "once" && html) {
          if (_Island.onceCache.has(html)) {
            node.remove();
            return;
          }
          _Island.onceCache.set(html, true);
        }
        node.replaceWith(node.content);
      }
    }
  }
  async hydrate() {
    let conditions = [];
    if (this.parentNode) {
      conditions.push(_Island.ready(this.parentNode));
    }
    let attrs = Conditions.getConditions(this);
    for (let condition in attrs) {
      if (Conditions.map[condition]) {
        conditions.push(Conditions.map[condition](attrs[condition], this));
      }
    }
    await Promise.all(conditions);
    this.replaceTemplates(this.getTemplates());
    for (let fn of _Island.onReady.values()) {
      await fn.call(this, _Island);
    }
    this.readyResolve();
    this.setAttribute(_Island.attr.ready, "");
    this.querySelectorAll(`[${_Island.attr.defer}]`).forEach((node) => node.removeAttribute(_Island.attr.defer));
  }
};
var Conditions = class _Conditions {
  static map = {
    visible: _Conditions.visible,
    idle: _Conditions.idle,
    interaction: _Conditions.interaction,
    media: _Conditions.media,
    "save-data": _Conditions.saveData
  };
  static hasConditions(node) {
    return Object.keys(_Conditions.getConditions(node)).length > 0;
  }
  static getConditions(node) {
    let map = {};
    for (let key of Object.keys(_Conditions.map)) {
      if (node.hasAttribute(`on:${key}`)) {
        map[key] = node.getAttribute(`on:${key}`);
      }
    }
    return map;
  }
  static visible(noop3, el) {
    if (!("IntersectionObserver" in window)) {
      return;
    }
    return new Promise((resolve) => {
      let observer2 = new IntersectionObserver((entries) => {
        let [entry] = entries;
        if (entry.isIntersecting) {
          observer2.unobserve(entry.target);
          resolve();
        }
      });
      observer2.observe(el);
    });
  }
  // Warning: on:idle is not very useful with other conditions as it may resolve long before.
  static idle() {
    let onload = new Promise((resolve) => {
      if (document.readyState !== "complete") {
        window.addEventListener("load", () => resolve(), { once: true });
      } else {
        resolve();
      }
    });
    if (!("requestIdleCallback" in window)) {
      return onload;
    }
    return Promise.all([
      new Promise((resolve) => {
        requestIdleCallback(() => {
          resolve();
        });
      }),
      onload
    ]);
  }
  static interaction(eventOverrides, el) {
    let events = ["click", "touchstart"];
    if (eventOverrides) {
      events = (eventOverrides || "").split(",").map((entry) => entry.trim());
    }
    return new Promise((resolve) => {
      function resolveFn(e) {
        resolve();
        for (let name of events) {
          el.removeEventListener(name, resolveFn);
        }
      }
      for (let name of events) {
        el.addEventListener(name, resolveFn, { once: true });
      }
    });
  }
  static media(query2) {
    let mm = {
      matches: true
    };
    if (query2 && "matchMedia" in window) {
      mm = window.matchMedia(query2);
    }
    if (mm.matches) {
      return;
    }
    return new Promise((resolve) => {
      mm.addListener((e) => {
        if (e.matches) {
          resolve();
        }
      });
    });
  }
  static saveData(expects) {
    if (!("connection" in navigator) || navigator.connection.saveData === (expects !== "false")) {
      return;
    }
    return new Promise(() => {
    });
  }
};
if ("customElements" in window) {
  window.customElements.define(Island.tagName, Island);
  window.Island = Island;
}
var ready = Island.ready;

// node_modules/alpinejs/dist/module.esm.js
var flushPending = false;
var flushing = false;
var queue = [];
var lastFlushedIndex = -1;
var transactionActive = false;
function scheduler(callback) {
  queueJob(callback);
}
function startTransaction() {
  transactionActive = true;
}
function commitTransaction() {
  transactionActive = false;
  queueFlush();
}
function queueJob(job) {
  if (!queue.includes(job))
    queue.push(job);
  queueFlush();
}
function dequeueJob(job) {
  let index = queue.indexOf(job);
  if (index !== -1 && index > lastFlushedIndex)
    queue.splice(index, 1);
}
function queueFlush() {
  if (!flushing && !flushPending) {
    if (transactionActive)
      return;
    flushPending = true;
    queueMicrotask(flushJobs);
  }
}
function flushJobs() {
  flushPending = false;
  flushing = true;
  for (let i = 0; i < queue.length; i++) {
    queue[i]();
    lastFlushedIndex = i;
  }
  queue.length = 0;
  lastFlushedIndex = -1;
  flushing = false;
}
var reactive;
var effect;
var release;
var raw;
var shouldSchedule = true;
function disableEffectScheduling(callback) {
  shouldSchedule = false;
  callback();
  shouldSchedule = true;
}
function setReactivityEngine(engine) {
  reactive = engine.reactive;
  release = engine.release;
  effect = (callback) => engine.effect(callback, { scheduler: (task) => {
    if (shouldSchedule) {
      scheduler(task);
    } else {
      task();
    }
  } });
  raw = engine.raw;
}
function overrideEffect(override) {
  effect = override;
}
function elementBoundEffect(el) {
  let cleanup2 = () => {
  };
  let wrappedEffect = (callback) => {
    let effectReference = effect(callback);
    if (!el._x_effects) {
      el._x_effects = /* @__PURE__ */ new Set();
      el._x_runEffects = () => {
        el._x_effects.forEach((i) => i());
      };
    }
    el._x_effects.add(effectReference);
    cleanup2 = () => {
      if (effectReference === void 0)
        return;
      el._x_effects.delete(effectReference);
      release(effectReference);
    };
    return effectReference;
  };
  return [wrappedEffect, () => {
    cleanup2();
  }];
}
function watch(getter, callback) {
  let firstTime = true;
  let oldValue;
  let oldValueJSON;
  let effectReference = effect(() => {
    let value = getter();
    let newJSON = JSON.stringify(value);
    if (!firstTime) {
      if (typeof value === "object" || value !== oldValue) {
        let previousValue = typeof oldValue === "object" ? JSON.parse(oldValueJSON) : oldValue;
        queueMicrotask(() => {
          callback(value, previousValue);
        });
      }
    }
    oldValue = value;
    oldValueJSON = newJSON;
    firstTime = false;
  });
  return () => release(effectReference);
}
async function transaction(callback) {
  startTransaction();
  try {
    await callback();
    await Promise.resolve();
  } finally {
    commitTransaction();
  }
}
var onAttributeAddeds = [];
var onElRemoveds = [];
var onElAddeds = [];
function onElAdded(callback) {
  onElAddeds.push(callback);
}
function onElRemoved(el, callback) {
  if (typeof callback === "function") {
    if (!el._x_cleanups)
      el._x_cleanups = [];
    el._x_cleanups.push(callback);
  } else {
    callback = el;
    onElRemoveds.push(callback);
  }
}
function onAttributesAdded(callback) {
  onAttributeAddeds.push(callback);
}
function onAttributeRemoved(el, name, callback) {
  if (!el._x_attributeCleanups)
    el._x_attributeCleanups = {};
  if (!el._x_attributeCleanups[name])
    el._x_attributeCleanups[name] = [];
  el._x_attributeCleanups[name].push(callback);
}
function cleanupAttributes(el, names) {
  if (!el._x_attributeCleanups)
    return;
  Object.entries(el._x_attributeCleanups).forEach(([name, value]) => {
    if (names === void 0 || names.includes(name)) {
      value.forEach((i) => i());
      delete el._x_attributeCleanups[name];
    }
  });
}
function cleanupElement(el) {
  el._x_effects?.forEach(dequeueJob);
  while (el._x_cleanups?.length)
    el._x_cleanups.pop()();
}
var observer = new MutationObserver(onMutate);
var currentlyObserving = false;
function startObservingMutations() {
  observer.observe(document, { subtree: true, childList: true, attributes: true, attributeOldValue: true });
  currentlyObserving = true;
}
function stopObservingMutations() {
  flushObserver();
  observer.disconnect();
  currentlyObserving = false;
}
var queuedMutations = [];
function flushObserver() {
  let records = observer.takeRecords();
  queuedMutations.push(() => records.length > 0 && onMutate(records));
  let queueLengthWhenTriggered = queuedMutations.length;
  queueMicrotask(() => {
    if (queuedMutations.length === queueLengthWhenTriggered) {
      while (queuedMutations.length > 0)
        queuedMutations.shift()();
    }
  });
}
function mutateDom(callback) {
  if (!currentlyObserving)
    return callback();
  stopObservingMutations();
  let result = callback();
  startObservingMutations();
  return result;
}
var isCollecting = false;
var deferredMutations = [];
function deferMutations() {
  isCollecting = true;
}
function flushAndStopDeferringMutations() {
  isCollecting = false;
  onMutate(deferredMutations);
  deferredMutations = [];
}
function onMutate(mutations) {
  if (isCollecting) {
    deferredMutations = deferredMutations.concat(mutations);
    return;
  }
  let addedNodes = [];
  let removedNodes = /* @__PURE__ */ new Set();
  let addedAttributes = /* @__PURE__ */ new Map();
  let removedAttributes = /* @__PURE__ */ new Map();
  for (let i = 0; i < mutations.length; i++) {
    if (mutations[i].target._x_ignoreMutationObserver)
      continue;
    if (mutations[i].type === "childList") {
      mutations[i].removedNodes.forEach((node) => {
        if (node.nodeType !== 1)
          return;
        if (!node._x_marker)
          return;
        removedNodes.add(node);
      });
      mutations[i].addedNodes.forEach((node) => {
        if (node.nodeType !== 1)
          return;
        if (removedNodes.has(node)) {
          removedNodes.delete(node);
          return;
        }
        if (node._x_marker)
          return;
        addedNodes.push(node);
      });
    }
    if (mutations[i].type === "attributes") {
      let el = mutations[i].target;
      let name = mutations[i].attributeName;
      let oldValue = mutations[i].oldValue;
      let add2 = () => {
        if (!addedAttributes.has(el))
          addedAttributes.set(el, []);
        addedAttributes.get(el).push({ name, value: el.getAttribute(name) });
      };
      let remove2 = () => {
        if (!removedAttributes.has(el))
          removedAttributes.set(el, []);
        removedAttributes.get(el).push(name);
      };
      if (el.hasAttribute(name) && oldValue === null) {
        add2();
      } else if (el.hasAttribute(name)) {
        remove2();
        add2();
      } else {
        remove2();
      }
    }
  }
  removedAttributes.forEach((attrs, el) => {
    cleanupAttributes(el, attrs);
  });
  addedAttributes.forEach((attrs, el) => {
    onAttributeAddeds.forEach((i) => i(el, attrs));
  });
  for (let node of removedNodes) {
    if (addedNodes.some((i) => i.contains(node)))
      continue;
    onElRemoveds.forEach((i) => i(node));
  }
  for (let node of addedNodes) {
    if (!node.isConnected)
      continue;
    onElAddeds.forEach((i) => i(node));
  }
  addedNodes = null;
  removedNodes = null;
  addedAttributes = null;
  removedAttributes = null;
}
function scope(node) {
  return mergeProxies(closestDataStack(node));
}
function addScopeToNode(node, data2, referenceNode) {
  node._x_dataStack = [data2, ...closestDataStack(referenceNode || node)];
  return () => {
    node._x_dataStack = node._x_dataStack.filter((i) => i !== data2);
  };
}
function closestDataStack(node) {
  if (node._x_dataStack)
    return node._x_dataStack;
  if (typeof ShadowRoot === "function" && node instanceof ShadowRoot) {
    return closestDataStack(node.host);
  }
  if (!node.parentNode) {
    return [];
  }
  return closestDataStack(node.parentNode);
}
function mergeProxies(objects) {
  return new Proxy({ objects }, mergeProxyTrap);
}
function keyInPrototypeChain(obj, key) {
  if (obj === null || obj === Object.prototype)
    return null;
  if (Object.prototype.hasOwnProperty.call(obj, key))
    return obj;
  return keyInPrototypeChain(Object.getPrototypeOf(obj), key);
}
var mergeProxyTrap = {
  ownKeys({ objects }) {
    return Array.from(
      new Set(objects.flatMap((i) => Object.keys(i)))
    );
  },
  has({ objects }, name) {
    if (name == Symbol.unscopables)
      return false;
    return objects.some(
      (obj) => Object.prototype.hasOwnProperty.call(obj, name) || Reflect.has(obj, name)
    );
  },
  get({ objects }, name, thisProxy) {
    if (name == "toJSON")
      return collapseProxies;
    return Reflect.get(
      objects.find(
        (obj) => Reflect.has(obj, name)
      ) || {},
      name,
      thisProxy
    );
  },
  set({ objects }, name, value, thisProxy) {
    let target;
    for (const obj of objects) {
      target = keyInPrototypeChain(obj, name);
      if (target)
        break;
    }
    if (!target)
      target = objects[objects.length - 1];
    const descriptor = Object.getOwnPropertyDescriptor(target, name);
    if (descriptor?.set && descriptor?.get)
      return descriptor.set.call(thisProxy, value) || true;
    return Reflect.set(target, name, value);
  }
};
function collapseProxies() {
  let keys = Reflect.ownKeys(this);
  return keys.reduce((acc, key) => {
    acc[key] = Reflect.get(this, key);
    return acc;
  }, {});
}
function initInterceptors(data2, cleanup2 = () => {
}) {
  let isObject32 = (val) => typeof val === "object" && !Array.isArray(val) && val !== null;
  let recurse = (obj, basePath = "") => {
    Object.entries(Object.getOwnPropertyDescriptors(obj)).forEach(([key, { value, enumerable }]) => {
      if (enumerable === false || value === void 0)
        return;
      if (typeof value === "object" && value !== null && value.__v_skip)
        return;
      let path = basePath === "" ? key : `${basePath}.${key}`;
      if (typeof value === "object" && value !== null && value._x_interceptor) {
        obj[key] = value.initialize(data2, path, key, cleanup2);
      } else {
        if (isObject32(value) && value !== obj && !(value instanceof Element)) {
          recurse(value, path);
        }
      }
    });
  };
  return recurse(data2);
}
function interceptor(callback, mutateObj = () => {
}) {
  let obj = {
    initialValue: void 0,
    _x_interceptor: true,
    initialize(data2, path, key, cleanup2) {
      return callback(this.initialValue, () => get(data2, path), (value) => set(data2, path, value), path, key, cleanup2);
    }
  };
  mutateObj(obj);
  return (initialValue) => {
    if (typeof initialValue === "object" && initialValue !== null && initialValue._x_interceptor) {
      let initialize = obj.initialize.bind(obj);
      obj.initialize = (data2, path, key, cleanup2) => {
        let innerValue = initialValue.initialize(data2, path, key, cleanup2);
        obj.initialValue = innerValue;
        return initialize(data2, path, key, cleanup2);
      };
    } else {
      obj.initialValue = initialValue;
    }
    return obj;
  };
}
function get(obj, path) {
  return path.split(".").reduce((carry, segment) => carry[segment], obj);
}
function set(obj, path, value) {
  if (typeof path === "string")
    path = path.split(".");
  if (path.length === 1)
    obj[path[0]] = value;
  else if (path.length === 0)
    throw error;
  else {
    if (obj[path[0]])
      return set(obj[path[0]], path.slice(1), value);
    else {
      obj[path[0]] = {};
      return set(obj[path[0]], path.slice(1), value);
    }
  }
}
var magics = {};
function magic(name, callback) {
  magics[name] = callback;
}
function injectMagics(obj, el) {
  let memoizedUtilities = getUtilities(el);
  Object.entries(magics).forEach(([name, callback]) => {
    Object.defineProperty(obj, `$${name}`, {
      get() {
        return callback(el, memoizedUtilities);
      },
      enumerable: false
    });
  });
  return obj;
}
function getUtilities(el) {
  let [utilities, cleanup2] = getElementBoundUtilities(el);
  let utils = { interceptor, ...utilities };
  onElRemoved(el, cleanup2);
  return utils;
}
function tryCatch(el, expression, callback, ...args) {
  try {
    return callback(...args);
  } catch (e) {
    handleError(e, el, expression);
  }
}
function handleError(...args) {
  return errorHandler(...args);
}
var errorHandler = normalErrorHandler;
function setErrorHandler(handler4) {
  errorHandler = handler4;
}
function normalErrorHandler(error2, el, expression = void 0) {
  error2 = Object.assign(
    error2 ?? { message: "No error message given." },
    { el, expression }
  );
  console.warn(`Alpine Expression Error: ${error2.message}

${expression ? 'Expression: "' + expression + '"\n\n' : ""}`, el);
  setTimeout(() => {
    throw error2;
  }, 0);
}
var shouldAutoEvaluateFunctions = true;
function dontAutoEvaluateFunctions(callback) {
  let cache = shouldAutoEvaluateFunctions;
  shouldAutoEvaluateFunctions = false;
  let result = callback();
  shouldAutoEvaluateFunctions = cache;
  return result;
}
function evaluate(el, expression, extras = {}) {
  let result;
  evaluateLater(el, expression)((value) => result = value, extras);
  return result;
}
function evaluateLater(...args) {
  return theEvaluatorFunction(...args);
}
var theEvaluatorFunction = () => {
};
function setEvaluator(newEvaluator) {
  theEvaluatorFunction = newEvaluator;
}
var theRawEvaluatorFunction;
function setRawEvaluator(newEvaluator) {
  theRawEvaluatorFunction = newEvaluator;
}
function normalEvaluator(el, expression) {
  let overriddenMagics = {};
  injectMagics(overriddenMagics, el);
  let dataStack = [overriddenMagics, ...closestDataStack(el)];
  let evaluator = typeof expression === "function" ? generateEvaluatorFromFunction(dataStack, expression) : generateEvaluatorFromString(dataStack, expression, el);
  return tryCatch.bind(null, el, expression, evaluator);
}
function generateEvaluatorFromFunction(dataStack, func) {
  return (receiver = () => {
  }, { scope: scope2 = {}, params = [], context } = {}) => {
    if (!shouldAutoEvaluateFunctions) {
      runIfTypeOfFunction(receiver, func, mergeProxies([scope2, ...dataStack]), params);
      return;
    }
    let result = func.apply(mergeProxies([scope2, ...dataStack]), params);
    runIfTypeOfFunction(receiver, result);
  };
}
var evaluatorMemo = {};
function generateFunctionFromString(expression, el) {
  if (evaluatorMemo[expression]) {
    return evaluatorMemo[expression];
  }
  let AsyncFunction = Object.getPrototypeOf(async function() {
  }).constructor;
  let rightSideSafeExpression = /^[\n\s]*if.*\(.*\)/.test(expression.trim()) || /^(let|const)\s/.test(expression.trim()) ? `(async()=>{ ${expression} })()` : expression;
  const safeAsyncFunction = () => {
    try {
      let func2 = new AsyncFunction(
        ["__self", "scope"],
        `with (scope) { __self.result = ${rightSideSafeExpression} }; __self.finished = true; return __self.result;`
      );
      Object.defineProperty(func2, "name", {
        value: `[Alpine] ${expression}`
      });
      return func2;
    } catch (error2) {
      handleError(error2, el, expression);
      return Promise.resolve();
    }
  };
  let func = safeAsyncFunction();
  evaluatorMemo[expression] = func;
  return func;
}
function generateEvaluatorFromString(dataStack, expression, el) {
  let func = generateFunctionFromString(expression, el);
  return (receiver = () => {
  }, { scope: scope2 = {}, params = [], context } = {}) => {
    func.result = void 0;
    func.finished = false;
    let completeScope = mergeProxies([scope2, ...dataStack]);
    if (typeof func === "function") {
      let promise = func.call(context, func, completeScope).catch((error2) => handleError(error2, el, expression));
      if (func.finished) {
        runIfTypeOfFunction(receiver, func.result, completeScope, params, el);
        func.result = void 0;
      } else {
        promise.then((result) => {
          runIfTypeOfFunction(receiver, result, completeScope, params, el);
        }).catch((error2) => handleError(error2, el, expression)).finally(() => func.result = void 0);
      }
    }
  };
}
function runIfTypeOfFunction(receiver, value, scope2, params, el) {
  if (shouldAutoEvaluateFunctions && typeof value === "function") {
    let result = value.apply(scope2, params);
    if (result instanceof Promise) {
      result.then((i) => runIfTypeOfFunction(receiver, i, scope2, params)).catch((error2) => handleError(error2, el, value));
    } else {
      receiver(result);
    }
  } else if (typeof value === "object" && value instanceof Promise) {
    value.then((i) => receiver(i));
  } else {
    receiver(value);
  }
}
function evaluateRaw(...args) {
  return theRawEvaluatorFunction(...args);
}
function normalRawEvaluator(el, expression, extras = {}) {
  let overriddenMagics = {};
  injectMagics(overriddenMagics, el);
  let dataStack = [overriddenMagics, ...closestDataStack(el)];
  let scope2 = mergeProxies([extras.scope ?? {}, ...dataStack]);
  let params = extras.params ?? [];
  if (expression.includes("await")) {
    let AsyncFunction = Object.getPrototypeOf(async function() {
    }).constructor;
    let rightSideSafeExpression = /^[\n\s]*if.*\(.*\)/.test(expression.trim()) || /^(let|const)\s/.test(expression.trim()) ? `(async()=>{ ${expression} })()` : expression;
    let func = new AsyncFunction(
      ["scope"],
      `with (scope) { let __result = ${rightSideSafeExpression}; return __result }`
    );
    let result = func.call(extras.context, scope2);
    return result;
  } else {
    let rightSideSafeExpression = /^[\n\s]*if.*\(.*\)/.test(expression.trim()) || /^(let|const)\s/.test(expression.trim()) ? `(()=>{ ${expression} })()` : expression;
    let func = new Function(
      ["scope"],
      `with (scope) { let __result = ${rightSideSafeExpression}; return __result }`
    );
    let result = func.call(extras.context, scope2);
    if (typeof result === "function" && shouldAutoEvaluateFunctions) {
      return result.apply(scope2, params);
    }
    return result;
  }
}
var prefixAsString = "x-";
function prefix(subject = "") {
  return prefixAsString + subject;
}
function setPrefix(newPrefix) {
  prefixAsString = newPrefix;
}
var directiveHandlers = {};
function directive(name, callback) {
  directiveHandlers[name] = callback;
  return {
    before(directive2) {
      if (!directiveHandlers[directive2]) {
        console.warn(String.raw`Cannot find directive \`${directive2}\`. \`${name}\` will use the default order of execution`);
        return;
      }
      const pos = directiveOrder.indexOf(directive2);
      directiveOrder.splice(pos >= 0 ? pos : directiveOrder.indexOf("DEFAULT"), 0, name);
    }
  };
}
function directiveExists(name) {
  return Object.keys(directiveHandlers).includes(name);
}
function directives(el, attributes, originalAttributeOverride) {
  attributes = Array.from(attributes);
  if (el._x_virtualDirectives) {
    let vAttributes = Object.entries(el._x_virtualDirectives).map(([name, value]) => ({ name, value }));
    let staticAttributes = attributesOnly(vAttributes);
    vAttributes = vAttributes.map((attribute) => {
      if (staticAttributes.find((attr) => attr.name === attribute.name)) {
        return {
          name: `x-bind:${attribute.name}`,
          value: `"${attribute.value}"`
        };
      }
      return attribute;
    });
    attributes = attributes.concat(vAttributes);
  }
  let transformedAttributeMap = {};
  let directives2 = attributes.map(toTransformedAttributes((newName, oldName) => transformedAttributeMap[newName] = oldName)).filter(outNonAlpineAttributes).map(toParsedDirectives(transformedAttributeMap, originalAttributeOverride)).sort(byPriority);
  return directives2.map((directive2) => {
    return getDirectiveHandler(el, directive2);
  });
}
function attributesOnly(attributes) {
  return Array.from(attributes).map(toTransformedAttributes()).filter((attr) => !outNonAlpineAttributes(attr));
}
var isDeferringHandlers = false;
var directiveHandlerStacks = /* @__PURE__ */ new Map();
var currentHandlerStackKey = Symbol();
function deferHandlingDirectives(callback) {
  isDeferringHandlers = true;
  let key = Symbol();
  currentHandlerStackKey = key;
  directiveHandlerStacks.set(key, []);
  let flushHandlers = () => {
    while (directiveHandlerStacks.get(key).length)
      directiveHandlerStacks.get(key).shift()();
    directiveHandlerStacks.delete(key);
  };
  let stopDeferring = () => {
    isDeferringHandlers = false;
    flushHandlers();
  };
  callback(flushHandlers);
  stopDeferring();
}
function getElementBoundUtilities(el) {
  let cleanups = [];
  let cleanup2 = (callback) => cleanups.push(callback);
  let [effect3, cleanupEffect] = elementBoundEffect(el);
  cleanups.push(cleanupEffect);
  let utilities = {
    Alpine: alpine_default,
    effect: effect3,
    cleanup: cleanup2,
    evaluateLater: evaluateLater.bind(evaluateLater, el),
    evaluate: evaluate.bind(evaluate, el)
  };
  let doCleanup = () => cleanups.forEach((i) => i());
  return [utilities, doCleanup];
}
function getDirectiveHandler(el, directive2) {
  let noop3 = () => {
  };
  let handler4 = directiveHandlers[directive2.type] || noop3;
  let [utilities, cleanup2] = getElementBoundUtilities(el);
  onAttributeRemoved(el, directive2.original, cleanup2);
  let fullHandler = () => {
    if (el._x_ignore || el._x_ignoreSelf)
      return;
    handler4.inline && handler4.inline(el, directive2, utilities);
    handler4 = handler4.bind(handler4, el, directive2, utilities);
    isDeferringHandlers ? directiveHandlerStacks.get(currentHandlerStackKey).push(handler4) : handler4();
  };
  fullHandler.runCleanups = cleanup2;
  return fullHandler;
}
var startingWith = (subject, replacement) => ({ name, value }) => {
  if (name.startsWith(subject))
    name = name.replace(subject, replacement);
  return { name, value };
};
var into = (i) => i;
function toTransformedAttributes(callback = () => {
}) {
  return ({ name, value }) => {
    let { name: newName, value: newValue } = attributeTransformers.reduce((carry, transform) => {
      return transform(carry);
    }, { name, value });
    if (newName !== name)
      callback(newName, name);
    return { name: newName, value: newValue };
  };
}
var attributeTransformers = [];
function mapAttributes(callback) {
  attributeTransformers.push(callback);
}
function outNonAlpineAttributes({ name }) {
  return alpineAttributeRegex().test(name);
}
var alpineAttributeRegex = () => new RegExp(`^${prefixAsString}([^:^.]+)\\b`);
function toParsedDirectives(transformedAttributeMap, originalAttributeOverride) {
  return ({ name, value }) => {
    if (name === value)
      value = "";
    let typeMatch = name.match(alpineAttributeRegex());
    let valueMatch = name.match(/:([a-zA-Z0-9\-_:]+)/);
    let modifiers = name.match(/\.[^.\]]+(?=[^\]]*$)/g) || [];
    let original = originalAttributeOverride || transformedAttributeMap[name] || name;
    return {
      type: typeMatch ? typeMatch[1] : null,
      value: valueMatch ? valueMatch[1] : null,
      modifiers: modifiers.map((i) => i.replace(".", "")),
      expression: value,
      original
    };
  };
}
var DEFAULT = "DEFAULT";
var directiveOrder = [
  "ignore",
  "ref",
  "id",
  "data",
  "anchor",
  "bind",
  "init",
  "for",
  "model",
  "modelable",
  "transition",
  "show",
  "if",
  DEFAULT,
  "teleport"
];
function byPriority(a, b) {
  let typeA = directiveOrder.indexOf(a.type) === -1 ? DEFAULT : a.type;
  let typeB = directiveOrder.indexOf(b.type) === -1 ? DEFAULT : b.type;
  return directiveOrder.indexOf(typeA) - directiveOrder.indexOf(typeB);
}
function dispatch(el, name, detail = {}, options = {}) {
  return el.dispatchEvent(
    new CustomEvent(name, {
      detail,
      bubbles: true,
      // Allows events to pass the shadow DOM barrier.
      composed: true,
      cancelable: true,
      // Allows overriding the default event options.
      ...options
    })
  );
}
function walk(el, callback) {
  if (typeof ShadowRoot === "function" && el instanceof ShadowRoot) {
    Array.from(el.children).forEach((el2) => walk(el2, callback));
    return;
  }
  let skip = false;
  callback(el, () => skip = true);
  if (skip)
    return;
  let node = el.firstElementChild;
  while (node) {
    walk(node, callback, false);
    node = node.nextElementSibling;
  }
}
function warn(message, ...args) {
  console.warn(`Alpine Warning: ${message}`, ...args);
}
var started = false;
function start() {
  if (started)
    warn("Alpine has already been initialized on this page. Calling Alpine.start() more than once can cause problems.");
  started = true;
  if (!document.body)
    warn("Unable to initialize. Trying to load Alpine before `<body>` is available. Did you forget to add `defer` in Alpine's `<script>` tag?");
  dispatch(document, "alpine:init");
  dispatch(document, "alpine:initializing");
  startObservingMutations();
  onElAdded((el) => initTree(el, walk));
  onElRemoved((el) => destroyTree(el));
  onAttributesAdded((el, attrs) => {
    directives(el, attrs).forEach((handle) => handle());
  });
  let outNestedComponents = (el) => !closestRoot(el.parentElement, true);
  Array.from(document.querySelectorAll(allSelectors().join(","))).filter(outNestedComponents).forEach((el) => {
    initTree(el);
  });
  dispatch(document, "alpine:initialized");
  setTimeout(() => {
    warnAboutMissingPlugins();
  });
}
var rootSelectorCallbacks = [];
var initSelectorCallbacks = [];
function rootSelectors() {
  return rootSelectorCallbacks.map((fn) => fn());
}
function allSelectors() {
  return rootSelectorCallbacks.concat(initSelectorCallbacks).map((fn) => fn());
}
function addRootSelector(selectorCallback) {
  rootSelectorCallbacks.push(selectorCallback);
}
function addInitSelector(selectorCallback) {
  initSelectorCallbacks.push(selectorCallback);
}
function closestRoot(el, includeInitSelectors = false) {
  return findClosest(el, (element) => {
    const selectors = includeInitSelectors ? allSelectors() : rootSelectors();
    if (selectors.some((selector) => element.matches(selector)))
      return true;
  });
}
function findClosest(el, callback) {
  if (!el)
    return;
  if (callback(el))
    return el;
  if (el._x_teleportBack)
    return findClosest(el._x_teleportBack, callback);
  if (el.parentNode instanceof ShadowRoot) {
    return findClosest(el.parentNode.host, callback);
  }
  if (!el.parentElement)
    return;
  return findClosest(el.parentElement, callback);
}
function isRoot(el) {
  return rootSelectors().some((selector) => el.matches(selector));
}
var initInterceptors2 = [];
function interceptInit(callback) {
  initInterceptors2.push(callback);
}
var markerDispenser = 1;
function initTree(el, walker = walk, intercept = () => {
}) {
  if (findClosest(el, (i) => i._x_ignore))
    return;
  deferHandlingDirectives(() => {
    walker(el, (el2, skip) => {
      if (el2._x_marker)
        return;
      intercept(el2, skip);
      initInterceptors2.forEach((i) => i(el2, skip));
      directives(el2, el2.attributes).forEach((handle) => handle());
      if (!el2._x_ignore)
        el2._x_marker = markerDispenser++;
      el2._x_ignore && skip();
    });
  });
}
function destroyTree(root, walker = walk) {
  walker(root, (el) => {
    cleanupElement(el);
    cleanupAttributes(el);
    delete el._x_marker;
  });
}
function warnAboutMissingPlugins() {
  let pluginDirectives = [
    ["ui", "dialog", ["[x-dialog], [x-popover]"]],
    ["anchor", "anchor", ["[x-anchor]"]],
    ["sort", "sort", ["[x-sort]"]]
  ];
  pluginDirectives.forEach(([plugin2, directive2, selectors]) => {
    if (directiveExists(directive2))
      return;
    selectors.some((selector) => {
      if (document.querySelector(selector)) {
        warn(`found "${selector}", but missing ${plugin2} plugin`);
        return true;
      }
    });
  });
}
var tickStack = [];
var isHolding = false;
function nextTick(callback = () => {
}) {
  queueMicrotask(() => {
    isHolding || setTimeout(() => {
      releaseNextTicks();
    });
  });
  return new Promise((res) => {
    tickStack.push(() => {
      callback();
      res();
    });
  });
}
function releaseNextTicks() {
  isHolding = false;
  while (tickStack.length)
    tickStack.shift()();
}
function holdNextTicks() {
  isHolding = true;
}
function setClasses(el, value) {
  if (Array.isArray(value)) {
    return setClassesFromString(el, value.join(" "));
  } else if (typeof value === "object" && value !== null) {
    return setClassesFromObject(el, value);
  } else if (typeof value === "function") {
    return setClasses(el, value());
  }
  return setClassesFromString(el, value);
}
function splitClasses(classString) {
  return classString.split(/\s/).filter(Boolean);
}
function setClassesFromString(el, classString) {
  let missingClasses = (classString2) => splitClasses(classString2).filter((i) => !el.classList.contains(i)).filter(Boolean);
  let addClassesAndReturnUndo = (classes) => {
    el.classList.add(...classes);
    return () => {
      el.classList.remove(...classes);
    };
  };
  classString = classString === true ? classString = "" : classString || "";
  return addClassesAndReturnUndo(missingClasses(classString));
}
function setClassesFromObject(el, classObject) {
  let forAdd = Object.entries(classObject).flatMap(([classString, bool]) => bool ? splitClasses(classString) : false).filter(Boolean);
  let forRemove = Object.entries(classObject).flatMap(([classString, bool]) => !bool ? splitClasses(classString) : false).filter(Boolean);
  let added = [];
  let removed = [];
  forRemove.forEach((i) => {
    if (el.classList.contains(i)) {
      el.classList.remove(i);
      removed.push(i);
    }
  });
  forAdd.forEach((i) => {
    if (!el.classList.contains(i)) {
      el.classList.add(i);
      added.push(i);
    }
  });
  return () => {
    removed.forEach((i) => el.classList.add(i));
    added.forEach((i) => el.classList.remove(i));
  };
}
function setStyles(el, value) {
  if (typeof value === "object" && value !== null) {
    return setStylesFromObject(el, value);
  }
  return setStylesFromString(el, value);
}
function setStylesFromObject(el, value) {
  let previousStyles = {};
  Object.entries(value).forEach(([key, value2]) => {
    previousStyles[key] = el.style[key];
    if (!key.startsWith("--")) {
      key = kebabCase(key);
    }
    el.style.setProperty(key, value2);
  });
  setTimeout(() => {
    if (el.style.length === 0) {
      el.removeAttribute("style");
    }
  });
  return () => {
    setStyles(el, previousStyles);
  };
}
function setStylesFromString(el, value) {
  let cache = el.getAttribute("style", value);
  el.setAttribute("style", value);
  return () => {
    el.setAttribute("style", cache || "");
  };
}
function kebabCase(subject) {
  return subject.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase();
}
function once(callback, fallback = () => {
}) {
  let called = false;
  return function() {
    if (!called) {
      called = true;
      callback.apply(this, arguments);
    } else {
      fallback.apply(this, arguments);
    }
  };
}
directive("transition", (el, { value, modifiers, expression }, { evaluate: evaluate2 }) => {
  if (typeof expression === "function")
    expression = evaluate2(expression);
  if (expression === false)
    return;
  if (!expression || typeof expression === "boolean") {
    registerTransitionsFromHelper(el, modifiers, value);
  } else {
    registerTransitionsFromClassString(el, expression, value);
  }
});
function registerTransitionsFromClassString(el, classString, stage) {
  registerTransitionObject(el, setClasses, "");
  let directiveStorageMap = {
    "enter": (classes) => {
      el._x_transition.enter.during = classes;
    },
    "enter-start": (classes) => {
      el._x_transition.enter.start = classes;
    },
    "enter-end": (classes) => {
      el._x_transition.enter.end = classes;
    },
    "leave": (classes) => {
      el._x_transition.leave.during = classes;
    },
    "leave-start": (classes) => {
      el._x_transition.leave.start = classes;
    },
    "leave-end": (classes) => {
      el._x_transition.leave.end = classes;
    }
  };
  directiveStorageMap[stage](classString);
}
function registerTransitionsFromHelper(el, modifiers, stage) {
  registerTransitionObject(el, setStyles);
  let doesntSpecify = !modifiers.includes("in") && !modifiers.includes("out") && !stage;
  let transitioningIn = doesntSpecify || modifiers.includes("in") || ["enter"].includes(stage);
  let transitioningOut = doesntSpecify || modifiers.includes("out") || ["leave"].includes(stage);
  if (modifiers.includes("in") && !doesntSpecify) {
    modifiers = modifiers.filter((i, index) => index < modifiers.indexOf("out"));
  }
  if (modifiers.includes("out") && !doesntSpecify) {
    modifiers = modifiers.filter((i, index) => index > modifiers.indexOf("out"));
  }
  let wantsAll = !modifiers.includes("opacity") && !modifiers.includes("scale");
  let wantsOpacity = wantsAll || modifiers.includes("opacity");
  let wantsScale = wantsAll || modifiers.includes("scale");
  let opacityValue = wantsOpacity ? 0 : 1;
  let scaleValue = wantsScale ? modifierValue(modifiers, "scale", 95) / 100 : 1;
  let delay = modifierValue(modifiers, "delay", 0) / 1e3;
  let origin = modifierValue(modifiers, "origin", "center");
  let property = "opacity, transform";
  let durationIn = modifierValue(modifiers, "duration", 150) / 1e3;
  let durationOut = modifierValue(modifiers, "duration", 75) / 1e3;
  let easing = `cubic-bezier(0.4, 0.0, 0.2, 1)`;
  if (transitioningIn) {
    el._x_transition.enter.during = {
      transformOrigin: origin,
      transitionDelay: `${delay}s`,
      transitionProperty: property,
      transitionDuration: `${durationIn}s`,
      transitionTimingFunction: easing
    };
    el._x_transition.enter.start = {
      opacity: opacityValue,
      transform: `scale(${scaleValue})`
    };
    el._x_transition.enter.end = {
      opacity: 1,
      transform: `scale(1)`
    };
  }
  if (transitioningOut) {
    el._x_transition.leave.during = {
      transformOrigin: origin,
      transitionDelay: `${delay}s`,
      transitionProperty: property,
      transitionDuration: `${durationOut}s`,
      transitionTimingFunction: easing
    };
    el._x_transition.leave.start = {
      opacity: 1,
      transform: `scale(1)`
    };
    el._x_transition.leave.end = {
      opacity: opacityValue,
      transform: `scale(${scaleValue})`
    };
  }
}
function registerTransitionObject(el, setFunction, defaultValue = {}) {
  if (!el._x_transition)
    el._x_transition = {
      enter: { during: defaultValue, start: defaultValue, end: defaultValue },
      leave: { during: defaultValue, start: defaultValue, end: defaultValue },
      in(before2 = () => {
      }, after = () => {
      }) {
        transition(el, setFunction, {
          during: this.enter.during,
          start: this.enter.start,
          end: this.enter.end
        }, before2, after);
      },
      out(before2 = () => {
      }, after = () => {
      }) {
        transition(el, setFunction, {
          during: this.leave.during,
          start: this.leave.start,
          end: this.leave.end
        }, before2, after);
      }
    };
}
window.Element.prototype._x_toggleAndCascadeWithTransitions = function(el, value, show, hide) {
  const nextTick22 = document.visibilityState === "visible" ? requestAnimationFrame : setTimeout;
  let clickAwayCompatibleShow = () => nextTick22(show);
  if (value) {
    if (el._x_transition && (el._x_transition.enter || el._x_transition.leave)) {
      el._x_transition.enter && (Object.entries(el._x_transition.enter.during).length || Object.entries(el._x_transition.enter.start).length || Object.entries(el._x_transition.enter.end).length) ? el._x_transition.in(show) : clickAwayCompatibleShow();
    } else {
      el._x_transition ? el._x_transition.in(show) : clickAwayCompatibleShow();
    }
    return;
  }
  el._x_hidePromise = el._x_transition ? new Promise((resolve, reject) => {
    el._x_transition.out(() => {
    }, () => resolve(hide));
    el._x_transitioning && el._x_transitioning.beforeCancel(() => reject({ isFromCancelledTransition: true }));
  }) : Promise.resolve(hide);
  queueMicrotask(() => {
    let closest2 = closestHide(el);
    if (closest2) {
      if (!closest2._x_hideChildren)
        closest2._x_hideChildren = [];
      closest2._x_hideChildren.push(el);
    } else {
      nextTick22(() => {
        let hideAfterChildren = (el2) => {
          let carry = Promise.all([
            el2._x_hidePromise,
            ...(el2._x_hideChildren || []).map(hideAfterChildren)
          ]).then(([i]) => i?.());
          delete el2._x_hidePromise;
          delete el2._x_hideChildren;
          return carry;
        };
        hideAfterChildren(el).catch((e) => {
          if (!e.isFromCancelledTransition)
            throw e;
        });
      });
    }
  });
};
function closestHide(el) {
  let parent = el.parentNode;
  if (!parent)
    return;
  return parent._x_hidePromise ? parent : closestHide(parent);
}
function transition(el, setFunction, { during, start: start2, end } = {}, before2 = () => {
}, after = () => {
}) {
  if (el._x_transitioning)
    el._x_transitioning.cancel();
  if (Object.keys(during).length === 0 && Object.keys(start2).length === 0 && Object.keys(end).length === 0) {
    before2();
    after();
    return;
  }
  let undoStart, undoDuring, undoEnd;
  performTransition(el, {
    start() {
      undoStart = setFunction(el, start2);
    },
    during() {
      undoDuring = setFunction(el, during);
    },
    before: before2,
    end() {
      undoStart();
      undoEnd = setFunction(el, end);
    },
    after,
    cleanup() {
      undoDuring();
      undoEnd();
    }
  });
}
function performTransition(el, stages) {
  let interrupted, reachedBefore, reachedEnd;
  let finish = once(() => {
    mutateDom(() => {
      interrupted = true;
      if (!reachedBefore)
        stages.before();
      if (!reachedEnd) {
        stages.end();
        releaseNextTicks();
      }
      stages.after();
      if (el.isConnected)
        stages.cleanup();
      delete el._x_transitioning;
    });
  });
  el._x_transitioning = {
    beforeCancels: [],
    beforeCancel(callback) {
      this.beforeCancels.push(callback);
    },
    cancel: once(function() {
      while (this.beforeCancels.length) {
        this.beforeCancels.shift()();
      }
      ;
      finish();
    }),
    finish
  };
  mutateDom(() => {
    stages.start();
    stages.during();
  });
  holdNextTicks();
  requestAnimationFrame(() => {
    if (interrupted)
      return;
    let duration = Number(getComputedStyle(el).transitionDuration.replace(/,.*/, "").replace("s", "")) * 1e3;
    let delay = Number(getComputedStyle(el).transitionDelay.replace(/,.*/, "").replace("s", "")) * 1e3;
    if (duration === 0)
      duration = Number(getComputedStyle(el).animationDuration.replace("s", "")) * 1e3;
    mutateDom(() => {
      stages.before();
    });
    reachedBefore = true;
    requestAnimationFrame(() => {
      if (interrupted)
        return;
      mutateDom(() => {
        stages.end();
      });
      releaseNextTicks();
      setTimeout(el._x_transitioning.finish, duration + delay);
      reachedEnd = true;
    });
  });
}
function modifierValue(modifiers, key, fallback) {
  if (modifiers.indexOf(key) === -1)
    return fallback;
  const rawValue = modifiers[modifiers.indexOf(key) + 1];
  if (!rawValue)
    return fallback;
  if (key === "scale") {
    if (isNaN(rawValue))
      return fallback;
  }
  if (key === "duration" || key === "delay") {
    let match = rawValue.match(/([0-9]+)ms/);
    if (match)
      return match[1];
  }
  if (key === "origin") {
    if (["top", "right", "left", "center", "bottom"].includes(modifiers[modifiers.indexOf(key) + 2])) {
      return [rawValue, modifiers[modifiers.indexOf(key) + 2]].join(" ");
    }
  }
  return rawValue;
}
var isCloning = false;
function skipDuringClone(callback, fallback = () => {
}) {
  return (...args) => isCloning ? fallback(...args) : callback(...args);
}
function onlyDuringClone(callback) {
  return (...args) => isCloning && callback(...args);
}
var interceptors = [];
function interceptClone(callback) {
  interceptors.push(callback);
}
function cloneNode(from, to) {
  interceptors.forEach((i) => i(from, to));
  isCloning = true;
  dontRegisterReactiveSideEffects(() => {
    initTree(to, (el, callback) => {
      callback(el, () => {
      });
    });
  });
  isCloning = false;
}
var isCloningLegacy = false;
function clone(oldEl, newEl) {
  if (!newEl._x_dataStack)
    newEl._x_dataStack = oldEl._x_dataStack;
  isCloning = true;
  isCloningLegacy = true;
  dontRegisterReactiveSideEffects(() => {
    cloneTree(newEl);
  });
  isCloning = false;
  isCloningLegacy = false;
}
function cloneTree(el) {
  let hasRunThroughFirstEl = false;
  let shallowWalker = (el2, callback) => {
    walk(el2, (el3, skip) => {
      if (hasRunThroughFirstEl && isRoot(el3))
        return skip();
      hasRunThroughFirstEl = true;
      callback(el3, skip);
    });
  };
  initTree(el, shallowWalker);
}
function dontRegisterReactiveSideEffects(callback) {
  let cache = effect;
  overrideEffect((callback2, el) => {
    let storedEffect = cache(callback2);
    release(storedEffect);
    return () => {
    };
  });
  callback();
  overrideEffect(cache);
}
function bind(el, name, value, modifiers = []) {
  if (!el._x_bindings)
    el._x_bindings = reactive({});
  el._x_bindings[name] = value;
  name = modifiers.includes("camel") ? camelCase(name) : name;
  switch (name) {
    case "value":
      bindInputValue(el, value);
      break;
    case "style":
      bindStyles(el, value);
      break;
    case "class":
      bindClasses(el, value);
      break;
    case "selected":
    case "checked":
      bindAttributeAndProperty(el, name, value);
      break;
    default:
      bindAttribute(el, name, value);
      break;
  }
}
function bindInputValue(el, value) {
  if (isRadio(el)) {
    if (el.attributes.value === void 0) {
      el.value = value;
    }
  } else if (isCheckbox(el)) {
    if (Number.isInteger(value)) {
      el.value = value;
    } else if (!Array.isArray(value) && typeof value !== "boolean" && ![null, void 0].includes(value)) {
      el.value = String(value);
    } else {
      if (Array.isArray(value)) {
        el.checked = value.some((val) => checkedAttrLooseCompare(val, el.value));
      } else {
        el.checked = !!value;
      }
    }
  } else if (el.tagName === "SELECT") {
    updateSelect(el, value);
  } else if (el.tagName === "OPTION") {
    bindAttribute(el, "value", value);
  } else {
    if (el.value === value && (typeof value !== "object" || value === null))
      return;
    el.value = value === void 0 ? "" : value;
  }
}
function bindClasses(el, value) {
  if (el._x_undoAddedClasses)
    el._x_undoAddedClasses();
  el._x_undoAddedClasses = setClasses(el, value);
}
function bindStyles(el, value) {
  if (el._x_undoAddedStyles)
    el._x_undoAddedStyles();
  el._x_undoAddedStyles = setStyles(el, value);
}
function bindAttributeAndProperty(el, name, value) {
  bindAttribute(el, name, value);
  setPropertyIfChanged(el, name, value);
}
function bindAttribute(el, name, value) {
  if ([null, void 0, false].includes(value) && attributeShouldntBePreservedIfFalsy(name)) {
    el.removeAttribute(name);
  } else {
    if (isBooleanAttr(name))
      value = name;
    if (isObjectAttr(value))
      value = JSON.stringify(value);
    setIfChanged(el, name, value);
  }
}
function setIfChanged(el, attrName, value) {
  if (el.getAttribute(attrName) != value) {
    el.setAttribute(attrName, value);
  }
}
function setPropertyIfChanged(el, propName, value) {
  if (el[propName] !== value) {
    el[propName] = value;
  }
}
function updateSelect(el, value) {
  const arrayWrappedValue = [].concat(value).map((value2) => {
    return value2 + "";
  });
  Array.from(el.options).forEach((option) => {
    option.selected = arrayWrappedValue.includes(option.value);
  });
}
function camelCase(subject) {
  return subject.toLowerCase().replace(/-(\w)/g, (match, char) => char.toUpperCase());
}
function checkedAttrLooseCompare(valueA, valueB) {
  return valueA == valueB;
}
function safeParseBoolean(rawValue) {
  if ([1, "1", "true", "on", "yes", true].includes(rawValue)) {
    return true;
  }
  if ([0, "0", "false", "off", "no", false].includes(rawValue)) {
    return false;
  }
  return rawValue ? Boolean(rawValue) : null;
}
var booleanAttributes = /* @__PURE__ */ new Set([
  "allowfullscreen",
  "async",
  "autofocus",
  "autoplay",
  "checked",
  "controls",
  "default",
  "defer",
  "disabled",
  "formnovalidate",
  "inert",
  "ismap",
  "itemscope",
  "loop",
  "multiple",
  "muted",
  "nomodule",
  "novalidate",
  "open",
  "playsinline",
  "readonly",
  "required",
  "reversed",
  "selected",
  "shadowrootclonable",
  "shadowrootdelegatesfocus",
  "shadowrootserializable"
]);
function isBooleanAttr(attrName) {
  return booleanAttributes.has(attrName);
}
function attributeShouldntBePreservedIfFalsy(name) {
  return !["aria-pressed", "aria-checked", "aria-expanded", "aria-selected"].includes(name);
}
function isObjectAttr(value) {
  return typeof value === "object" && value !== null;
}
function getBinding(el, name, fallback) {
  if (el._x_bindings && el._x_bindings[name] !== void 0)
    return el._x_bindings[name];
  return getAttributeBinding(el, name, fallback);
}
function extractProp(el, name, fallback, extract = true) {
  if (el._x_bindings && el._x_bindings[name] !== void 0)
    return el._x_bindings[name];
  if (el._x_inlineBindings && el._x_inlineBindings[name] !== void 0) {
    let binding = el._x_inlineBindings[name];
    binding.extract = extract;
    return dontAutoEvaluateFunctions(() => {
      return evaluate(el, binding.expression);
    });
  }
  return getAttributeBinding(el, name, fallback);
}
function getAttributeBinding(el, name, fallback) {
  let attr = el.getAttribute(name);
  if (attr === null)
    return typeof fallback === "function" ? fallback() : fallback;
  if (attr === "")
    return true;
  if (isBooleanAttr(name)) {
    return !![name, "true"].includes(attr);
  }
  return attr;
}
function isCheckbox(el) {
  return el.type === "checkbox" || el.localName === "ui-checkbox" || el.localName === "ui-switch";
}
function isRadio(el) {
  return el.type === "radio" || el.localName === "ui-radio";
}
function debounce(func, wait) {
  let timeout;
  return function() {
    const context = this, args = arguments;
    const later = function() {
      timeout = null;
      func.apply(context, args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}
function throttle(func, limit) {
  let inThrottle;
  return function() {
    let context = this, args = arguments;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}
function entangle({ get: outerGet, set: outerSet }, { get: innerGet, set: innerSet }) {
  let firstRun = true;
  let outerHash;
  let innerHash;
  let reference = effect(() => {
    let outer = outerGet();
    let inner = innerGet();
    if (firstRun) {
      innerSet(cloneIfObject(outer));
      firstRun = false;
    } else {
      let outerHashLatest = JSON.stringify(outer);
      let innerHashLatest = JSON.stringify(inner);
      if (outerHashLatest !== outerHash) {
        innerSet(cloneIfObject(outer));
      } else if (outerHashLatest !== innerHashLatest) {
        outerSet(cloneIfObject(inner));
      } else {
      }
    }
    outerHash = JSON.stringify(outerGet());
    innerHash = JSON.stringify(innerGet());
  });
  return () => {
    release(reference);
  };
}
function cloneIfObject(value) {
  return typeof value === "object" ? JSON.parse(JSON.stringify(value)) : value;
}
function plugin(callback) {
  let callbacks = Array.isArray(callback) ? callback : [callback];
  callbacks.forEach((i) => i(alpine_default));
}
var stores = {};
var isReactive = false;
function store(name, value) {
  if (!isReactive) {
    stores = reactive(stores);
    isReactive = true;
  }
  if (value === void 0) {
    return stores[name];
  }
  stores[name] = value;
  if (typeof value === "object" && value !== null && value._x_interceptor) {
    stores[name] = value.initialize(stores, name, name, () => {
    });
  } else {
    initInterceptors(stores[name]);
  }
  if (typeof value === "object" && value !== null && value.hasOwnProperty("init") && typeof value.init === "function") {
    stores[name].init();
  }
}
function getStores() {
  return stores;
}
var binds = {};
function bind2(name, bindings) {
  let getBindings = typeof bindings !== "function" ? () => bindings : bindings;
  if (name instanceof Element) {
    return applyBindingsObject(name, getBindings());
  } else {
    binds[name] = getBindings;
  }
  return () => {
  };
}
function injectBindingProviders(obj) {
  Object.entries(binds).forEach(([name, callback]) => {
    Object.defineProperty(obj, name, {
      get() {
        return (...args) => {
          return callback(...args);
        };
      }
    });
  });
  return obj;
}
function applyBindingsObject(el, obj, original) {
  let cleanupRunners = [];
  while (cleanupRunners.length)
    cleanupRunners.pop()();
  let attributes = Object.entries(obj).map(([name, value]) => ({ name, value }));
  let staticAttributes = attributesOnly(attributes);
  attributes = attributes.map((attribute) => {
    if (staticAttributes.find((attr) => attr.name === attribute.name)) {
      return {
        name: `x-bind:${attribute.name}`,
        value: `"${attribute.value}"`
      };
    }
    return attribute;
  });
  directives(el, attributes, original).map((handle) => {
    cleanupRunners.push(handle.runCleanups);
    handle();
  });
  return () => {
    while (cleanupRunners.length)
      cleanupRunners.pop()();
  };
}
var datas = {};
function data(name, callback) {
  datas[name] = callback;
}
function injectDataProviders(obj, context) {
  Object.entries(datas).forEach(([name, callback]) => {
    Object.defineProperty(obj, name, {
      get() {
        return (...args) => {
          return callback.bind(context)(...args);
        };
      },
      enumerable: false
    });
  });
  return obj;
}
var Alpine2 = {
  get reactive() {
    return reactive;
  },
  get release() {
    return release;
  },
  get effect() {
    return effect;
  },
  get raw() {
    return raw;
  },
  get transaction() {
    return transaction;
  },
  version: "3.16.1",
  flushAndStopDeferringMutations,
  dontAutoEvaluateFunctions,
  disableEffectScheduling,
  startObservingMutations,
  stopObservingMutations,
  setReactivityEngine,
  onAttributeRemoved,
  onAttributesAdded,
  closestDataStack,
  skipDuringClone,
  onlyDuringClone,
  addRootSelector,
  addInitSelector,
  setErrorHandler,
  interceptClone,
  addScopeToNode,
  deferMutations,
  mapAttributes,
  evaluateLater,
  interceptInit,
  initInterceptors,
  injectMagics,
  setEvaluator,
  setRawEvaluator,
  mergeProxies,
  extractProp,
  findClosest,
  onElRemoved,
  closestRoot,
  destroyTree,
  interceptor,
  // INTERNAL: not public API and is subject to change without major release.
  transition,
  // INTERNAL
  setStyles,
  // INTERNAL
  mutateDom,
  directive,
  entangle,
  throttle,
  debounce,
  evaluate,
  evaluateRaw,
  initTree,
  nextTick,
  prefixed: prefix,
  prefix: setPrefix,
  plugin,
  magic,
  store,
  start,
  clone,
  // INTERNAL
  cloneNode,
  // INTERNAL
  bound: getBinding,
  $data: scope,
  watch,
  walk,
  data,
  bind: bind2
};
var alpine_default = Alpine2;
function makeMap(str, expectsLowerCase) {
  const map = /* @__PURE__ */ Object.create(null);
  const list = str.split(",");
  for (let i = 0; i < list.length; i++) {
    map[list[i]] = true;
  }
  return expectsLowerCase ? (val) => !!map[val.toLowerCase()] : (val) => !!map[val];
}
var specialBooleanAttrs = `itemscope,allowfullscreen,formnovalidate,ismap,nomodule,novalidate,readonly`;
var isBooleanAttr2 = /* @__PURE__ */ makeMap(specialBooleanAttrs + `,async,autofocus,autoplay,controls,default,defer,disabled,hidden,loop,open,required,reversed,scoped,seamless,checked,muted,multiple,selected`);
var EMPTY_OBJ = true ? Object.freeze({}) : {};
var EMPTY_ARR = true ? Object.freeze([]) : [];
var hasOwnProperty = Object.prototype.hasOwnProperty;
var hasOwn = (val, key) => hasOwnProperty.call(val, key);
var isArray = Array.isArray;
var isMap = (val) => toTypeString(val) === "[object Map]";
var isString = (val) => typeof val === "string";
var isSymbol = (val) => typeof val === "symbol";
var isObject = (val) => val !== null && typeof val === "object";
var objectToString = Object.prototype.toString;
var toTypeString = (value) => objectToString.call(value);
var toRawType = (value) => {
  return toTypeString(value).slice(8, -1);
};
var isIntegerKey = (key) => isString(key) && key !== "NaN" && key[0] !== "-" && "" + parseInt(key, 10) === key;
var cacheStringFunction = (fn) => {
  const cache = /* @__PURE__ */ Object.create(null);
  return (str) => {
    const hit = cache[str];
    return hit || (cache[str] = fn(str));
  };
};
var camelizeRE = /-(\w)/g;
var camelize = cacheStringFunction((str) => {
  return str.replace(camelizeRE, (_, c) => c ? c.toUpperCase() : "");
});
var hyphenateRE = /\B([A-Z])/g;
var hyphenate = cacheStringFunction((str) => str.replace(hyphenateRE, "-$1").toLowerCase());
var capitalize = cacheStringFunction((str) => str.charAt(0).toUpperCase() + str.slice(1));
var toHandlerKey = cacheStringFunction((str) => str ? `on${capitalize(str)}` : ``);
var hasChanged = (value, oldValue) => value !== oldValue && (value === value || oldValue === oldValue);
var targetMap = /* @__PURE__ */ new WeakMap();
var effectStack = [];
var activeEffect;
var ITERATE_KEY = Symbol(true ? "iterate" : "");
var MAP_KEY_ITERATE_KEY = Symbol(true ? "Map key iterate" : "");
function isEffect(fn) {
  return fn && fn._isEffect === true;
}
function effect2(fn, options = EMPTY_OBJ) {
  if (isEffect(fn)) {
    fn = fn.raw;
  }
  const effect3 = createReactiveEffect(fn, options);
  if (!options.lazy) {
    effect3();
  }
  return effect3;
}
function stop(effect3) {
  if (effect3.active) {
    cleanup(effect3);
    if (effect3.options.onStop) {
      effect3.options.onStop();
    }
    effect3.active = false;
  }
}
var uid = 0;
function createReactiveEffect(fn, options) {
  const effect3 = function reactiveEffect() {
    if (!effect3.active) {
      return fn();
    }
    if (!effectStack.includes(effect3)) {
      cleanup(effect3);
      try {
        enableTracking();
        effectStack.push(effect3);
        activeEffect = effect3;
        return fn();
      } finally {
        effectStack.pop();
        resetTracking();
        activeEffect = effectStack[effectStack.length - 1];
      }
    }
  };
  effect3.id = uid++;
  effect3.allowRecurse = !!options.allowRecurse;
  effect3._isEffect = true;
  effect3.active = true;
  effect3.raw = fn;
  effect3.deps = [];
  effect3.options = options;
  return effect3;
}
function cleanup(effect3) {
  const { deps } = effect3;
  if (deps.length) {
    for (let i = 0; i < deps.length; i++) {
      deps[i].delete(effect3);
    }
    deps.length = 0;
  }
}
var shouldTrack = true;
var trackStack = [];
function pauseTracking() {
  trackStack.push(shouldTrack);
  shouldTrack = false;
}
function enableTracking() {
  trackStack.push(shouldTrack);
  shouldTrack = true;
}
function resetTracking() {
  const last = trackStack.pop();
  shouldTrack = last === void 0 ? true : last;
}
function track(target, type, key) {
  if (!shouldTrack || activeEffect === void 0) {
    return;
  }
  let depsMap = targetMap.get(target);
  if (!depsMap) {
    targetMap.set(target, depsMap = /* @__PURE__ */ new Map());
  }
  let dep = depsMap.get(key);
  if (!dep) {
    depsMap.set(key, dep = /* @__PURE__ */ new Set());
  }
  if (!dep.has(activeEffect)) {
    dep.add(activeEffect);
    activeEffect.deps.push(dep);
    if (activeEffect.options.onTrack) {
      activeEffect.options.onTrack({
        effect: activeEffect,
        target,
        type,
        key
      });
    }
  }
}
function trigger(target, type, key, newValue, oldValue, oldTarget) {
  const depsMap = targetMap.get(target);
  if (!depsMap) {
    return;
  }
  const effects = /* @__PURE__ */ new Set();
  const add2 = (effectsToAdd) => {
    if (effectsToAdd) {
      effectsToAdd.forEach((effect3) => {
        if (effect3 !== activeEffect || effect3.allowRecurse) {
          effects.add(effect3);
        }
      });
    }
  };
  if (type === "clear") {
    depsMap.forEach(add2);
  } else if (key === "length" && isArray(target)) {
    depsMap.forEach((dep, key2) => {
      if (key2 === "length" || key2 >= newValue) {
        add2(dep);
      }
    });
  } else {
    if (key !== void 0) {
      add2(depsMap.get(key));
    }
    switch (type) {
      case "add":
        if (!isArray(target)) {
          add2(depsMap.get(ITERATE_KEY));
          if (isMap(target)) {
            add2(depsMap.get(MAP_KEY_ITERATE_KEY));
          }
        } else if (isIntegerKey(key)) {
          add2(depsMap.get("length"));
        }
        break;
      case "delete":
        if (!isArray(target)) {
          add2(depsMap.get(ITERATE_KEY));
          if (isMap(target)) {
            add2(depsMap.get(MAP_KEY_ITERATE_KEY));
          }
        }
        break;
      case "set":
        if (isMap(target)) {
          add2(depsMap.get(ITERATE_KEY));
        }
        break;
    }
  }
  const run = (effect3) => {
    if (effect3.options.onTrigger) {
      effect3.options.onTrigger({
        effect: effect3,
        target,
        key,
        type,
        newValue,
        oldValue,
        oldTarget
      });
    }
    if (effect3.options.scheduler) {
      effect3.options.scheduler(effect3);
    } else {
      effect3();
    }
  };
  effects.forEach(run);
}
var isNonTrackableKeys = /* @__PURE__ */ makeMap(`__proto__,__v_isRef,__isVue`);
var builtInSymbols = new Set(Object.getOwnPropertyNames(Symbol).map((key) => Symbol[key]).filter(isSymbol));
var get2 = /* @__PURE__ */ createGetter();
var readonlyGet = /* @__PURE__ */ createGetter(true);
var arrayInstrumentations = /* @__PURE__ */ createArrayInstrumentations();
function createArrayInstrumentations() {
  const instrumentations = {};
  ["includes", "indexOf", "lastIndexOf"].forEach((key) => {
    instrumentations[key] = function(...args) {
      const arr = toRaw(this);
      for (let i = 0, l = this.length; i < l; i++) {
        track(arr, "get", i + "");
      }
      const res = arr[key](...args);
      if (res === -1 || res === false) {
        return arr[key](...args.map(toRaw));
      } else {
        return res;
      }
    };
  });
  ["push", "pop", "shift", "unshift", "splice"].forEach((key) => {
    instrumentations[key] = function(...args) {
      pauseTracking();
      const res = toRaw(this)[key].apply(this, args);
      resetTracking();
      return res;
    };
  });
  return instrumentations;
}
function createGetter(isReadonly = false, shallow = false) {
  return function get3(target, key, receiver) {
    if (key === "__v_isReactive") {
      return !isReadonly;
    } else if (key === "__v_isReadonly") {
      return isReadonly;
    } else if (key === "__v_raw" && receiver === (isReadonly ? shallow ? shallowReadonlyMap : readonlyMap : shallow ? shallowReactiveMap : reactiveMap).get(target)) {
      return target;
    }
    const targetIsArray = isArray(target);
    if (!isReadonly && targetIsArray && hasOwn(arrayInstrumentations, key)) {
      return Reflect.get(arrayInstrumentations, key, receiver);
    }
    const res = Reflect.get(target, key, receiver);
    if (isSymbol(key) ? builtInSymbols.has(key) : isNonTrackableKeys(key)) {
      return res;
    }
    if (!isReadonly) {
      track(target, "get", key);
    }
    if (shallow) {
      return res;
    }
    if (isRef(res)) {
      const shouldUnwrap = !targetIsArray || !isIntegerKey(key);
      return shouldUnwrap ? res.value : res;
    }
    if (isObject(res)) {
      return isReadonly ? readonly(res) : reactive2(res);
    }
    return res;
  };
}
var set2 = /* @__PURE__ */ createSetter();
function createSetter(shallow = false) {
  return function set3(target, key, value, receiver) {
    let oldValue = target[key];
    if (!shallow) {
      value = toRaw(value);
      oldValue = toRaw(oldValue);
      if (!isArray(target) && isRef(oldValue) && !isRef(value)) {
        oldValue.value = value;
        return true;
      }
    }
    const hadKey = isArray(target) && isIntegerKey(key) ? Number(key) < target.length : hasOwn(target, key);
    const result = Reflect.set(target, key, value, receiver);
    if (target === toRaw(receiver)) {
      if (!hadKey) {
        trigger(target, "add", key, value);
      } else if (hasChanged(value, oldValue)) {
        trigger(target, "set", key, value, oldValue);
      }
    }
    return result;
  };
}
function deleteProperty(target, key) {
  const hadKey = hasOwn(target, key);
  const oldValue = target[key];
  const result = Reflect.deleteProperty(target, key);
  if (result && hadKey) {
    trigger(target, "delete", key, void 0, oldValue);
  }
  return result;
}
function has(target, key) {
  const result = Reflect.has(target, key);
  if (!isSymbol(key) || !builtInSymbols.has(key)) {
    track(target, "has", key);
  }
  return result;
}
function ownKeys(target) {
  track(target, "iterate", isArray(target) ? "length" : ITERATE_KEY);
  return Reflect.ownKeys(target);
}
var mutableHandlers = {
  get: get2,
  set: set2,
  deleteProperty,
  has,
  ownKeys
};
var readonlyHandlers = {
  get: readonlyGet,
  set(target, key) {
    if (true) {
      console.warn(`Set operation on key "${String(key)}" failed: target is readonly.`, target);
    }
    return true;
  },
  deleteProperty(target, key) {
    if (true) {
      console.warn(`Delete operation on key "${String(key)}" failed: target is readonly.`, target);
    }
    return true;
  }
};
var toReactive = (value) => isObject(value) ? reactive2(value) : value;
var toReadonly = (value) => isObject(value) ? readonly(value) : value;
var toShallow = (value) => value;
var getProto = (v) => Reflect.getPrototypeOf(v);
function get$1(target, key, isReadonly = false, isShallow = false) {
  target = target[
    "__v_raw"
    /* RAW */
  ];
  const rawTarget = toRaw(target);
  const rawKey = toRaw(key);
  if (key !== rawKey) {
    !isReadonly && track(rawTarget, "get", key);
  }
  !isReadonly && track(rawTarget, "get", rawKey);
  const { has: has2 } = getProto(rawTarget);
  const wrap = isShallow ? toShallow : isReadonly ? toReadonly : toReactive;
  if (has2.call(rawTarget, key)) {
    return wrap(target.get(key));
  } else if (has2.call(rawTarget, rawKey)) {
    return wrap(target.get(rawKey));
  } else if (target !== rawTarget) {
    target.get(key);
  }
}
function has$1(key, isReadonly = false) {
  const target = this[
    "__v_raw"
    /* RAW */
  ];
  const rawTarget = toRaw(target);
  const rawKey = toRaw(key);
  if (key !== rawKey) {
    !isReadonly && track(rawTarget, "has", key);
  }
  !isReadonly && track(rawTarget, "has", rawKey);
  return key === rawKey ? target.has(key) : target.has(key) || target.has(rawKey);
}
function size(target, isReadonly = false) {
  target = target[
    "__v_raw"
    /* RAW */
  ];
  !isReadonly && track(toRaw(target), "iterate", ITERATE_KEY);
  return Reflect.get(target, "size", target);
}
function add(value) {
  value = toRaw(value);
  const target = toRaw(this);
  const proto = getProto(target);
  const hadKey = proto.has.call(target, value);
  if (!hadKey) {
    target.add(value);
    trigger(target, "add", value, value);
  }
  return this;
}
function set$1(key, value) {
  value = toRaw(value);
  const target = toRaw(this);
  const { has: has2, get: get3 } = getProto(target);
  let hadKey = has2.call(target, key);
  if (!hadKey) {
    key = toRaw(key);
    hadKey = has2.call(target, key);
  } else if (true) {
    checkIdentityKeys(target, has2, key);
  }
  const oldValue = get3.call(target, key);
  target.set(key, value);
  if (!hadKey) {
    trigger(target, "add", key, value);
  } else if (hasChanged(value, oldValue)) {
    trigger(target, "set", key, value, oldValue);
  }
  return this;
}
function deleteEntry(key) {
  const target = toRaw(this);
  const { has: has2, get: get3 } = getProto(target);
  let hadKey = has2.call(target, key);
  if (!hadKey) {
    key = toRaw(key);
    hadKey = has2.call(target, key);
  } else if (true) {
    checkIdentityKeys(target, has2, key);
  }
  const oldValue = get3 ? get3.call(target, key) : void 0;
  const result = target.delete(key);
  if (hadKey) {
    trigger(target, "delete", key, void 0, oldValue);
  }
  return result;
}
function clear() {
  const target = toRaw(this);
  const hadItems = target.size !== 0;
  const oldTarget = true ? isMap(target) ? new Map(target) : new Set(target) : void 0;
  const result = target.clear();
  if (hadItems) {
    trigger(target, "clear", void 0, void 0, oldTarget);
  }
  return result;
}
function createForEach(isReadonly, isShallow) {
  return function forEach3(callback, thisArg) {
    const observed = this;
    const target = observed[
      "__v_raw"
      /* RAW */
    ];
    const rawTarget = toRaw(target);
    const wrap = isShallow ? toShallow : isReadonly ? toReadonly : toReactive;
    !isReadonly && track(rawTarget, "iterate", ITERATE_KEY);
    return target.forEach((value, key) => {
      return callback.call(thisArg, wrap(value), wrap(key), observed);
    });
  };
}
function createIterableMethod(method, isReadonly, isShallow) {
  return function(...args) {
    const target = this[
      "__v_raw"
      /* RAW */
    ];
    const rawTarget = toRaw(target);
    const targetIsMap = isMap(rawTarget);
    const isPair = method === "entries" || method === Symbol.iterator && targetIsMap;
    const isKeyOnly = method === "keys" && targetIsMap;
    const innerIterator = target[method](...args);
    const wrap = isShallow ? toShallow : isReadonly ? toReadonly : toReactive;
    !isReadonly && track(rawTarget, "iterate", isKeyOnly ? MAP_KEY_ITERATE_KEY : ITERATE_KEY);
    return {
      // iterator protocol
      next() {
        const { value, done } = innerIterator.next();
        return done ? { value, done } : {
          value: isPair ? [wrap(value[0]), wrap(value[1])] : wrap(value),
          done
        };
      },
      // iterable protocol
      [Symbol.iterator]() {
        return this;
      }
    };
  };
}
function createReadonlyMethod(type) {
  return function(...args) {
    if (true) {
      const key = args[0] ? `on key "${args[0]}" ` : ``;
      console.warn(`${capitalize(type)} operation ${key}failed: target is readonly.`, toRaw(this));
    }
    return type === "delete" ? false : this;
  };
}
function createInstrumentations() {
  const mutableInstrumentations2 = {
    get(key) {
      return get$1(this, key);
    },
    get size() {
      return size(this);
    },
    has: has$1,
    add,
    set: set$1,
    delete: deleteEntry,
    clear,
    forEach: createForEach(false, false)
  };
  const shallowInstrumentations2 = {
    get(key) {
      return get$1(this, key, false, true);
    },
    get size() {
      return size(this);
    },
    has: has$1,
    add,
    set: set$1,
    delete: deleteEntry,
    clear,
    forEach: createForEach(false, true)
  };
  const readonlyInstrumentations2 = {
    get(key) {
      return get$1(this, key, true);
    },
    get size() {
      return size(this, true);
    },
    has(key) {
      return has$1.call(this, key, true);
    },
    add: createReadonlyMethod(
      "add"
      /* ADD */
    ),
    set: createReadonlyMethod(
      "set"
      /* SET */
    ),
    delete: createReadonlyMethod(
      "delete"
      /* DELETE */
    ),
    clear: createReadonlyMethod(
      "clear"
      /* CLEAR */
    ),
    forEach: createForEach(true, false)
  };
  const shallowReadonlyInstrumentations2 = {
    get(key) {
      return get$1(this, key, true, true);
    },
    get size() {
      return size(this, true);
    },
    has(key) {
      return has$1.call(this, key, true);
    },
    add: createReadonlyMethod(
      "add"
      /* ADD */
    ),
    set: createReadonlyMethod(
      "set"
      /* SET */
    ),
    delete: createReadonlyMethod(
      "delete"
      /* DELETE */
    ),
    clear: createReadonlyMethod(
      "clear"
      /* CLEAR */
    ),
    forEach: createForEach(true, true)
  };
  const iteratorMethods = ["keys", "values", "entries", Symbol.iterator];
  iteratorMethods.forEach((method) => {
    mutableInstrumentations2[method] = createIterableMethod(method, false, false);
    readonlyInstrumentations2[method] = createIterableMethod(method, true, false);
    shallowInstrumentations2[method] = createIterableMethod(method, false, true);
    shallowReadonlyInstrumentations2[method] = createIterableMethod(method, true, true);
  });
  return [
    mutableInstrumentations2,
    readonlyInstrumentations2,
    shallowInstrumentations2,
    shallowReadonlyInstrumentations2
  ];
}
var [mutableInstrumentations, readonlyInstrumentations, shallowInstrumentations, shallowReadonlyInstrumentations] = /* @__PURE__ */ createInstrumentations();
function createInstrumentationGetter(isReadonly, shallow) {
  const instrumentations = shallow ? isReadonly ? shallowReadonlyInstrumentations : shallowInstrumentations : isReadonly ? readonlyInstrumentations : mutableInstrumentations;
  return (target, key, receiver) => {
    if (key === "__v_isReactive") {
      return !isReadonly;
    } else if (key === "__v_isReadonly") {
      return isReadonly;
    } else if (key === "__v_raw") {
      return target;
    }
    return Reflect.get(hasOwn(instrumentations, key) && key in target ? instrumentations : target, key, receiver);
  };
}
var mutableCollectionHandlers = {
  get: /* @__PURE__ */ createInstrumentationGetter(false, false)
};
var readonlyCollectionHandlers = {
  get: /* @__PURE__ */ createInstrumentationGetter(true, false)
};
function checkIdentityKeys(target, has2, key) {
  const rawKey = toRaw(key);
  if (rawKey !== key && has2.call(target, rawKey)) {
    const type = toRawType(target);
    console.warn(`Reactive ${type} contains both the raw and reactive versions of the same object${type === `Map` ? ` as keys` : ``}, which can lead to inconsistencies. Avoid differentiating between the raw and reactive versions of an object and only use the reactive version if possible.`);
  }
}
var reactiveMap = /* @__PURE__ */ new WeakMap();
var shallowReactiveMap = /* @__PURE__ */ new WeakMap();
var readonlyMap = /* @__PURE__ */ new WeakMap();
var shallowReadonlyMap = /* @__PURE__ */ new WeakMap();
function targetTypeMap(rawType) {
  switch (rawType) {
    case "Object":
    case "Array":
      return 1;
    case "Map":
    case "Set":
    case "WeakMap":
    case "WeakSet":
      return 2;
    default:
      return 0;
  }
}
function getTargetType(value) {
  return value[
    "__v_skip"
    /* SKIP */
  ] || !Object.isExtensible(value) ? 0 : targetTypeMap(toRawType(value));
}
function reactive2(target) {
  if (target && target[
    "__v_isReadonly"
    /* IS_READONLY */
  ]) {
    return target;
  }
  return createReactiveObject(target, false, mutableHandlers, mutableCollectionHandlers, reactiveMap);
}
function readonly(target) {
  return createReactiveObject(target, true, readonlyHandlers, readonlyCollectionHandlers, readonlyMap);
}
function createReactiveObject(target, isReadonly, baseHandlers, collectionHandlers, proxyMap) {
  if (!isObject(target)) {
    if (true) {
      console.warn(`value cannot be made reactive: ${String(target)}`);
    }
    return target;
  }
  if (target[
    "__v_raw"
    /* RAW */
  ] && !(isReadonly && target[
    "__v_isReactive"
    /* IS_REACTIVE */
  ])) {
    return target;
  }
  const existingProxy = proxyMap.get(target);
  if (existingProxy) {
    return existingProxy;
  }
  const targetType = getTargetType(target);
  if (targetType === 0) {
    return target;
  }
  const proxy = new Proxy(target, targetType === 2 ? collectionHandlers : baseHandlers);
  proxyMap.set(target, proxy);
  return proxy;
}
function toRaw(observed) {
  return observed && toRaw(observed[
    "__v_raw"
    /* RAW */
  ]) || observed;
}
function isRef(r) {
  return Boolean(r && r.__v_isRef === true);
}
magic("nextTick", () => nextTick);
magic("dispatch", (el) => dispatch.bind(dispatch, el));
magic("watch", (el, { evaluateLater: evaluateLater2, cleanup: cleanup2 }) => (key, callback) => {
  let evaluate2 = evaluateLater2(key);
  let getter = () => {
    let value;
    evaluate2((i) => value = i);
    return value;
  };
  let unwatch = watch(getter, callback);
  cleanup2(unwatch);
});
magic("store", getStores);
magic("data", (el) => scope(el));
magic("root", (el) => closestRoot(el));
magic("refs", (el) => {
  if (el._x_refs_proxy)
    return el._x_refs_proxy;
  el._x_refs_proxy = mergeProxies(getArrayOfRefObject(el));
  return el._x_refs_proxy;
});
function getArrayOfRefObject(el) {
  let refObjects = [];
  findClosest(el, (i) => {
    if (i._x_refs)
      refObjects.push(i._x_refs);
  });
  return refObjects;
}
var globalIdMemo = {};
function findAndIncrementId(name) {
  if (!globalIdMemo[name])
    globalIdMemo[name] = 0;
  return ++globalIdMemo[name];
}
function closestIdRoot(el, name) {
  return findClosest(el, (element) => {
    if (element._x_ids && element._x_ids[name])
      return true;
  });
}
function setIdRoot(el, name) {
  if (!el._x_ids)
    el._x_ids = {};
  if (!el._x_ids[name])
    el._x_ids[name] = findAndIncrementId(name);
}
magic("id", (el, { cleanup: cleanup2 }) => (name, key = null) => {
  let cacheKey = `${name}${key ? `-${key}` : ""}`;
  return cacheIdByNameOnElement(el, cacheKey, cleanup2, () => {
    let root = closestIdRoot(el, name);
    let id = root ? root._x_ids[name] : findAndIncrementId(name);
    return key ? `${name}-${id}-${key}` : `${name}-${id}`;
  });
});
interceptClone((from, to) => {
  if (from._x_id) {
    to._x_id = from._x_id;
  }
});
function cacheIdByNameOnElement(el, cacheKey, cleanup2, callback) {
  if (!el._x_id)
    el._x_id = {};
  if (el._x_id[cacheKey])
    return el._x_id[cacheKey];
  let output = callback();
  el._x_id[cacheKey] = output;
  cleanup2(() => {
    delete el._x_id[cacheKey];
  });
  return output;
}
magic("el", (el) => el);
warnMissingPluginMagic("Focus", "focus", "focus");
warnMissingPluginMagic("Persist", "persist", "persist");
function warnMissingPluginMagic(name, magicName, slug) {
  magic(magicName, (el) => warn(`You can't use [$${magicName}] without first installing the "${name}" plugin here: https://alpinejs.dev/plugins/${slug}`, el));
}
directive("modelable", (el, { expression }, { effect: effect3, evaluateLater: evaluateLater2, cleanup: cleanup2 }) => {
  let func = evaluateLater2(expression);
  let innerGet = () => {
    let result;
    func((i) => result = i);
    return result;
  };
  let evaluateInnerSet = evaluateLater2(`${expression} = __placeholder`);
  let innerSet = (val) => evaluateInnerSet(() => {
  }, { scope: { "__placeholder": val } });
  let initialValue = innerGet();
  innerSet(initialValue);
  queueMicrotask(() => {
    if (!el._x_model)
      return;
    el._x_removeModelListeners["default"]();
    let outerGet = el._x_model.get;
    let outerSet = el._x_model.setWithModifiers;
    let releaseEntanglement = entangle(
      {
        get() {
          return outerGet();
        },
        set(value) {
          outerSet(value);
        }
      },
      {
        get() {
          return innerGet();
        },
        set(value) {
          innerSet(value);
        }
      }
    );
    cleanup2(releaseEntanglement);
  });
});
directive("teleport", (el, { modifiers, expression }, { cleanup: cleanup2 }) => {
  if (el.tagName.toLowerCase() !== "template")
    warn("x-teleport can only be used on a <template> tag", el);
  let target = getTarget(expression);
  let clone2 = el.content.cloneNode(true).firstElementChild;
  el._x_teleport = clone2;
  clone2._x_teleportBack = el;
  el.setAttribute("data-teleport-template", true);
  clone2.setAttribute("data-teleport-target", true);
  if (el._x_forwardEvents) {
    el._x_forwardEvents.forEach((eventName) => {
      clone2.addEventListener(eventName, (e) => {
        e.stopPropagation();
        el.dispatchEvent(new e.constructor(e.type, e));
      });
    });
  }
  addScopeToNode(clone2, {}, el);
  let placeInDom = (clone3, target2, modifiers2) => {
    if (modifiers2.includes("prepend")) {
      target2.parentNode.insertBefore(clone3, target2);
    } else if (modifiers2.includes("append")) {
      target2.parentNode.insertBefore(clone3, target2.nextSibling);
    } else {
      target2.appendChild(clone3);
    }
  };
  mutateDom(() => {
    skipDuringClone(() => {
      placeInDom(clone2, target, modifiers);
      initTree(clone2);
    })();
  });
  el._x_teleportPutBack = () => {
    let target2 = getTarget(expression);
    mutateDom(() => {
      placeInDom(el._x_teleport, target2, modifiers);
    });
  };
  cleanup2(
    () => mutateDom(() => {
      clone2.remove();
      destroyTree(clone2);
    })
  );
});
var teleportContainerDuringClone = document.createElement("div");
function getTarget(expression) {
  let target = skipDuringClone(() => {
    return document.querySelector(expression);
  }, () => {
    return teleportContainerDuringClone;
  })();
  if (!target)
    warn(`Cannot find x-teleport element for selector: "${expression}"`);
  return target;
}
var handler = () => {
};
handler.inline = (el, { modifiers }, { cleanup: cleanup2 }) => {
  modifiers.includes("self") ? el._x_ignoreSelf = true : el._x_ignore = true;
  cleanup2(() => {
    modifiers.includes("self") ? delete el._x_ignoreSelf : delete el._x_ignore;
  });
};
directive("ignore", handler);
directive("effect", skipDuringClone((el, { expression }, { effect: effect3 }) => {
  effect3(evaluateLater(el, expression));
}));
function on(el, event, modifiers, callback) {
  let listenerTarget = el;
  let handler4 = (e) => callback(e);
  let options = {};
  let wrapHandler = (callback2, wrapper) => (e) => wrapper(callback2, e);
  if (modifiers.includes("dot"))
    event = dotSyntax(event);
  if (modifiers.includes("camel"))
    event = camelCase2(event);
  if (modifiers.includes("capture"))
    options.capture = true;
  if (modifiers.includes("window"))
    listenerTarget = window;
  if (modifiers.includes("document"))
    listenerTarget = document;
  if (modifiers.includes("passive")) {
    options.passive = modifiers[modifiers.indexOf("passive") + 1] !== "false";
  }
  handler4 = addDebounceOrThrottle(modifiers, handler4);
  if (modifiers.includes("prevent"))
    handler4 = wrapHandler(handler4, (next, e) => {
      e.preventDefault();
      next(e);
    });
  if (modifiers.includes("stop"))
    handler4 = wrapHandler(handler4, (next, e) => {
      e.stopPropagation();
      next(e);
    });
  if (modifiers.includes("once")) {
    handler4 = wrapHandler(handler4, (next, e) => {
      next(e);
      listenerTarget.removeEventListener(event, handler4, options);
    });
  }
  if (modifiers.includes("away") || modifiers.includes("outside")) {
    listenerTarget = document;
    handler4 = wrapHandler(handler4, (next, e) => {
      if (el.contains(e.target))
        return;
      if (e.target.isConnected === false)
        return;
      if (el.offsetWidth < 1 && el.offsetHeight < 1)
        return;
      if (el._x_isShown === false)
        return;
      next(e);
    });
  }
  if (modifiers.includes("self"))
    handler4 = wrapHandler(handler4, (next, e) => {
      e.target === el && next(e);
    });
  if (event === "submit") {
    handler4 = wrapHandler(handler4, (next, e) => {
      if (e.target._x_pendingModelUpdates) {
        e.target._x_pendingModelUpdates.forEach((fn) => fn());
      }
      next(e);
    });
  }
  if (isKeyEvent(event) || isClickEvent(event)) {
    handler4 = wrapHandler(handler4, (next, e) => {
      if (isListeningForASpecificKeyThatHasntBeenPressed(e, modifiers)) {
        return;
      }
      next(e);
    });
  }
  listenerTarget.addEventListener(event, handler4, options);
  return () => {
    listenerTarget.removeEventListener(event, handler4, options);
  };
}
function addDebounceOrThrottle(modifiers, handler4) {
  if (modifiers.includes("debounce")) {
    let nextModifier = modifiers[modifiers.indexOf("debounce") + 1] || "invalid-wait";
    let wait = isNumeric(nextModifier.split("ms")[0]) ? Number(nextModifier.split("ms")[0]) : 250;
    handler4 = debounce(handler4, wait);
  }
  if (modifiers.includes("throttle")) {
    let nextModifier = modifiers[modifiers.indexOf("throttle") + 1] || "invalid-wait";
    let wait = isNumeric(nextModifier.split("ms")[0]) ? Number(nextModifier.split("ms")[0]) : 250;
    handler4 = throttle(handler4, wait);
  }
  return handler4;
}
function dotSyntax(subject) {
  return subject.replace(/-/g, ".");
}
function camelCase2(subject) {
  return subject.toLowerCase().replace(/-(\w)/g, (match, char) => char.toUpperCase());
}
function isNumeric(subject) {
  return !Array.isArray(subject) && !isNaN(subject);
}
function kebabCase2(subject) {
  if ([" ", "_"].includes(
    subject
  ))
    return subject;
  return subject.replace(/([a-z])([A-Z])/g, "$1-$2").replace(/[_\s]/, "-").toLowerCase();
}
function isKeyEvent(event) {
  return ["keydown", "keyup"].includes(event);
}
function isClickEvent(event) {
  return ["contextmenu", "click", "mouse"].some((i) => event.includes(i));
}
function isListeningForASpecificKeyThatHasntBeenPressed(e, modifiers) {
  let keyModifiers = modifiers.filter((i) => {
    return !["window", "document", "prevent", "stop", "once", "capture", "self", "away", "outside", "passive", "preserve-scroll", "blur", "change", "lazy"].includes(i);
  });
  if (keyModifiers.includes("debounce")) {
    let debounceIndex = keyModifiers.indexOf("debounce");
    keyModifiers.splice(debounceIndex, isNumeric((keyModifiers[debounceIndex + 1] || "invalid-wait").split("ms")[0]) ? 2 : 1);
  }
  if (keyModifiers.includes("throttle")) {
    let debounceIndex = keyModifiers.indexOf("throttle");
    keyModifiers.splice(debounceIndex, isNumeric((keyModifiers[debounceIndex + 1] || "invalid-wait").split("ms")[0]) ? 2 : 1);
  }
  if (keyModifiers.length === 0)
    return false;
  if (keyModifiers.length === 1 && keyToModifiers(e.key).includes(keyModifiers[0]))
    return false;
  const systemKeyModifiers = ["ctrl", "shift", "alt", "meta", "cmd", "super"];
  const selectedSystemKeyModifiers = systemKeyModifiers.filter((modifier) => keyModifiers.includes(modifier));
  keyModifiers = keyModifiers.filter((i) => !selectedSystemKeyModifiers.includes(i));
  if (selectedSystemKeyModifiers.length > 0) {
    const activelyPressedKeyModifiers = selectedSystemKeyModifiers.filter((modifier) => {
      if (modifier === "cmd" || modifier === "super")
        modifier = "meta";
      return e[`${modifier}Key`];
    });
    if (activelyPressedKeyModifiers.length === selectedSystemKeyModifiers.length) {
      if (isClickEvent(e.type))
        return false;
      if (keyToModifiers(e.key).includes(keyModifiers[0]))
        return false;
    }
  }
  return true;
}
function keyToModifiers(key) {
  if (!key)
    return [];
  key = kebabCase2(key);
  let modifierToKeyMap = {
    "ctrl": "control",
    "slash": "/",
    "space": " ",
    "spacebar": " ",
    "cmd": "meta",
    "esc": "escape",
    "up": "arrow-up",
    "down": "arrow-down",
    "left": "arrow-left",
    "right": "arrow-right",
    "period": ".",
    "comma": ",",
    "equal": "=",
    "minus": "-",
    "underscore": "_"
  };
  modifierToKeyMap[key] = key;
  return Object.keys(modifierToKeyMap).map((modifier) => {
    if (modifierToKeyMap[modifier] === key)
      return modifier;
  }).filter((modifier) => modifier);
}
directive("model", (el, { modifiers, expression }, { effect: effect3, cleanup: cleanup2 }) => {
  let scopeTarget = el;
  if (modifiers.includes("parent")) {
    scopeTarget = findClosest(el, (element) => element !== el);
  }
  let evaluateGet = evaluateLater(scopeTarget, expression);
  let evaluateSet;
  if (typeof expression === "string") {
    evaluateSet = evaluateLater(scopeTarget, `${expression} = __placeholder`);
  } else if (typeof expression === "function" && typeof expression() === "string") {
    evaluateSet = evaluateLater(scopeTarget, `${expression()} = __placeholder`);
  } else {
    evaluateSet = () => {
    };
  }
  let getValue = () => {
    let result;
    evaluateGet((value) => result = value);
    return isGetterSetter(result) ? result.get() : result;
  };
  let setValue = (value) => {
    let result;
    evaluateGet((value2) => result = value2);
    if (isGetterSetter(result)) {
      result.set(value);
    } else {
      evaluateSet(() => {
      }, {
        scope: { "__placeholder": value }
      });
    }
  };
  if (typeof expression === "string" && el.type === "radio") {
    mutateDom(() => {
      if (!el.hasAttribute("name"))
        el.setAttribute("name", expression);
    });
  }
  let hasChangeModifier = modifiers.includes("change") || modifiers.includes("lazy");
  let hasBlurModifier = modifiers.includes("blur");
  let hasEnterModifier = modifiers.includes("enter");
  let hasExplicitEventModifiers = hasChangeModifier || hasBlurModifier || hasEnterModifier;
  let removeListener;
  if (isCloning) {
    removeListener = () => {
    };
  } else if (hasExplicitEventModifiers) {
    let listeners = [];
    let syncValue = (e) => setValue(getInputValue(el, modifiers, e, getValue()));
    if (hasChangeModifier) {
      listeners.push(on(el, "change", modifiers, syncValue));
    }
    if (hasBlurModifier) {
      listeners.push(on(el, "blur", modifiers, syncValue));
      if (el.form) {
        let form = el.form;
        let syncCallback = () => syncValue({ target: el });
        if (!form._x_pendingModelUpdates)
          form._x_pendingModelUpdates = [];
        form._x_pendingModelUpdates.push(syncCallback);
        cleanup2(() => {
          if (form._x_pendingModelUpdates) {
            form._x_pendingModelUpdates.splice(form._x_pendingModelUpdates.indexOf(syncCallback), 1);
          }
        });
      }
    }
    if (hasEnterModifier) {
      listeners.push(on(el, "keydown", modifiers, (e) => {
        if (e.key === "Enter")
          syncValue(e);
      }));
    }
    removeListener = () => listeners.forEach((remove2) => remove2());
  } else {
    let event = el.tagName.toLowerCase() === "select" || ["checkbox", "radio"].includes(el.type) ? "change" : "input";
    removeListener = on(el, event, modifiers, (e) => {
      setValue(getInputValue(el, modifiers, e, getValue()));
    });
  }
  if (modifiers.includes("fill")) {
    if ([void 0, null, ""].includes(getValue()) || isCheckbox(el) && Array.isArray(getValue()) || el.tagName.toLowerCase() === "select" && el.multiple) {
      setValue(
        getInputValue(el, modifiers, { target: el }, getValue())
      );
    }
  }
  if (!el._x_removeModelListeners)
    el._x_removeModelListeners = {};
  el._x_removeModelListeners["default"] = removeListener;
  cleanup2(() => el._x_removeModelListeners["default"]());
  if (el.form) {
    let removeResetListener = on(el.form, "reset", [], (e) => {
      nextTick(() => el._x_model && el._x_model.set(getInputValue(el, modifiers, { target: el }, getValue())));
    });
    cleanup2(() => removeResetListener());
  }
  el._x_model = {
    get() {
      return getValue();
    },
    set(value) {
      setValue(value);
    },
    setWithModifiers: addDebounceOrThrottle(modifiers, setValue)
  };
  el._x_forceModelUpdate = (value) => {
    if (value === void 0 && typeof expression === "string" && expression.match(/\./))
      value = "";
    mutateDom(() => {
      if (isCheckbox(el)) {
        if (Array.isArray(value)) {
          el.checked = value.some((val) => val == el.value);
        } else {
          el.checked = !!value;
        }
      } else if (isRadio(el)) {
        if (typeof value === "boolean") {
          el.checked = safeParseBoolean(el.value) === value;
        } else {
          el.checked = el.value == value;
        }
      } else {
        bind(el, "value", value);
      }
    });
  };
  if (el.tagName === "SELECT") {
    let observer2 = new MutationObserver(() => {
      el._x_forceModelUpdate(getValue());
    });
    observer2.observe(el, { childList: true });
    cleanup2(() => observer2.disconnect());
  }
  effect3(() => {
    let value = getValue();
    if (modifiers.includes("unintrusive") && document.activeElement.isSameNode(el))
      return;
    el._x_forceModelUpdate(value);
  });
});
function getInputValue(el, modifiers, event, currentValue) {
  return mutateDom(() => {
    if (event instanceof CustomEvent && event.detail !== void 0)
      return event.detail !== null && event.detail !== void 0 ? event.detail : event.target.value;
    else if (isCheckbox(el)) {
      if (Array.isArray(currentValue)) {
        let newValue = null;
        if (modifiers.includes("number")) {
          newValue = safeParseNumber(event.target.value);
        } else if (modifiers.includes("boolean")) {
          newValue = safeParseBoolean(event.target.value);
        } else {
          newValue = event.target.value;
        }
        return event.target.checked ? currentValue.includes(newValue) ? currentValue : currentValue.concat([newValue]) : currentValue.filter((el2) => !checkedAttrLooseCompare2(el2, newValue));
      } else {
        return event.target.checked;
      }
    } else if (el.tagName.toLowerCase() === "select" && el.multiple) {
      if (modifiers.includes("number")) {
        return Array.from(event.target.selectedOptions).map((option) => {
          let rawValue = option.value || option.text;
          return safeParseNumber(rawValue);
        });
      } else if (modifiers.includes("boolean")) {
        return Array.from(event.target.selectedOptions).map((option) => {
          let rawValue = option.value || option.text;
          return safeParseBoolean(rawValue);
        });
      }
      return Array.from(event.target.selectedOptions).map((option) => {
        return option.value || option.text;
      });
    } else {
      let newValue;
      if (isRadio(el)) {
        if (event.target.checked) {
          newValue = event.target.value;
        } else {
          newValue = currentValue;
        }
      } else {
        newValue = event.target.value;
      }
      if (modifiers.includes("number")) {
        return safeParseNumber(newValue);
      } else if (modifiers.includes("boolean")) {
        return safeParseBoolean(newValue);
      } else if (modifiers.includes("trim")) {
        return newValue.trim();
      } else {
        return newValue;
      }
    }
  });
}
function safeParseNumber(rawValue) {
  let number = rawValue ? parseFloat(rawValue) : null;
  return isNumeric2(number) ? number : rawValue;
}
function checkedAttrLooseCompare2(valueA, valueB) {
  return valueA == valueB;
}
function isNumeric2(subject) {
  return !Array.isArray(subject) && !isNaN(subject);
}
function isGetterSetter(value) {
  return value !== null && typeof value === "object" && typeof value.get === "function" && typeof value.set === "function";
}
directive("cloak", (el) => queueMicrotask(() => mutateDom(() => el.removeAttribute(prefix("cloak")))));
addInitSelector(() => `[${prefix("init")}]`);
directive("init", skipDuringClone((el, { expression }, { evaluate: evaluate2 }) => {
  if (typeof expression === "string") {
    return !!expression.trim() && evaluate2(expression, {}, false);
  }
  return evaluate2(expression, {}, false);
}));
directive("text", (el, { expression }, { effect: effect3, evaluateLater: evaluateLater2 }) => {
  let evaluate2 = evaluateLater2(expression);
  effect3(() => {
    evaluate2((value) => {
      mutateDom(() => {
        el.textContent = value;
      });
    });
  });
});
directive("html", (el, { expression }, { effect: effect3, evaluateLater: evaluateLater2 }) => {
  let evaluate2 = evaluateLater2(expression);
  effect3(() => {
    evaluate2((value) => {
      mutateDom(() => {
        el.innerHTML = value ?? "";
        el._x_ignoreSelf = true;
        initTree(el);
        delete el._x_ignoreSelf;
      });
    });
  });
});
mapAttributes(startingWith(":", into(prefix("bind:"))));
var handler2 = (el, { value, modifiers, expression, original }, { effect: effect3, cleanup: cleanup2 }) => {
  if (!value) {
    let bindingProviders = {};
    injectBindingProviders(bindingProviders);
    let getBindings = evaluateLater(el, expression);
    getBindings((bindings) => {
      applyBindingsObject(el, bindings, original);
    }, { scope: bindingProviders });
    return;
  }
  if (value === "key")
    return storeKeyForXFor(el, expression);
  if (el._x_inlineBindings && el._x_inlineBindings[value] && el._x_inlineBindings[value].extract) {
    return;
  }
  let evaluate2 = evaluateLater(el, expression);
  effect3(() => evaluate2((result) => {
    if (result === void 0 && typeof expression === "string" && expression.match(/\./)) {
      result = "";
    }
    mutateDom(() => bind(el, value, result, modifiers));
  }));
  cleanup2(() => {
    el._x_undoAddedClasses && el._x_undoAddedClasses();
    el._x_undoAddedStyles && el._x_undoAddedStyles();
  });
};
handler2.inline = (el, { value, modifiers, expression }) => {
  if (!value)
    return;
  if (!el._x_inlineBindings)
    el._x_inlineBindings = {};
  el._x_inlineBindings[value] = { expression, extract: false };
};
directive("bind", handler2);
function storeKeyForXFor(el, expression) {
  el._x_keyExpression = expression;
}
addRootSelector(() => `[${prefix("data")}]`);
var dataForReconciliation = Symbol();
directive("data", (el, { expression }, { cleanup: cleanup2 }) => {
  if (shouldSkipRegisteringDataDuringClone(el))
    return;
  let dataToReconcile = el[dataForReconciliation];
  if (dataToReconcile?.expression === expression)
    return;
  expression = expression === "" ? "{}" : expression;
  let magicContext = {};
  injectMagics(magicContext, el);
  let dataProviderContext = {};
  injectDataProviders(dataProviderContext, magicContext);
  let data2 = evaluate(el, expression, { scope: dataProviderContext });
  if (data2 === void 0 || data2 === true)
    data2 = {};
  injectMagics(data2, el);
  let reactiveData;
  if (dataToReconcile?.reactiveData) {
    reactiveData = dataToReconcile.reactiveData;
    reconcileData(reactiveData, data2);
    let initialized = { expression };
    el[dataForReconciliation] = initialized;
    queueMicrotask(() => {
      if (el[dataForReconciliation] === initialized) {
        delete el[dataForReconciliation];
      }
    });
  } else {
    reactiveData = reactive(data2);
  }
  initInterceptors(reactiveData, cleanup2);
  let undo = addScopeToNode(el, reactiveData);
  reactiveData["init"] && evaluate(el, reactiveData["init"]);
  cleanup2(() => {
    reactiveData["destroy"] && evaluate(el, reactiveData["destroy"]);
    undo();
    let removed = { reactiveData };
    el[dataForReconciliation] = removed;
    queueMicrotask(() => {
      if (el[dataForReconciliation] === removed) {
        delete el[dataForReconciliation];
      }
    });
  });
});
function reconcileData(target, source) {
  Object.keys(source).forEach((key) => {
    let descriptor = Object.getOwnPropertyDescriptor(source, key);
    let existingDescriptor = Object.getOwnPropertyDescriptor(target, key);
    if (descriptor.get || descriptor.set || existingDescriptor?.get || existingDescriptor?.set) {
      if (existingDescriptor)
        delete target[key];
      if (!existingDescriptor)
        target[key] = void 0;
      descriptor.get || descriptor.set ? Object.defineProperty(target, key, descriptor) : target[key] = source[key];
    } else {
      target[key] = source[key];
    }
  });
  Object.keys(target).filter((key) => !Object.prototype.hasOwnProperty.call(source, key)).forEach((key) => delete target[key]);
}
interceptClone((from, to) => {
  if (from._x_dataStack) {
    to._x_dataStack = from._x_dataStack;
    to.setAttribute("data-has-alpine-state", true);
  }
});
function shouldSkipRegisteringDataDuringClone(el) {
  if (!isCloning)
    return false;
  if (isCloningLegacy)
    return true;
  return el.hasAttribute("data-has-alpine-state");
}
directive("show", (el, { modifiers, expression }, { effect: effect3 }) => {
  let evaluate2 = evaluateLater(el, expression);
  if (!el._x_doHide)
    el._x_doHide = () => {
      mutateDom(() => {
        el.style.setProperty("display", "none", modifiers.includes("important") ? "important" : void 0);
      });
    };
  if (!el._x_doShow)
    el._x_doShow = () => {
      mutateDom(() => {
        if (el.style.length === 1 && el.style.display === "none") {
          el.removeAttribute("style");
        } else {
          el.style.removeProperty("display");
        }
      });
    };
  let hide = () => {
    el._x_doHide();
    el._x_isShown = false;
  };
  let show = () => {
    el._x_doShow();
    el._x_isShown = true;
  };
  let clickAwayCompatibleShow = () => setTimeout(show);
  let toggle = once(
    (value) => value ? show() : hide(),
    (value) => {
      if (typeof el._x_toggleAndCascadeWithTransitions === "function") {
        el._x_toggleAndCascadeWithTransitions(el, value, show, hide);
      } else {
        value ? clickAwayCompatibleShow() : hide();
      }
    }
  );
  let oldValue;
  let firstTime = true;
  effect3(() => evaluate2((value) => {
    if (!firstTime && value === oldValue)
      return;
    if (modifiers.includes("immediate"))
      value ? clickAwayCompatibleShow() : hide();
    toggle(value);
    oldValue = value;
    firstTime = false;
  }));
});
directive("for", skipDuringClone((el, { expression }, { effect: effect3, cleanup: cleanup2 }) => {
  let iteratorNames = parseForExpression(expression);
  let evaluateItems = evaluateLater(el, iteratorNames.items);
  let evaluateKey = evaluateLater(
    el,
    // the x-bind:key expression is stored for our use instead of evaluated.
    el._x_keyExpression || "index"
  );
  el._x_lookup = /* @__PURE__ */ new Map();
  effect3(() => loop(el, iteratorNames, evaluateItems, evaluateKey));
  cleanup2(() => {
    el._x_lookup.forEach(
      (el2) => mutateDom(() => {
        destroyTree(el2);
        el2.remove();
      })
    );
    delete el._x_lookup;
    delete el._x_lastRenderedEl;
  });
}));
function refreshScope(scope2) {
  return (newScope) => {
    Object.entries(newScope).forEach(([key, value]) => {
      scope2[key] = value;
    });
  };
}
function loop(templateEl, iteratorNames, evaluateItems, evaluateKey) {
  evaluateItems((items) => {
    if (isNumeric3(items))
      items = Array.from({ length: items }, (_, i) => i + 1);
    if (items === void 0 || items === null)
      items = [];
    if (items instanceof Set)
      items = Array.from(items);
    if (items instanceof Map)
      items = Array.from(items);
    let oldLookup = templateEl._x_lookup;
    let lookup = /* @__PURE__ */ new Map();
    templateEl._x_lookup = lookup;
    let hasStringKeys = isObject2(items);
    let scopeEntries = Object.entries(items).map(([index, item]) => {
      if (!hasStringKeys)
        index = parseInt(index);
      let scope2 = getIterationScopeVariables(iteratorNames, item, index, items);
      let key;
      evaluateKey((innerKey) => {
        if (typeof innerKey === "object")
          warn("x-for key cannot be an object, it must be a string or an integer", templateEl);
        if (oldLookup.has(innerKey)) {
          lookup.set(innerKey, oldLookup.get(innerKey));
          oldLookup.delete(innerKey);
        }
        key = innerKey;
      }, { scope: { index, ...scope2 } });
      return [key, scope2];
    });
    mutateDom(() => {
      oldLookup.forEach((el) => {
        destroyTree(el);
        el.remove();
      });
      let added = /* @__PURE__ */ new Set();
      let prev = templateEl;
      scopeEntries.forEach(([key, scope2]) => {
        if (lookup.has(key)) {
          let el = lookup.get(key);
          el._x_refreshXForScope(scope2);
          if (prev.nextElementSibling !== el) {
            if (prev.nextElementSibling)
              el.replaceWith(prev.nextElementSibling);
            prev.after(el);
          }
          prev = el;
          if (el._x_currentIfEl) {
            if (el.nextElementSibling !== el._x_currentIfEl)
              prev.after(el._x_currentIfEl);
            prev = el._x_currentIfEl;
          }
          return;
        }
        if (templateEl.content.children.length > 1)
          warn("x-for templates require a single root element, additional elements will be ignored.", templateEl);
        let clone2 = document.importNode(templateEl.content, true).firstElementChild;
        let reactiveScope = reactive(scope2);
        addScopeToNode(clone2, reactiveScope, templateEl);
        clone2._x_refreshXForScope = refreshScope(reactiveScope);
        lookup.set(key, clone2);
        added.add(clone2);
        prev.after(clone2);
        prev = clone2;
      });
      added.forEach((clone2) => initTree(clone2));
      if (prev !== templateEl) {
        templateEl._x_lastRenderedEl = prev;
      } else {
        delete templateEl._x_lastRenderedEl;
      }
    });
  });
}
function parseForExpression(expression) {
  let forIteratorRE = /,([^,\}\]]*)(?:,([^,\}\]]*))?$/;
  let stripParensRE = /^\s*\(|\)\s*$/g;
  let forAliasRE = /([\s\S]*?)\s+(?:in|of)\s+([\s\S]*)/;
  let inMatch = expression.match(forAliasRE);
  if (!inMatch)
    return;
  let res = {};
  res.items = inMatch[2].trim();
  let item = inMatch[1].replace(stripParensRE, "").trim();
  let iteratorMatch = item.match(forIteratorRE);
  if (iteratorMatch) {
    res.item = item.replace(forIteratorRE, "").trim();
    res.index = iteratorMatch[1].trim();
    if (iteratorMatch[2]) {
      res.collection = iteratorMatch[2].trim();
    }
  } else {
    res.item = item;
  }
  return res;
}
function getIterationScopeVariables(iteratorNames, item, index, items) {
  let scopeVariables = {};
  if (/^\[.*\]$/.test(iteratorNames.item) && Array.isArray(item)) {
    let names = iteratorNames.item.replace("[", "").replace("]", "").split(",").map((i) => i.trim());
    names.forEach((name, i) => {
      scopeVariables[name] = item[i];
    });
  } else if (/^\{.*\}$/.test(iteratorNames.item) && !Array.isArray(item) && typeof item === "object") {
    let names = iteratorNames.item.replace("{", "").replace("}", "").split(",").map((i) => i.trim());
    names.forEach((name) => {
      scopeVariables[name] = item[name];
    });
  } else {
    scopeVariables[iteratorNames.item] = item;
  }
  if (iteratorNames.index)
    scopeVariables[iteratorNames.index] = index;
  if (iteratorNames.collection)
    scopeVariables[iteratorNames.collection] = items;
  return scopeVariables;
}
function isNumeric3(subject) {
  return typeof subject !== "object" && !isNaN(subject);
}
function isObject2(subject) {
  return typeof subject === "object" && !Array.isArray(subject);
}
function handler3() {
}
handler3.inline = (el, { expression }, { cleanup: cleanup2 }) => {
  let root = closestRoot(el);
  if (!root)
    return;
  if (!root._x_refs)
    root._x_refs = {};
  root._x_refs[expression] = el;
  cleanup2(() => delete root._x_refs[expression]);
};
directive("ref", handler3);
directive("if", skipDuringClone((el, { expression }, { effect: effect3, cleanup: cleanup2 }) => {
  if (el.tagName.toLowerCase() !== "template")
    warn("x-if can only be used on a <template> tag", el);
  let evaluate2 = evaluateLater(el, expression);
  let show = () => {
    if (el._x_currentIfEl)
      return el._x_currentIfEl;
    let clone2 = el.content.cloneNode(true).firstElementChild;
    addScopeToNode(clone2, {}, el);
    mutateDom(() => {
      el.after(clone2);
      initTree(clone2);
    });
    el._x_currentIfEl = clone2;
    el._x_lastRenderedEl = clone2;
    el._x_undoIf = () => {
      mutateDom(() => {
        destroyTree(clone2);
        clone2.remove();
      });
      delete el._x_currentIfEl;
      delete el._x_lastRenderedEl;
    };
    return clone2;
  };
  let hide = () => {
    if (!el._x_undoIf)
      return;
    el._x_undoIf();
    delete el._x_undoIf;
  };
  effect3(() => evaluate2((value) => {
    value ? show() : hide();
  }));
  cleanup2(() => el._x_undoIf && el._x_undoIf());
}));
directive("id", (el, { expression }, { evaluate: evaluate2 }) => {
  let names = evaluate2(expression);
  names.forEach((name) => setIdRoot(el, name));
});
interceptClone((from, to) => {
  if (from._x_ids) {
    to._x_ids = from._x_ids;
  }
});
mapAttributes(startingWith("@", into(prefix("on:"))));
directive("on", skipDuringClone((el, { value, modifiers, expression }, { cleanup: cleanup2 }) => {
  let evaluate2 = expression ? evaluateLater(el, expression) : () => {
  };
  if (el.tagName.toLowerCase() === "template") {
    if (!el._x_forwardEvents)
      el._x_forwardEvents = [];
    if (!el._x_forwardEvents.includes(value))
      el._x_forwardEvents.push(value);
  }
  let removeListener = on(el, value, modifiers, (e) => {
    evaluate2(() => {
    }, { scope: { "$event": e }, params: [e] });
  });
  cleanup2(() => removeListener());
}));
warnMissingPluginDirective("Collapse", "collapse", "collapse");
warnMissingPluginDirective("Intersect", "intersect", "intersect");
warnMissingPluginDirective("Focus", "trap", "focus");
warnMissingPluginDirective("Mask", "mask", "mask");
function warnMissingPluginDirective(name, directiveName, slug) {
  directive(directiveName, (el) => warn(`You can't use [x-${directiveName}] without first installing the "${name}" plugin here: https://alpinejs.dev/plugins/${slug}`, el));
}
alpine_default.setEvaluator(normalEvaluator);
alpine_default.setRawEvaluator(normalRawEvaluator);
alpine_default.setReactivityEngine({ reactive: reactive2, effect: effect2, release: stop, raw: toRaw });
var src_default = alpine_default;
var module_default = src_default;

// node_modules/@alpinejs/persist/dist/module.esm.js
function src_default2(Alpine3) {
  let persist = () => {
    let alias;
    let storage;
    try {
      storage = localStorage;
    } catch (e) {
      console.error(e);
      console.warn("Alpine: $persist is using temporary storage since localStorage is unavailable.");
      let dummy = /* @__PURE__ */ new Map();
      storage = {
        getItem: dummy.get.bind(dummy),
        setItem: dummy.set.bind(dummy)
      };
    }
    return Alpine3.interceptor((initialValue, getter, setter, path, key, cleanup2 = () => {
    }) => {
      let lookup = alias || `_x_${path}`;
      let initial = storageHas(lookup, storage) ? storageGet(lookup, storage) : initialValue;
      setter(initial);
      let effect3 = Alpine3.effect(() => {
        let value = getter();
        storageSet(lookup, value, storage);
        setter(value);
      });
      cleanup2(() => Alpine3.release(effect3));
      return initial;
    }, (func) => {
      func.as = (key) => {
        alias = key;
        return func;
      }, func.using = (target) => {
        storage = target;
        return func;
      };
    });
  };
  Object.defineProperty(Alpine3, "$persist", { get: () => persist() });
  Alpine3.magic("persist", persist);
  Alpine3.persist = (key, { get: get3, set: set3 }, storage = localStorage) => {
    let initial = storageHas(key, storage) ? storageGet(key, storage) : get3();
    set3(initial);
    Alpine3.effect(() => {
      let value = get3();
      storageSet(key, value, storage);
      set3(value);
    });
  };
}
function storageHas(key, storage) {
  return storage.getItem(key) !== null;
}
function storageGet(key, storage) {
  let value = storage.getItem(key);
  if (value === void 0)
    return;
  return JSON.parse(value);
}
function storageSet(key, value, storage) {
  storage.setItem(key, JSON.stringify(value));
}
var module_default2 = src_default2;

// src/scripts/utilities/store.js
module_default.plugin(module_default2);
module_default.store("lastViewed", module_default.$persist(false).as("lastViewed"));
module_default.store("errors", {
  list: [],
  add(message) {
    this.list.push({
      id: Date.now(),
      message,
      visible: true
    });
    setTimeout(() => {
      this.removeById(this.list[0]?.id);
    }, 6e3);
  },
  removeById(id) {
    this.list = this.list.filter((err) => err.id !== id);
  }
});

// src/scripts/utilities/observe-element.js
document.addEventListener("DOMContentLoaded", () => {
  const observedElements = document.querySelectorAll(".observed-element");
  observedElements.forEach((element) => {
    const variableName = element.getAttribute("data-variable");
    const observer2 = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const elementHeight = (entry.target.clientHeight || 0) + "px";
          const elementWidth = (entry.target.clientWidth || 0) + "px";
          document.documentElement.style.setProperty(
            `--${variableName}-height`,
            elementHeight
          );
          document.documentElement.style.setProperty(
            `--${variableName}-width`,
            elementWidth
          );
        }
      });
    });
    observer2.observe(element);
  });
});

// src/scripts/utilities/form-validation.js
function isEmptyOrWhitespace(value) {
  return !value || !value.trim();
}
function validateRequiredTextFields(form) {
  const requiredTextFields = form.querySelectorAll(
    'input[type="text"][required], input[type="email"][required], textarea[required]'
  );
  requiredTextFields.forEach((input) => {
    const errorContainer = document.getElementById(`${input.id}-error`);
    input.addEventListener("blur", () => {
      if (!errorContainer) return;
      let isValid = !isEmptyOrWhitespace(input.value);
      if (isValid && input.type === "email") {
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        isValid = emailPattern.test(input.value);
      }
      errorContainer.hidden = isValid;
      input.setAttribute("aria-invalid", isValid ? "false" : "true");
      const msg = errorContainer.querySelector(".form__message");
      if (msg) {
        msg.textContent = isValid ? "" : input.dataset.validationMessage || "This field is required.";
      }
    });
  });
}
function sanitizePhoneInput(input) {
  input.addEventListener("input", () => {
    input.value = input.value.replace(/[^0-9+\-\s().]/g, "");
  });
}
function validatePhoneField(input, errorContainer) {
  const pattern = /^[0-9+\-\s().]{7,}$/;
  input.addEventListener("blur", () => {
    const isValid = pattern.test(input.value);
    errorContainer.hidden = isValid;
    input.setAttribute("aria-invalid", isValid ? "false" : "true");
  });
}
function markRequiredFields(form) {
  const requiredFields = form.querySelectorAll("[required]");
  requiredFields.forEach((field) => {
    const label = form.querySelector(`label[for="${field.id}"]`);
    if (label && !label.innerHTML.includes("*")) {
      label.innerHTML += ' <span aria-hidden="true">*</span>';
    }
  });
}
function setupFormValidation(form) {
  const phoneInput = form.querySelector('input[type="tel"]');
  const phoneError = form.querySelector("#ContactFormPhone-error");
  if (phoneInput && phoneError) {
    sanitizePhoneInput(phoneInput);
    validatePhoneField(phoneInput, phoneError);
  }
  markRequiredFields(form);
  validateRequiredTextFields(form);
}
function initForms() {
  const forms = document.querySelectorAll("form.form--validate");
  forms.forEach(setupFormValidation);
}
document.addEventListener("DOMContentLoaded", initForms);

// src/scripts/utilities/video-player.js
window.VideoPlayerManager = {
  pauseAll() {
    document.querySelectorAll(".js-youtube").forEach((iframe) => {
      iframe.contentWindow?.postMessage(
        '{"event":"command","func":"pauseVideo","args":""}',
        "*"
      );
    });
    document.querySelectorAll(".js-vimeo").forEach((iframe) => {
      iframe.contentWindow?.postMessage('{"method":"pause"}', "*");
    });
    document.querySelectorAll("video").forEach((video) => {
      video.pause();
    });
  }
};

// node_modules/@alpinejs/collapse/dist/module.esm.js
function src_default3(Alpine3) {
  Alpine3.directive("collapse", collapse);
  collapse.inline = (el, { modifiers }) => {
    if (!modifiers.includes("min"))
      return;
    el._x_doShow = () => {
    };
    el._x_doHide = () => {
    };
  };
  function collapse(el, { modifiers }) {
    let duration = modifierValue2(modifiers, "duration", 250) / 1e3;
    let floor3 = modifierValue2(modifiers, "min", 0);
    let fullyHide = !modifiers.includes("min");
    if (!el._x_isShown)
      el.style.height = `${floor3}px`;
    if (!el._x_isShown && fullyHide)
      el.hidden = true;
    if (!el._x_isShown)
      el.style.overflow = "hidden";
    let setFunction = (el2, styles) => {
      let revertFunction = Alpine3.setStyles(el2, styles);
      return styles.height ? () => {
      } : revertFunction;
    };
    let transitionStyles = {
      transitionProperty: "height",
      transitionDuration: `${duration}s`,
      transitionTimingFunction: "cubic-bezier(0.4, 0.0, 0.2, 1)"
    };
    el._x_transition = {
      in(before2 = () => {
      }, after = () => {
      }) {
        if (fullyHide)
          el.hidden = false;
        if (fullyHide)
          el.style.display = null;
        let current = el.getBoundingClientRect().height;
        el.style.height = "auto";
        let full = el.getBoundingClientRect().height;
        if (current === full) {
          current = floor3;
        }
        Alpine3.transition(el, Alpine3.setStyles, {
          during: transitionStyles,
          start: { height: current + "px" },
          end: { height: full + "px" }
        }, () => el._x_isShown = true, () => {
          if (Math.abs(el.getBoundingClientRect().height - full) < 1) {
            el.style.overflow = null;
          }
        });
      },
      out(before2 = () => {
      }, after = () => {
      }) {
        let full = el.getBoundingClientRect().height;
        Alpine3.transition(el, setFunction, {
          during: transitionStyles,
          start: { height: full + "px" },
          end: { height: floor3 + "px" }
        }, () => el.style.overflow = "hidden", () => {
          el._x_isShown = false;
          if (el.style.height == `${floor3}px` && fullyHide) {
            el.style.display = "none";
            el.hidden = true;
          }
        });
      }
    };
  }
}
function modifierValue2(modifiers, key, fallback) {
  if (modifiers.indexOf(key) === -1)
    return fallback;
  const rawValue = modifiers[modifiers.indexOf(key) + 1];
  if (!rawValue)
    return fallback;
  if (key === "duration") {
    let match = rawValue.match(/([0-9]+)ms/);
    if (match)
      return match[1];
  }
  if (key === "min") {
    let match = rawValue.match(/([0-9]+)px/);
    if (match)
      return match[1];
  }
  return rawValue;
}
var module_default3 = src_default3;

// node_modules/@alpinejs/intersect/dist/module.esm.js
function src_default4(Alpine3) {
  Alpine3.directive("intersect", Alpine3.skipDuringClone((el, { value, expression, modifiers }, { evaluateLater: evaluateLater2, cleanup: cleanup2 }) => {
    let evaluate2 = evaluateLater2(expression);
    let options = {
      rootMargin: getRootMargin(modifiers),
      threshold: getThreshold(modifiers),
      root: modifiers.includes("parent") ? el.parentElement : null
    };
    let observer2 = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting === (value === "leave"))
          return;
        evaluate2();
        modifiers.includes("once") && observer2.disconnect();
      });
    }, options);
    observer2.observe(el);
    cleanup2(() => {
      observer2.disconnect();
    });
  }));
}
function getThreshold(modifiers) {
  if (modifiers.includes("full"))
    return 0.99;
  if (modifiers.includes("half"))
    return 0.5;
  if (!modifiers.includes("threshold"))
    return 0;
  let threshold = modifiers[modifiers.indexOf("threshold") + 1];
  if (threshold === "100")
    return 1;
  if (threshold === "0")
    return 0;
  return Number(`.${threshold}`);
}
function getLengthValue(rawValue) {
  let match = rawValue.match(/^(-?[0-9]+)(px|%)?$/);
  return match ? match[1] + (match[2] || "px") : void 0;
}
function getRootMargin(modifiers) {
  const key = "margin";
  const fallback = "0px 0px 0px 0px";
  const index = modifiers.indexOf(key);
  if (index === -1)
    return fallback;
  let values = [];
  for (let i = 1; i < 5; i++) {
    values.push(getLengthValue(modifiers[index + i] || ""));
  }
  values = values.filter((v) => v !== void 0);
  return values.length ? values.join(" ").trim() : fallback;
}
var module_default4 = src_default4;

// node_modules/@fylgja/alpinejs-dialog/dist/index.mjs
function dialog_default(Alpine3) {
  Alpine3.directive("dialog", fylgjaAlpineDialog);
  function fylgjaAlpineDialog(el, { expression, modifiers }, { evaluateLater: evaluateLater2, cleanup: cleanup2 }) {
    const evaluate2 = expression.length ? evaluateLater2(expression) : () => {
    };
    const lockPageScroll = modifiers.includes("noscroll");
    el.style.display = null;
    el.hidden = false;
    el.style.length === 0 && el.removeAttribute("style");
    function scrollLock(use = true) {
      document.body.style.overflow = use ? "hidden" : "";
    }
    function escapeDialog(event) {
      if (event.key !== "Escape")
        return;
      event.preventDefault();
      evaluate2();
    }
    function backdropDialog(event) {
      const rect2 = el.getBoundingClientRect();
      const isInDialog = rect2.top <= event.clientY && event.clientY <= rect2.top + rect2.height && rect2.left <= event.clientX && event.clientX <= rect2.left + rect2.width;
      if (isInDialog)
        return;
      evaluate2();
    }
    el._x_doShow = () => {
      if (el.hasAttribute("open"))
        return;
      el.showModal();
      document.addEventListener("keydown", escapeDialog);
      el.addEventListener("click", backdropDialog);
      scrollLock(lockPageScroll);
    };
    el._x_doHide = () => {
      if (!el.hasAttribute("open"))
        return;
      el.close();
      document.removeEventListener("keydown", escapeDialog);
      el.removeEventListener("click", backdropDialog);
      scrollLock(false);
    };
    cleanup2(() => {
      document.removeEventListener("keydown", escapeDialog);
      el.removeEventListener("click", backdropDialog);
      scrollLock(false);
    });
  }
}
var module_default5 = dialog_default;

// node_modules/@alpinejs/morph/dist/module.esm.js
function morph(from, toHtml, options) {
  monkeyPatchDomSetAttributeToAllowAtSymbols();
  let context = createMorphContext(options);
  let toEl = typeof toHtml === "string" ? createElement(toHtml) : toHtml;
  if (window.Alpine && window.Alpine.closestDataStack && !from._x_dataStack) {
    toEl._x_dataStack = window.Alpine.closestDataStack(from);
    toEl._x_dataStack && window.Alpine.cloneNode(from, toEl);
  }
  context.patch(from, toEl);
  return from;
}
function morphBetween(startMarker, endMarker, toHtml, options = {}) {
  monkeyPatchDomSetAttributeToAllowAtSymbols();
  let context = createMorphContext(options);
  let fromContainer = startMarker.parentNode;
  let fromBlock = new Block(startMarker, endMarker);
  let toContainer = typeof toHtml === "string" ? (() => {
    let container = document.createElement("div");
    container.insertAdjacentHTML("beforeend", toHtml);
    return container;
  })() : toHtml;
  let toStartMarker = document.createComment("[morph-start]");
  let toEndMarker = document.createComment("[morph-end]");
  toContainer.insertBefore(toStartMarker, toContainer.firstChild);
  toContainer.appendChild(toEndMarker);
  let toBlock = new Block(toStartMarker, toEndMarker);
  if (window.Alpine && window.Alpine.closestDataStack) {
    toContainer._x_dataStack = window.Alpine.closestDataStack(fromContainer);
    toContainer._x_dataStack && window.Alpine.cloneNode(fromContainer, toContainer);
  }
  context.patchChildren(fromBlock, toBlock);
}
function createMorphContext(options = {}) {
  let defaultGetKey = (el) => el.getAttribute("key");
  let noop3 = () => {
  };
  let context = {
    key: options.key || defaultGetKey,
    lookahead: options.lookahead || false,
    updating: options.updating || noop3,
    updated: options.updated || noop3,
    removing: options.removing || noop3,
    removed: options.removed || noop3,
    adding: options.adding || noop3,
    added: options.added || noop3
  };
  context.patch = function(from, to) {
    if (context.differentElementNamesTypesOrKeys(from, to)) {
      return context.swapElements(from, to);
    }
    let updateChildrenOnly = false;
    let skipChildren = false;
    let skipUntil = (predicate) => context.skipUntilCondition = predicate;
    if (shouldSkipChildren(context.updating, () => skipChildren = true, skipUntil, from, to, () => updateChildrenOnly = true))
      return;
    if (from.nodeType === 1 && window.Alpine) {
      window.Alpine.cloneNode(from, to);
      if (from._x_teleport && to._x_teleport) {
        context.patch(from._x_teleport, to._x_teleport);
      }
    }
    if (textOrComment(to)) {
      context.patchNodeValue(from, to);
      context.updated(from, to);
      return;
    }
    if (!updateChildrenOnly) {
      context.patchAttributes(from, to);
    }
    context.updated(from, to);
    if (!skipChildren) {
      context.patchChildren(from, to);
    }
  };
  context.differentElementNamesTypesOrKeys = function(from, to) {
    return from.nodeType != to.nodeType || from.nodeName != to.nodeName || context.getKey(from) != context.getKey(to);
  };
  context.swapElements = function(from, to) {
    if (shouldSkip(context.removing, from))
      return;
    let toCloned = to.cloneNode(true);
    if (shouldSkip(context.adding, toCloned))
      return;
    from.replaceWith(toCloned);
    context.removed(from);
    context.added(toCloned);
  };
  context.patchNodeValue = function(from, to) {
    let value = to.nodeValue;
    if (from.nodeValue !== value) {
      from.nodeValue = value;
    }
  };
  context.patchAttributes = function(from, to) {
    if (from._x_transitioning)
      return;
    if (from._x_isShown && !to._x_isShown) {
      return;
    }
    if (!from._x_isShown && to._x_isShown) {
      return;
    }
    let domAttributes = Array.from(from.attributes);
    let toAttributes = Array.from(to.attributes);
    for (let i = domAttributes.length - 1; i >= 0; i--) {
      let name = domAttributes[i].name;
      if (!to.hasAttribute(name)) {
        if (name === "open" && from.nodeName === "DIALOG" && from.open) {
          from.close();
        } else {
          from.removeAttribute(name);
        }
      }
    }
    for (let i = toAttributes.length - 1; i >= 0; i--) {
      let name = toAttributes[i].name;
      let value = toAttributes[i].value;
      if (from.getAttribute(name) !== value) {
        from.setAttribute(name, value);
      }
    }
  };
  context.patchChildren = function(from, to) {
    let fromKeys = context.keyToMap(from.children);
    let fromKeyHoldovers = {};
    let currentTo = getFirstNode(to);
    let currentFrom = getFirstNode(from);
    while (currentTo) {
      seedingMatchingId(currentTo, currentFrom);
      let toKey = context.getKey(currentTo);
      let fromKey = context.getKey(currentFrom);
      if (context.skipUntilCondition) {
        let fromDone = !currentFrom || context.skipUntilCondition(currentFrom);
        let toDone = !currentTo || context.skipUntilCondition(currentTo);
        if (fromDone && toDone) {
          context.skipUntilCondition = null;
        } else {
          if (!fromDone)
            currentFrom = currentFrom && getNextSibling(from, currentFrom);
          if (!toDone)
            currentTo = currentTo && getNextSibling(to, currentTo);
          continue;
        }
      }
      if (!currentFrom) {
        if (toKey && fromKeyHoldovers[toKey]) {
          let holdover = fromKeyHoldovers[toKey];
          from.appendChild(holdover);
          currentFrom = holdover;
          fromKey = context.getKey(currentFrom);
        } else {
          if (!shouldSkip(context.adding, currentTo)) {
            let clone2 = currentTo.cloneNode(true);
            from.appendChild(clone2);
            context.added(clone2);
          }
          currentTo = getNextSibling(to, currentTo);
          continue;
        }
      }
      let isIf = (node) => node && node.nodeType === 8 && node.textContent === "[if BLOCK]><![endif]";
      let isEnd = (node) => node && node.nodeType === 8 && node.textContent === "[if ENDBLOCK]><![endif]";
      if (isIf(currentTo) && isIf(currentFrom)) {
        let nestedIfCount = 0;
        let fromBlockStart = currentFrom;
        while (currentFrom) {
          let next = getNextSibling(from, currentFrom);
          if (isIf(next)) {
            nestedIfCount++;
          } else if (isEnd(next) && nestedIfCount > 0) {
            nestedIfCount--;
          } else if (isEnd(next) && nestedIfCount === 0) {
            currentFrom = next;
            break;
          }
          currentFrom = next;
        }
        let fromBlockEnd = currentFrom;
        nestedIfCount = 0;
        let toBlockStart = currentTo;
        while (currentTo) {
          let next = getNextSibling(to, currentTo);
          if (isIf(next)) {
            nestedIfCount++;
          } else if (isEnd(next) && nestedIfCount > 0) {
            nestedIfCount--;
          } else if (isEnd(next) && nestedIfCount === 0) {
            currentTo = next;
            break;
          }
          currentTo = next;
        }
        let toBlockEnd = currentTo;
        let fromBlock = new Block(fromBlockStart, fromBlockEnd);
        let toBlock = new Block(toBlockStart, toBlockEnd);
        context.patchChildren(fromBlock, toBlock);
        continue;
      }
      if (currentFrom.nodeType === 1 && context.lookahead && !currentFrom.isEqualNode(currentTo)) {
        let nextToElementSibling = getNextSibling(to, currentTo);
        let found = false;
        while (!found && nextToElementSibling) {
          if (nextToElementSibling.nodeType === 1 && currentFrom.isEqualNode(nextToElementSibling)) {
            found = true;
            currentFrom = context.addNodeBefore(from, currentTo, currentFrom);
            fromKey = context.getKey(currentFrom);
          }
          nextToElementSibling = getNextSibling(to, nextToElementSibling);
        }
      }
      if (toKey !== fromKey) {
        if (!toKey && fromKey) {
          fromKeyHoldovers[fromKey] = currentFrom;
          currentFrom = context.addNodeBefore(from, currentTo, currentFrom);
          fromKeyHoldovers[fromKey].remove();
          currentFrom = getNextSibling(from, currentFrom);
          currentTo = getNextSibling(to, currentTo);
          continue;
        }
        if (toKey && !fromKey) {
          if (fromKeys[toKey]) {
            currentFrom.replaceWith(fromKeys[toKey]);
            currentFrom = fromKeys[toKey];
            fromKey = context.getKey(currentFrom);
          }
        }
        if (toKey && fromKey) {
          let fromKeyNode = fromKeys[toKey];
          if (fromKeyNode) {
            fromKeyHoldovers[fromKey] = currentFrom;
            currentFrom.replaceWith(fromKeyNode);
            currentFrom = fromKeyNode;
            fromKey = context.getKey(currentFrom);
          } else {
            fromKeyHoldovers[fromKey] = currentFrom;
            currentFrom = context.addNodeBefore(from, currentTo, currentFrom);
            fromKeyHoldovers[fromKey].remove();
            currentFrom = getNextSibling(from, currentFrom);
            currentTo = getNextSibling(to, currentTo);
            continue;
          }
        }
      }
      let currentFromNext = currentFrom && getNextSibling(from, currentFrom);
      context.patch(currentFrom, currentTo);
      if (currentFrom._x_lastRenderedEl) {
        currentFromNext = getNextSibling(from, currentFrom._x_lastRenderedEl);
      }
      currentTo = currentTo && getNextSibling(to, currentTo);
      currentFrom = currentFromNext;
    }
    let removals = [];
    while (currentFrom) {
      if (!shouldSkip(context.removing, currentFrom))
        removals.push(currentFrom);
      currentFrom = getNextSibling(from, currentFrom);
    }
    while (removals.length) {
      let domForRemoval = removals.shift();
      domForRemoval.remove();
      context.removed(domForRemoval);
    }
  };
  context.getKey = function(el) {
    return el && el.nodeType === 1 && context.key(el);
  };
  context.keyToMap = function(els) {
    let map = {};
    for (let el of els) {
      let theKey = context.getKey(el);
      if (theKey) {
        map[theKey] = el;
      }
    }
    return map;
  };
  context.addNodeBefore = function(parent, node, beforeMe) {
    if (!shouldSkip(context.adding, node)) {
      let clone2 = node.cloneNode(true);
      parent.insertBefore(clone2, beforeMe);
      context.added(clone2);
      return clone2;
    }
    return node;
  };
  return context;
}
morph.step = () => {
};
morph.log = () => {
};
function shouldSkip(hook, ...args) {
  let skip = false;
  hook(...args, () => skip = true);
  return skip;
}
function shouldSkipChildren(hook, skipChildren, skipUntil, ...args) {
  let skip = false;
  hook(...args, () => skip = true, skipChildren, skipUntil);
  return skip;
}
var patched = false;
function createElement(html) {
  const template = document.createElement("template");
  template.innerHTML = html;
  return template.content.firstElementChild;
}
function textOrComment(el) {
  return el.nodeType === 3 || el.nodeType === 8;
}
var Block = class {
  constructor(start2, end) {
    this.startComment = start2;
    this.endComment = end;
  }
  get children() {
    let children2 = [];
    let currentNode = this.startComment.nextSibling;
    while (currentNode && currentNode !== this.endComment) {
      children2.push(currentNode);
      currentNode = currentNode.nextSibling;
    }
    return children2;
  }
  appendChild(child2) {
    this.endComment.before(child2);
  }
  get firstChild() {
    let first = this.startComment.nextSibling;
    if (first === this.endComment)
      return;
    return first;
  }
  nextNode(reference) {
    let next = reference.nextSibling;
    if (next === this.endComment)
      return;
    return next;
  }
  insertBefore(newNode, reference) {
    reference.before(newNode);
    return newNode;
  }
};
function getFirstNode(parent) {
  return parent.firstChild;
}
function getNextSibling(parent, reference) {
  let next;
  if (parent instanceof Block) {
    next = parent.nextNode(reference);
  } else {
    next = reference.nextSibling;
  }
  return next;
}
function monkeyPatchDomSetAttributeToAllowAtSymbols() {
  if (patched)
    return;
  patched = true;
  let original = Element.prototype.setAttribute;
  let hostDiv = document.createElement("div");
  Element.prototype.setAttribute = function newSetAttribute(name, value) {
    if (!name.includes("@")) {
      return original.call(this, name, value);
    }
    let escapedValue = value.replace(/&/g, "&amp;").replace(/"/g, "&quot;");
    hostDiv.innerHTML = `<span ${name}="${escapedValue}"></span>`;
    let attr = hostDiv.firstElementChild.getAttributeNode(name);
    hostDiv.firstElementChild.removeAttributeNode(attr);
    this.setAttributeNode(attr);
  };
}
function seedingMatchingId(to, from) {
  let fromId = from && from._x_bindings && from._x_bindings.id;
  if (!fromId)
    return;
  if (!to.setAttribute)
    return;
  to.setAttribute("id", fromId);
  to.id = fromId;
}
function src_default5(Alpine3) {
  Alpine3.morph = morph;
  Alpine3.morphBetween = morphBetween;
}
var module_default6 = src_default5;

// node_modules/@ryangjchandler/alpine-clipboard/src/index.js
var onCopy = () => {
};
var copy = (target, mimeType = void 0) => {
  if (typeof target === "function") {
    target = target();
  }
  if (typeof target === "object") {
    target = JSON.stringify(target);
  }
  if (mimeType !== void 0) {
    return window.navigator.clipboard.write([
      new ClipboardItem({
        [mimeType]: new Blob([target], {
          type: mimeType
        })
      })
    ]).then(onCopy);
  }
  return window.navigator.clipboard.writeText(target).then(onCopy);
};
function Clipboard(Alpine3) {
  Alpine3.magic("clipboard", () => {
    return copy;
  });
  Alpine3.directive("clipboard", (el, { modifiers, expression }, { evaluateLater: evaluateLater2, cleanup: cleanup2 }) => {
    const getCopyContent = modifiers.includes("raw") ? (c) => c(expression) : evaluateLater2(expression);
    const clickHandler = () => getCopyContent(copy);
    el.addEventListener("click", clickHandler);
    cleanup2(() => {
      el.removeEventListener("click", clickHandler);
    });
  });
}
Clipboard.configure = (config) => {
  if (config.hasOwnProperty("onCopy") && typeof config.onCopy === "function") {
    onCopy = config.onCopy;
  }
  return Clipboard;
};
var src_default6 = Clipboard;

// src/scripts/utilities/get-section.js
async function getSection({ sectionId, uri, pageAware, view }) {
  pageAware = pageAware || false;
  view = view || false;
  let root_url;
  view ? view = `&view=${view}` : view = "";
  pageAware ? root_url = window.location.pathname : root_url = window.Shopify.routes.root;
  uri.length > 0 ? uri = `${uri}&` : "";
  const response = await fetch(`${root_url}${uri}sections=${sectionId}${view}`);
  const responseJson = await response.json();
  return responseJson[sectionId];
}
function getSectionDOM({ html, selector }) {
  selector = selector || ".shopify-section";
  return new DOMParser().parseFromString(html, "text/html");
}
function reRenderSectionComponent({
  selector,
  section,
  newHTML,
  append: append2,
  removeEmpty
}) {
  selector = selector || ".shopify-section";
  append2 = append2 || false;
  removeEmpty = removeEmpty || false;
  if (!section || !newHTML) return;
  const targets = section.querySelectorAll(selector);
  if (!targets.length) return;
  const sources = newHTML.querySelectorAll(selector);
  targets.forEach((target, index) => {
    const source = sources[index];
    if (!source) {
      if (removeEmpty) {
        target.innerHTML = "";
      }
      return;
    }
    const html = source.innerHTML;
    if (append2) {
      target.insertAdjacentHTML("beforeend", html);
    } else {
      target.innerHTML = html;
    }
    if (window.Alpine) {
      Alpine.initTree(target);
    }
  });
}
var get_section_default = getSection;

// src/scripts/components/cart.js
var debug = false;
var Cart2 = {
  getSectionsToRender() {
    function getSectionId(selector) {
      return document.querySelector(selector)?.id?.replace("shopify-section-", "");
    }
    const mainCartId = getSectionId(".shopify-section--main-cart");
    const cartDrawerId = getSectionId(".shopify-section--cart-drawer");
    const headerCartId = getSectionId(".shopify-section-header");
    const sectionsToUpdate = [
      {
        id: mainCartId,
        selector: ".cart-page-inner"
      },
      {
        id: cartDrawerId,
        selector: ".cart-drawer-inner"
      },
      {
        id: headerCartId,
        selector: ".header-cart-bubble"
      }
    ];
    if (debug) {
      sectionsToUpdate.forEach((section) => {
        const element = document.querySelector(section.selector);
        if (element) {
          console.log(
            `HTML for section with ID "${section.id}" and selector "${section.selector}":`
          );
          console.log(element.outerHTML);
        } else {
          console.warn(`Element not found for selector "${section.selector}"`);
        }
      });
    }
    return sectionsToUpdate;
  },
  // Shopify renders an empty container for PayPal and the other dynamic
  // checkout buttons, then fills it client-side after page load. The Section
  // Rendering API only ever returns the empty container, so any cart re-render
  // replaces working buttons with a blank shell and Shopify never re-scans.
  DYNAMIC_CHECKOUT_SELECTOR: "[data-dynamic-checkout]",
  // Re-runs Shopify's dynamic checkout setup. Only needed where the container
  // is genuinely new, since a preserved one still holds its live buttons.
  initDynamicCheckout() {
    document.querySelectorAll(Cart2.DYNAMIC_CHECKOUT_SELECTOR).forEach((container) => {
      if (container.childElementCount > 0) return;
      window.Shopify?.PaymentButton?.init?.();
    });
  },
  renderCartSections(sectionsHTML, morph2 = true) {
    const sectionsToUpdate = Cart2.getSectionsToRender();
    if (debug) console.log(this.renderCartSections);
    sectionsToUpdate?.map((section) => {
      const section_id = document.getElementById(
        `shopify-section-${section.id}`
      );
      if (!section_id || !sectionsHTML) return;
      let sectionSelector = section_id.querySelector(section.selector);
      let updatedContentDOM = getSectionDOM({
        html: sectionsHTML[section.id],
        selector: section.selector
      });
      let updatedContentElement = updatedContentDOM.querySelector(
        section.selector
      );
      let updatedContent = updatedContentElement ? updatedContentElement.outerHTML : null;
      if (morph2) {
        Alpine.morph(sectionSelector, updatedContent, {
          updating(el, _toEl, _childrenOnly, skip) {
            if (el.matches?.(Cart2.DYNAMIC_CHECKOUT_SELECTOR)) skip();
          }
        });
      } else {
        sectionSelector.outerHTML = updatedContent;
      }
    });
    Cart2.initDynamicCheckout();
  },
  async updateQuantity(line, qty) {
    const sectionsToUpdate = Cart2.getSectionsToRender();
    const sections = Array.from(sectionsToUpdate, (section) => section.id);
    if (debug) console.log(this.updateQuantity);
    try {
      const cart = await fetch(window.Shopify.routes.root + "cart.js").then(
        (r) => r.json()
      );
      const changedItem = cart.items[line - 1];
      const updates = {};
      if (changedItem) {
        updates[changedItem.key] = qty;
        const coveredVariantId = String(changedItem.variant_id);
        cart.items.forEach((cartItem) => {
          if (cartItem.properties?._covers_variant === coveredVariantId) {
            updates[cartItem.key] = qty;
          }
        });
      }
      const response = await fetch(
        window.Shopify.routes.root + "cart/update.js",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ updates, sections })
        }
      );
      const data2 = await response.json();
      if (!response.ok) {
        if (response.status == 422) {
          Alpine.store("errors").add(data2.message);
          throw new Error(`${data2.description}: ${data2.message}`);
        }
      }
      Cart2.renderCartSections(data2.sections);
    } catch (error2) {
      console.error("Error:", error2);
    }
    window.dispatchEvent(new Event("cart-updated"));
  },
  async updateCart(toggleCart = true) {
    const sectionsToUpdate = Cart2.getSectionsToRender();
    if (debug) console.log(this.updateCart);
    try {
      const response = await fetch(
        window.Shopify.routes.root + `?sections=${Array.from(sectionsToUpdate, (section) => section.id)}`
      );
      const html = await response.text();
      const sectionsHTML = JSON.parse(html);
      Cart2.renderCartSections(sectionsHTML);
      window.dispatchEvent(new Event("cart-updated"));
      toggleCart && window.dispatchEvent(new Event("cart-toggle"));
    } catch (error2) {
      console.error("Error:", error2);
    }
  },
  async clearCart() {
    try {
      await fetch(window.Shopify.routes.root + "cart/clear.js", {
        method: "POST"
      });
      window.dispatchEvent(new Event("cart-updated"));
    } catch (error2) {
      console.error("Error:", error2);
    }
  },
  // Adds several line items in one request, via the JSON form of /cart/add.js.
  // Used when a product is bought alongside a warranty or service plan: two
  // sequential addToCart calls would leave the cart half updated if the second
  // failed, and would render the cart sections twice.
  async addItems(items) {
    if (debug) console.log(this.addItems, items);
    const sectionsToUpdate = Cart2.getSectionsToRender();
    try {
      const response = await fetch(window.routes.cart_add_url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({
          items,
          sections: Array.from(sectionsToUpdate, (section) => section.id)
        })
      });
      const responseData = await response.json();
      if (!response.ok) {
        Alpine.store("errors").add(
          responseData.description || responseData.message || window.cartStrings.error
        );
        return;
      }
      if (responseData.sections) {
        Cart2.renderCartSections(responseData.sections, false);
        window.dispatchEvent(new Event("cart-updated"));
        const drawer = document.querySelector("dialog.cart-drawer");
        if (!drawer?.hasAttribute("open")) {
          window.dispatchEvent(new Event("cart-toggle"));
        }
      }
    } catch (error2) {
      console.error("Error:", error2);
      Alpine.store("errors").add(window.cartStrings.error);
    }
  },
  async addToCart(data2) {
    if (debug) console.log(this.addToCart);
    const sectionsToUpdate = Cart2.getSectionsToRender();
    const headers = () => {
      return {
        Accept: `application/json`,
        "X-Requested-With": "XMLHttpRequest"
      };
    };
    data2.set(
      "sections",
      Array.from(sectionsToUpdate, (section) => section.id)
    );
    try {
      const response = await fetch(window.routes.cart_add_url, {
        method: "POST",
        headers: headers(),
        body: data2
      });
      const responseData = await response.json();
      if (!response.ok || responseData.id == void 0) {
        if (responseData.id == void 0 && !responseData?.errors) {
          if (data2.has("requires_selling_plan") && data2.get("requires_selling_plan") == "true" && !data2.get("selling_plan")) {
            Alpine.store("errors").add(window.cartStrings.selling_plan_error);
          } else {
            Alpine.store("errors").add(window.cartStrings.error);
          }
          return;
        } else {
          Alpine.store("errors").add(responseData.errors);
          if (!responseData.sections) {
            const responseSections = await fetch(
              window.routes.cart_url + `?sections=${Array.from(
                sectionsToUpdate,
                (section) => section.id
              )}`
            );
            responseData.sections = await responseSections.json();
          }
        }
      }
      if (responseData.sections) {
        Cart2.renderCartSections(responseData.sections, false);
        window.dispatchEvent(new Event("cart-updated"));
        const drawer = document.querySelector("dialog.cart-drawer");
        const isDrawerOpen = drawer?.hasAttribute("open");
        if (!isDrawerOpen) {
          window.dispatchEvent(new Event("cart-toggle"));
        }
      }
    } catch (error2) {
      console.error("Error:", error2);
    }
  }
};
window.Cart = Cart2;
document.addEventListener("change", async (event) => {
  const select = event.target.closest(".cart-item__warranty-select");
  if (!select) return;
  const wrapper = select.closest("[data-cart-item-warranty]");
  if (!wrapper) return;
  const coversVariant = wrapper.dataset.coversVariant;
  const coversTitle = wrapper.dataset.coversTitle;
  const existingLine = Number(wrapper.dataset.existingLine) || 0;
  const quantity = Number(select.dataset.quantity) || 1;
  const newVariantId = select.value;
  select.disabled = true;
  if (existingLine > 0) {
    await Cart2.updateQuantity(existingLine, 0);
  }
  if (newVariantId) {
    await Cart2.addItems([
      {
        id: Number(newVariantId),
        quantity,
        properties: {
          _covers_variant: coversVariant,
          // Underscore-prefixed so it stays off the customer-facing cart line
          // (and the hidden .cart-item--addon row besides), but still shows
          // on the line item in Shopify Admin - a readable title beats the
          // bare variant id above for telling cover apart from order to order.
          _covers_product: coversTitle
        }
      }
    ]);
  }
});

// node_modules/dialog-polyfill/dist/dialog-polyfill.esm.js
var supportCustomEvent = window.CustomEvent;
if (!supportCustomEvent || typeof supportCustomEvent === "object") {
  supportCustomEvent = function CustomEvent2(event, x) {
    x = x || {};
    var ev = document.createEvent("CustomEvent");
    ev.initCustomEvent(event, !!x.bubbles, !!x.cancelable, x.detail || null);
    return ev;
  };
  supportCustomEvent.prototype = window.Event.prototype;
}
function safeDispatchEvent(target, event) {
  var check = "on" + event.type.toLowerCase();
  if (typeof target[check] === "function") {
    target[check](event);
  }
  return target.dispatchEvent(event);
}
function createsStackingContext(el) {
  while (el && el !== document.body) {
    var s = window.getComputedStyle(el);
    var invalid = function(k, ok) {
      return !(s[k] === void 0 || s[k] === ok);
    };
    if (s.opacity < 1 || invalid("zIndex", "auto") || invalid("transform", "none") || invalid("mixBlendMode", "normal") || invalid("filter", "none") || invalid("perspective", "none") || s["isolation"] === "isolate" || s.position === "fixed" || s.webkitOverflowScrolling === "touch") {
      return true;
    }
    el = el.parentElement;
  }
  return false;
}
function findNearestDialog(el) {
  while (el) {
    if (el.localName === "dialog") {
      return (
        /** @type {HTMLDialogElement} */
        el
      );
    }
    if (el.parentElement) {
      el = el.parentElement;
    } else if (el.parentNode) {
      el = el.parentNode.host;
    } else {
      el = null;
    }
  }
  return null;
}
function safeBlur(el) {
  while (el && el.shadowRoot && el.shadowRoot.activeElement) {
    el = el.shadowRoot.activeElement;
  }
  if (el && el.blur && el !== document.body) {
    el.blur();
  }
}
function inNodeList(nodeList, node) {
  for (var i = 0; i < nodeList.length; ++i) {
    if (nodeList[i] === node) {
      return true;
    }
  }
  return false;
}
function isFormMethodDialog(el) {
  if (!el || !el.hasAttribute("method")) {
    return false;
  }
  return el.getAttribute("method").toLowerCase() === "dialog";
}
function findFocusableElementWithin(hostElement) {
  var opts = ["button", "input", "keygen", "select", "textarea"];
  var query2 = opts.map(function(el) {
    return el + ":not([disabled])";
  });
  query2.push('[tabindex]:not([disabled]):not([tabindex=""])');
  var target = hostElement.querySelector(query2.join(", "));
  if (!target && "attachShadow" in Element.prototype) {
    var elems = hostElement.querySelectorAll("*");
    for (var i = 0; i < elems.length; i++) {
      if (elems[i].tagName && elems[i].shadowRoot) {
        target = findFocusableElementWithin(elems[i].shadowRoot);
        if (target) {
          break;
        }
      }
    }
  }
  return target;
}
function isConnected(element) {
  return element.isConnected || document.body.contains(element);
}
function findFormSubmitter(event) {
  if (event.submitter) {
    return event.submitter;
  }
  var form = event.target;
  if (!(form instanceof HTMLFormElement)) {
    return null;
  }
  var submitter = dialogPolyfill.formSubmitter;
  if (!submitter) {
    var target = event.target;
    var root = "getRootNode" in target && target.getRootNode() || document;
    submitter = root.activeElement;
  }
  if (!submitter || submitter.form !== form) {
    return null;
  }
  return submitter;
}
function maybeHandleSubmit(event) {
  if (event.defaultPrevented) {
    return;
  }
  var form = (
    /** @type {!HTMLFormElement} */
    event.target
  );
  var value = dialogPolyfill.imagemapUseValue;
  var submitter = findFormSubmitter(event);
  if (value === null && submitter) {
    value = submitter.value;
  }
  var dialog2 = findNearestDialog(form);
  if (!dialog2) {
    return;
  }
  var formmethod = submitter && submitter.getAttribute("formmethod") || form.getAttribute("method");
  if (formmethod !== "dialog") {
    return;
  }
  event.preventDefault();
  if (value != null) {
    dialog2.close(value);
  } else {
    dialog2.close();
  }
}
function dialogPolyfillInfo(dialog2) {
  this.dialog_ = dialog2;
  this.replacedStyleTop_ = false;
  this.openAsModal_ = false;
  if (!dialog2.hasAttribute("role")) {
    dialog2.setAttribute("role", "dialog");
  }
  dialog2.show = this.show.bind(this);
  dialog2.showModal = this.showModal.bind(this);
  dialog2.close = this.close.bind(this);
  dialog2.addEventListener("submit", maybeHandleSubmit, false);
  if (!("returnValue" in dialog2)) {
    dialog2.returnValue = "";
  }
  if ("MutationObserver" in window) {
    var mo = new MutationObserver(this.maybeHideModal.bind(this));
    mo.observe(dialog2, { attributes: true, attributeFilter: ["open"] });
  } else {
    var removed = false;
    var cb = function() {
      removed ? this.downgradeModal() : this.maybeHideModal();
      removed = false;
    }.bind(this);
    var timeout;
    var delayModel = function(ev) {
      if (ev.target !== dialog2) {
        return;
      }
      var cand = "DOMNodeRemoved";
      removed |= ev.type.substr(0, cand.length) === cand;
      window.clearTimeout(timeout);
      timeout = window.setTimeout(cb, 0);
    };
    ["DOMAttrModified", "DOMNodeRemoved", "DOMNodeRemovedFromDocument"].forEach(function(name) {
      dialog2.addEventListener(name, delayModel);
    });
  }
  Object.defineProperty(dialog2, "open", {
    set: this.setOpen.bind(this),
    get: dialog2.hasAttribute.bind(dialog2, "open")
  });
  this.backdrop_ = document.createElement("div");
  this.backdrop_.className = "backdrop";
  this.backdrop_.addEventListener("mouseup", this.backdropMouseEvent_.bind(this));
  this.backdrop_.addEventListener("mousedown", this.backdropMouseEvent_.bind(this));
  this.backdrop_.addEventListener("click", this.backdropMouseEvent_.bind(this));
}
dialogPolyfillInfo.prototype = /** @type {HTMLDialogElement.prototype} */
{
  get dialog() {
    return this.dialog_;
  },
  /**
   * Maybe remove this dialog from the modal top layer. This is called when
   * a modal dialog may no longer be tenable, e.g., when the dialog is no
   * longer open or is no longer part of the DOM.
   */
  maybeHideModal: function() {
    if (this.dialog_.hasAttribute("open") && isConnected(this.dialog_)) {
      return;
    }
    this.downgradeModal();
  },
  /**
   * Remove this dialog from the modal top layer, leaving it as a non-modal.
   */
  downgradeModal: function() {
    if (!this.openAsModal_) {
      return;
    }
    this.openAsModal_ = false;
    this.dialog_.style.zIndex = "";
    if (this.replacedStyleTop_) {
      this.dialog_.style.top = "";
      this.replacedStyleTop_ = false;
    }
    this.backdrop_.parentNode && this.backdrop_.parentNode.removeChild(this.backdrop_);
    dialogPolyfill.dm.removeDialog(this);
  },
  /**
   * @param {boolean} value whether to open or close this dialog
   */
  setOpen: function(value) {
    if (value) {
      this.dialog_.hasAttribute("open") || this.dialog_.setAttribute("open", "");
    } else {
      this.dialog_.removeAttribute("open");
      this.maybeHideModal();
    }
  },
  /**
   * Handles mouse events ('mouseup', 'mousedown', 'click') on the fake .backdrop element, redirecting them as if
   * they were on the dialog itself.
   *
   * @param {!Event} e to redirect
   */
  backdropMouseEvent_: function(e) {
    if (!this.dialog_.hasAttribute("tabindex")) {
      var fake = document.createElement("div");
      this.dialog_.insertBefore(fake, this.dialog_.firstChild);
      fake.tabIndex = -1;
      fake.focus();
      this.dialog_.removeChild(fake);
    } else {
      this.dialog_.focus();
    }
    var redirectedEvent = document.createEvent("MouseEvents");
    redirectedEvent.initMouseEvent(
      e.type,
      e.bubbles,
      e.cancelable,
      window,
      e.detail,
      e.screenX,
      e.screenY,
      e.clientX,
      e.clientY,
      e.ctrlKey,
      e.altKey,
      e.shiftKey,
      e.metaKey,
      e.button,
      e.relatedTarget
    );
    this.dialog_.dispatchEvent(redirectedEvent);
    e.stopPropagation();
  },
  /**
   * Focuses on the first focusable element within the dialog. This will always blur the current
   * focus, even if nothing within the dialog is found.
   */
  focus_: function() {
    var target = this.dialog_.querySelector("[autofocus]:not([disabled])");
    if (!target && this.dialog_.tabIndex >= 0) {
      target = this.dialog_;
    }
    if (!target) {
      target = findFocusableElementWithin(this.dialog_);
    }
    safeBlur(document.activeElement);
    target && target.focus();
  },
  /**
   * Sets the zIndex for the backdrop and dialog.
   *
   * @param {number} dialogZ
   * @param {number} backdropZ
   */
  updateZIndex: function(dialogZ, backdropZ) {
    if (dialogZ < backdropZ) {
      throw new Error("dialogZ should never be < backdropZ");
    }
    this.dialog_.style.zIndex = dialogZ;
    this.backdrop_.style.zIndex = backdropZ;
  },
  /**
   * Shows the dialog. If the dialog is already open, this does nothing.
   */
  show: function() {
    if (!this.dialog_.open) {
      this.setOpen(true);
      this.focus_();
    }
  },
  /**
   * Show this dialog modally.
   */
  showModal: function() {
    if (this.dialog_.hasAttribute("open")) {
      throw new Error("Failed to execute 'showModal' on dialog: The element is already open, and therefore cannot be opened modally.");
    }
    if (!isConnected(this.dialog_)) {
      throw new Error("Failed to execute 'showModal' on dialog: The element is not in a Document.");
    }
    if (!dialogPolyfill.dm.pushDialog(this)) {
      throw new Error("Failed to execute 'showModal' on dialog: There are too many open modal dialogs.");
    }
    if (createsStackingContext(this.dialog_.parentElement)) {
      console.warn("A dialog is being shown inside a stacking context. This may cause it to be unusable. For more information, see this link: https://github.com/GoogleChrome/dialog-polyfill/#stacking-context");
    }
    this.setOpen(true);
    this.openAsModal_ = true;
    if (dialogPolyfill.needsCentering(this.dialog_)) {
      dialogPolyfill.reposition(this.dialog_);
      this.replacedStyleTop_ = true;
    } else {
      this.replacedStyleTop_ = false;
    }
    this.dialog_.parentNode.insertBefore(this.backdrop_, this.dialog_.nextSibling);
    this.focus_();
  },
  /**
   * Closes this HTMLDialogElement. This is optional vs clearing the open
   * attribute, however this fires a 'close' event.
   *
   * @param {string=} opt_returnValue to use as the returnValue
   */
  close: function(opt_returnValue) {
    if (!this.dialog_.hasAttribute("open")) {
      throw new Error("Failed to execute 'close' on dialog: The element does not have an 'open' attribute, and therefore cannot be closed.");
    }
    this.setOpen(false);
    if (opt_returnValue !== void 0) {
      this.dialog_.returnValue = opt_returnValue;
    }
    var closeEvent = new supportCustomEvent("close", {
      bubbles: false,
      cancelable: false
    });
    safeDispatchEvent(this.dialog_, closeEvent);
  }
};
var dialogPolyfill = {};
dialogPolyfill.reposition = function(element) {
  var scrollTop = document.body.scrollTop || document.documentElement.scrollTop;
  var topValue = scrollTop + (window.innerHeight - element.offsetHeight) / 2;
  element.style.top = Math.max(scrollTop, topValue) + "px";
};
dialogPolyfill.isInlinePositionSetByStylesheet = function(element) {
  for (var i = 0; i < document.styleSheets.length; ++i) {
    var styleSheet = document.styleSheets[i];
    var cssRules = null;
    try {
      cssRules = styleSheet.cssRules;
    } catch (e) {
    }
    if (!cssRules) {
      continue;
    }
    for (var j = 0; j < cssRules.length; ++j) {
      var rule = cssRules[j];
      var selectedNodes = null;
      try {
        selectedNodes = document.querySelectorAll(rule.selectorText);
      } catch (e) {
      }
      if (!selectedNodes || !inNodeList(selectedNodes, element)) {
        continue;
      }
      var cssTop = rule.style.getPropertyValue("top");
      var cssBottom = rule.style.getPropertyValue("bottom");
      if (cssTop && cssTop !== "auto" || cssBottom && cssBottom !== "auto") {
        return true;
      }
    }
  }
  return false;
};
dialogPolyfill.needsCentering = function(dialog2) {
  var computedStyle = window.getComputedStyle(dialog2);
  if (computedStyle.position !== "absolute") {
    return false;
  }
  if (dialog2.style.top !== "auto" && dialog2.style.top !== "" || dialog2.style.bottom !== "auto" && dialog2.style.bottom !== "") {
    return false;
  }
  return !dialogPolyfill.isInlinePositionSetByStylesheet(dialog2);
};
dialogPolyfill.forceRegisterDialog = function(element) {
  if (window.HTMLDialogElement || element.showModal) {
    console.warn("This browser already supports <dialog>, the polyfill may not work correctly", element);
  }
  if (element.localName !== "dialog") {
    throw new Error("Failed to register dialog: The element is not a dialog.");
  }
  new dialogPolyfillInfo(
    /** @type {!HTMLDialogElement} */
    element
  );
};
dialogPolyfill.registerDialog = function(element) {
  if (!element.showModal) {
    dialogPolyfill.forceRegisterDialog(element);
  }
};
dialogPolyfill.DialogManager = function() {
  this.pendingDialogStack = [];
  var checkDOM = this.checkDOM_.bind(this);
  this.overlay = document.createElement("div");
  this.overlay.className = "_dialog_overlay";
  this.overlay.addEventListener("click", function(e) {
    this.forwardTab_ = void 0;
    e.stopPropagation();
    checkDOM([]);
  }.bind(this));
  this.handleKey_ = this.handleKey_.bind(this);
  this.handleFocus_ = this.handleFocus_.bind(this);
  this.zIndexLow_ = 1e5;
  this.zIndexHigh_ = 1e5 + 150;
  this.forwardTab_ = void 0;
  if ("MutationObserver" in window) {
    this.mo_ = new MutationObserver(function(records) {
      var removed = [];
      records.forEach(function(rec) {
        for (var i = 0, c; c = rec.removedNodes[i]; ++i) {
          if (!(c instanceof Element)) {
            continue;
          } else if (c.localName === "dialog") {
            removed.push(c);
          }
          removed = removed.concat(c.querySelectorAll("dialog"));
        }
      });
      removed.length && checkDOM(removed);
    });
  }
};
dialogPolyfill.DialogManager.prototype.blockDocument = function() {
  document.documentElement.addEventListener("focus", this.handleFocus_, true);
  document.addEventListener("keydown", this.handleKey_);
  this.mo_ && this.mo_.observe(document, { childList: true, subtree: true });
};
dialogPolyfill.DialogManager.prototype.unblockDocument = function() {
  document.documentElement.removeEventListener("focus", this.handleFocus_, true);
  document.removeEventListener("keydown", this.handleKey_);
  this.mo_ && this.mo_.disconnect();
};
dialogPolyfill.DialogManager.prototype.updateStacking = function() {
  var zIndex = this.zIndexHigh_;
  for (var i = 0, dpi; dpi = this.pendingDialogStack[i]; ++i) {
    dpi.updateZIndex(--zIndex, --zIndex);
    if (i === 0) {
      this.overlay.style.zIndex = --zIndex;
    }
  }
  var last = this.pendingDialogStack[0];
  if (last) {
    var p = last.dialog.parentNode || document.body;
    p.appendChild(this.overlay);
  } else if (this.overlay.parentNode) {
    this.overlay.parentNode.removeChild(this.overlay);
  }
};
dialogPolyfill.DialogManager.prototype.containedByTopDialog_ = function(candidate) {
  while (candidate = findNearestDialog(candidate)) {
    for (var i = 0, dpi; dpi = this.pendingDialogStack[i]; ++i) {
      if (dpi.dialog === candidate) {
        return i === 0;
      }
    }
    candidate = candidate.parentElement;
  }
  return false;
};
dialogPolyfill.DialogManager.prototype.handleFocus_ = function(event) {
  var target = event.composedPath ? event.composedPath()[0] : event.target;
  if (this.containedByTopDialog_(target)) {
    return;
  }
  if (document.activeElement === document.documentElement) {
    return;
  }
  event.preventDefault();
  event.stopPropagation();
  safeBlur(
    /** @type {Element} */
    target
  );
  if (this.forwardTab_ === void 0) {
    return;
  }
  var dpi = this.pendingDialogStack[0];
  var dialog2 = dpi.dialog;
  var position = dialog2.compareDocumentPosition(target);
  if (position & Node.DOCUMENT_POSITION_PRECEDING) {
    if (this.forwardTab_) {
      dpi.focus_();
    } else if (target !== document.documentElement) {
      document.documentElement.focus();
    }
  }
  return false;
};
dialogPolyfill.DialogManager.prototype.handleKey_ = function(event) {
  this.forwardTab_ = void 0;
  if (event.keyCode === 27) {
    event.preventDefault();
    event.stopPropagation();
    var cancelEvent = new supportCustomEvent("cancel", {
      bubbles: false,
      cancelable: true
    });
    var dpi = this.pendingDialogStack[0];
    if (dpi && safeDispatchEvent(dpi.dialog, cancelEvent)) {
      dpi.dialog.close();
    }
  } else if (event.keyCode === 9) {
    this.forwardTab_ = !event.shiftKey;
  }
};
dialogPolyfill.DialogManager.prototype.checkDOM_ = function(removed) {
  var clone2 = this.pendingDialogStack.slice();
  clone2.forEach(function(dpi) {
    if (removed.indexOf(dpi.dialog) !== -1) {
      dpi.downgradeModal();
    } else {
      dpi.maybeHideModal();
    }
  });
};
dialogPolyfill.DialogManager.prototype.pushDialog = function(dpi) {
  var allowed = (this.zIndexHigh_ - this.zIndexLow_) / 2 - 1;
  if (this.pendingDialogStack.length >= allowed) {
    return false;
  }
  if (this.pendingDialogStack.unshift(dpi) === 1) {
    this.blockDocument();
  }
  this.updateStacking();
  return true;
};
dialogPolyfill.DialogManager.prototype.removeDialog = function(dpi) {
  var index = this.pendingDialogStack.indexOf(dpi);
  if (index === -1) {
    return;
  }
  this.pendingDialogStack.splice(index, 1);
  if (this.pendingDialogStack.length === 0) {
    this.unblockDocument();
  }
  this.updateStacking();
};
dialogPolyfill.dm = new dialogPolyfill.DialogManager();
dialogPolyfill.formSubmitter = null;
dialogPolyfill.imagemapUseValue = null;
if (window.HTMLDialogElement === void 0) {
  testForm = document.createElement("form");
  testForm.setAttribute("method", "dialog");
  if (testForm.method !== "dialog") {
    methodDescriptor = Object.getOwnPropertyDescriptor(HTMLFormElement.prototype, "method");
    if (methodDescriptor) {
      realGet = methodDescriptor.get;
      methodDescriptor.get = function() {
        if (isFormMethodDialog(this)) {
          return "dialog";
        }
        return realGet.call(this);
      };
      realSet = methodDescriptor.set;
      methodDescriptor.set = function(v) {
        if (typeof v === "string" && v.toLowerCase() === "dialog") {
          return this.setAttribute("method", v);
        }
        return realSet.call(this, v);
      };
      Object.defineProperty(HTMLFormElement.prototype, "method", methodDescriptor);
    }
  }
  document.addEventListener("click", function(ev) {
    dialogPolyfill.formSubmitter = null;
    dialogPolyfill.imagemapUseValue = null;
    if (ev.defaultPrevented) {
      return;
    }
    var target = (
      /** @type {Element} */
      ev.target
    );
    if ("composedPath" in ev) {
      var path = ev.composedPath();
      target = path.shift() || target;
    }
    if (!target || !isFormMethodDialog(target.form)) {
      return;
    }
    var valid = target.type === "submit" && ["button", "input"].indexOf(target.localName) > -1;
    if (!valid) {
      if (!(target.localName === "input" && target.type === "image")) {
        return;
      }
      dialogPolyfill.imagemapUseValue = ev.offsetX + "," + ev.offsetY;
    }
    var dialog2 = findNearestDialog(target);
    if (!dialog2) {
      return;
    }
    dialogPolyfill.formSubmitter = target;
  }, false);
  document.addEventListener("submit", function(ev) {
    var form = ev.target;
    var dialog2 = findNearestDialog(form);
    if (dialog2) {
      return;
    }
    var submitter = findFormSubmitter(ev);
    var formmethod = submitter && submitter.getAttribute("formmethod") || form.getAttribute("method");
    if (formmethod === "dialog") {
      ev.preventDefault();
    }
  });
  nativeFormSubmit = HTMLFormElement.prototype.submit;
  replacementFormSubmit = function() {
    if (!isFormMethodDialog(this)) {
      return nativeFormSubmit.call(this);
    }
    var dialog2 = findNearestDialog(this);
    dialog2 && dialog2.close();
  };
  HTMLFormElement.prototype.submit = replacementFormSubmit;
}
var testForm;
var methodDescriptor;
var realGet;
var realSet;
var nativeFormSubmit;
var replacementFormSubmit;
var dialog_polyfill_esm_default = dialogPolyfill;

// src/scripts/utilities/browsing-context.js
var getBrowsingContext = async () => await fetch(
  window.Shopify.routes.root + `browsing_context_suggestions.json?country[enabled]=true&country[exclude]=${window.Shopify.country}&language[enabled]=true&language[exclude]=${window.Shopify.language}`
).then((data2) => data2.json());
var browsing_context_default = getBrowsingContext;

// src/scripts/components/localisation.js
function setBrowsingContext(data2) {
  const dialog2 = document.getElementById("localisation-popup");
  if (!dialog2) return;
  const detectedCountry = data2.detected_values.country.handle;
  const language = data2.suggestions[0].parts.language.handle;
  const form = dialog2?.querySelector("form");
  const submit = document.getElementById("submit-localisation");
  const languageInput = dialog2.querySelector('input[name="language_code"]');
  const countryInput = dialog2.querySelector('input[name="country_code"]');
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const queryStringCountry = urlParams.get("country");
  submit.autofocus = false;
  function setLocalisationDefaults() {
    const countryList = document.getElementById("CountryList");
    const languageList = document.getElementById("LanguageList");
    const languageListButton = languageList?.closest(".disclosure")?.querySelector(".disclosure__button_text");
    const countryListButton = countryList?.closest(".disclosure")?.querySelector(".disclosure__button_text");
    let availableCountry = queryStringCountry || detectedCountry;
    const languageListItem = languageList?.querySelector(
      `[lang="${language}"]`
    );
    const countryListItem = countryList?.querySelector(
      `[country="${availableCountry}"]`
    );
    const flag = dialog2.querySelectorAll(".localisation__country_flag img")[0];
    const country_tag = dialog2.querySelectorAll(".country_tag")[0];
    if (languageListItem) {
      languageInput.value = language;
      languageListButton.innerHTML = languageList.querySelector(
        `[lang="${language}"]`
      ).text;
    }
    if (countryListItem) {
      const countryName = countryListItem.getAttribute("country_name");
      countryInput.value = availableCountry;
      countryListButton.innerHTML = countryListItem.text;
      country_tag.innerHTML = countryName;
      flag.setAttribute(
        "src",
        `http://cdn.shopify.com/static/images/flags/${availableCountry.toLowerCase()}.svg?width=80`
      );
      flag.setAttribute(
        "srcset",
        `http://cdn.shopify.com/static/images/flags/${availableCountry.toLowerCase()}.svg?width=80`
      );
      flag.setAttribute("alt", countryName);
    }
  }
  function setLocalisationPreferences(country, language2) {
    localStorage.setItem(
      "localisationPreferences",
      JSON.stringify({
        country,
        language: language2
      })
    );
  }
  const localisationPreferences = JSON.parse(
    localStorage.getItem("localisationPreferences")
  );
  if (!localisationPreferences) {
    setLocalisationDefaults();
    dialog2.showModal();
  } else {
    if (queryStringCountry)
      setLocalisationPreferences(queryStringCountry, languageInput?.value);
  }
  function submitLocalisationForm() {
    setLocalisationPreferences(countryInput?.value, languageInput?.value);
    if (form) form.submit();
  }
  function localisationPopup() {
    const close = dialog2.querySelector(".close");
    close.addEventListener("click", () => {
      dialog2.close();
      submitLocalisationForm();
    });
    dialog2.addEventListener("cancel", () => {
      dialog2.close();
      submitLocalisationForm();
    });
    submit.addEventListener("click", (e) => {
      e.preventDefault();
      submitLocalisationForm();
    });
  }
  localisationPopup();
}
browsing_context_default().then((data2) => setBrowsingContext(data2));
var openDialog = document.getElementById("openDialog");
var dialog = document.getElementById("localisation-popup");
if (dialog) dialog_polyfill_esm_default.registerDialog(dialog);
openDialog?.addEventListener("click", () => {
  dialog?.showModal();
});

// node_modules/@splidejs/splide/dist/js/splide.esm.js
function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}
function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  Object.defineProperty(Constructor, "prototype", { writable: false });
  return Constructor;
}
var MEDIA_PREFERS_REDUCED_MOTION = "(prefers-reduced-motion: reduce)";
var CREATED = 1;
var MOUNTED = 2;
var IDLE = 3;
var MOVING = 4;
var SCROLLING = 5;
var DRAGGING = 6;
var DESTROYED = 7;
var STATES = {
  CREATED,
  MOUNTED,
  IDLE,
  MOVING,
  SCROLLING,
  DRAGGING,
  DESTROYED
};
function empty(array) {
  array.length = 0;
}
function slice(arrayLike, start2, end) {
  return Array.prototype.slice.call(arrayLike, start2, end);
}
function apply(func) {
  return func.bind.apply(func, [null].concat(slice(arguments, 1)));
}
var nextTick2 = setTimeout;
var noop = function noop2() {
};
function raf(func) {
  return requestAnimationFrame(func);
}
function typeOf(type, subject) {
  return typeof subject === type;
}
function isObject3(subject) {
  return !isNull(subject) && typeOf("object", subject);
}
var isArray2 = Array.isArray;
var isFunction = apply(typeOf, "function");
var isString2 = apply(typeOf, "string");
var isUndefined = apply(typeOf, "undefined");
function isNull(subject) {
  return subject === null;
}
function isHTMLElement(subject) {
  try {
    return subject instanceof (subject.ownerDocument.defaultView || window).HTMLElement;
  } catch (e) {
    return false;
  }
}
function toArray(value) {
  return isArray2(value) ? value : [value];
}
function forEach(values, iteratee) {
  toArray(values).forEach(iteratee);
}
function includes(array, value) {
  return array.indexOf(value) > -1;
}
function push(array, items) {
  array.push.apply(array, toArray(items));
  return array;
}
function toggleClass(elm, classes, add2) {
  if (elm) {
    forEach(classes, function(name) {
      if (name) {
        elm.classList[add2 ? "add" : "remove"](name);
      }
    });
  }
}
function addClass(elm, classes) {
  toggleClass(elm, isString2(classes) ? classes.split(" ") : classes, true);
}
function append(parent, children2) {
  forEach(children2, parent.appendChild.bind(parent));
}
function before(nodes, ref) {
  forEach(nodes, function(node) {
    var parent = (ref || node).parentNode;
    if (parent) {
      parent.insertBefore(node, ref);
    }
  });
}
function matches(elm, selector) {
  return isHTMLElement(elm) && (elm["msMatchesSelector"] || elm.matches).call(elm, selector);
}
function children(parent, selector) {
  var children2 = parent ? slice(parent.children) : [];
  return selector ? children2.filter(function(child2) {
    return matches(child2, selector);
  }) : children2;
}
function child(parent, selector) {
  return selector ? children(parent, selector)[0] : parent.firstElementChild;
}
var ownKeys2 = Object.keys;
function forOwn(object, iteratee, right) {
  if (object) {
    (right ? ownKeys2(object).reverse() : ownKeys2(object)).forEach(function(key) {
      key !== "__proto__" && iteratee(object[key], key);
    });
  }
  return object;
}
function assign(object) {
  slice(arguments, 1).forEach(function(source) {
    forOwn(source, function(value, key) {
      object[key] = source[key];
    });
  });
  return object;
}
function merge(object) {
  slice(arguments, 1).forEach(function(source) {
    forOwn(source, function(value, key) {
      if (isArray2(value)) {
        object[key] = value.slice();
      } else if (isObject3(value)) {
        object[key] = merge({}, isObject3(object[key]) ? object[key] : {}, value);
      } else {
        object[key] = value;
      }
    });
  });
  return object;
}
function omit(object, keys) {
  forEach(keys || ownKeys2(object), function(key) {
    delete object[key];
  });
}
function removeAttribute(elms, attrs) {
  forEach(elms, function(elm) {
    forEach(attrs, function(attr) {
      elm && elm.removeAttribute(attr);
    });
  });
}
function setAttribute(elms, attrs, value) {
  if (isObject3(attrs)) {
    forOwn(attrs, function(value2, name) {
      setAttribute(elms, name, value2);
    });
  } else {
    forEach(elms, function(elm) {
      isNull(value) || value === "" ? removeAttribute(elm, attrs) : elm.setAttribute(attrs, String(value));
    });
  }
}
function create(tag, attrs, parent) {
  var elm = document.createElement(tag);
  if (attrs) {
    isString2(attrs) ? addClass(elm, attrs) : setAttribute(elm, attrs);
  }
  parent && append(parent, elm);
  return elm;
}
function style(elm, prop, value) {
  if (isUndefined(value)) {
    return getComputedStyle(elm)[prop];
  }
  if (!isNull(value)) {
    elm.style[prop] = "" + value;
  }
}
function display(elm, display2) {
  style(elm, "display", display2);
}
function focus(elm) {
  elm["setActive"] && elm["setActive"]() || elm.focus({
    preventScroll: true
  });
}
function getAttribute(elm, attr) {
  return elm.getAttribute(attr);
}
function hasClass(elm, className) {
  return elm && elm.classList.contains(className);
}
function rect(target) {
  return target.getBoundingClientRect();
}
function remove(nodes) {
  forEach(nodes, function(node) {
    if (node && node.parentNode) {
      node.parentNode.removeChild(node);
    }
  });
}
function parseHtml(html) {
  return child(new DOMParser().parseFromString(html, "text/html").body);
}
function prevent(e, stopPropagation) {
  e.preventDefault();
  if (stopPropagation) {
    e.stopPropagation();
    e.stopImmediatePropagation();
  }
}
function query(parent, selector) {
  return parent && parent.querySelector(selector);
}
function queryAll(parent, selector) {
  return selector ? slice(parent.querySelectorAll(selector)) : [];
}
function removeClass(elm, classes) {
  toggleClass(elm, classes, false);
}
function timeOf(e) {
  return e.timeStamp;
}
function unit(value) {
  return isString2(value) ? value : value ? value + "px" : "";
}
var PROJECT_CODE = "splide";
var DATA_ATTRIBUTE = "data-" + PROJECT_CODE;
function assert(condition, message) {
  if (!condition) {
    throw new Error("[" + PROJECT_CODE + "] " + (message || ""));
  }
}
var min = Math.min;
var max = Math.max;
var floor = Math.floor;
var ceil = Math.ceil;
var abs = Math.abs;
function approximatelyEqual(x, y, epsilon) {
  return abs(x - y) < epsilon;
}
function between(number, x, y, exclusive) {
  var minimum = min(x, y);
  var maximum = max(x, y);
  return exclusive ? minimum < number && number < maximum : minimum <= number && number <= maximum;
}
function clamp(number, x, y) {
  var minimum = min(x, y);
  var maximum = max(x, y);
  return min(max(minimum, number), maximum);
}
function sign(x) {
  return +(x > 0) - +(x < 0);
}
function format(string, replacements) {
  forEach(replacements, function(replacement) {
    string = string.replace("%s", "" + replacement);
  });
  return string;
}
function pad(number) {
  return number < 10 ? "0" + number : "" + number;
}
var ids = {};
function uniqueId(prefix2) {
  return "" + prefix2 + pad(ids[prefix2] = (ids[prefix2] || 0) + 1);
}
function EventBinder() {
  var listeners = [];
  function bind3(targets, events, callback, options) {
    forEachEvent(targets, events, function(target, event, namespace) {
      var isEventTarget = "addEventListener" in target;
      var remover = isEventTarget ? target.removeEventListener.bind(target, event, callback, options) : target["removeListener"].bind(target, callback);
      isEventTarget ? target.addEventListener(event, callback, options) : target["addListener"](callback);
      listeners.push([target, event, namespace, callback, remover]);
    });
  }
  function unbind(targets, events, callback) {
    forEachEvent(targets, events, function(target, event, namespace) {
      listeners = listeners.filter(function(listener) {
        if (listener[0] === target && listener[1] === event && listener[2] === namespace && (!callback || listener[3] === callback)) {
          listener[4]();
          return false;
        }
        return true;
      });
    });
  }
  function dispatch2(target, type, detail) {
    var e;
    var bubbles = true;
    if (typeof CustomEvent === "function") {
      e = new CustomEvent(type, {
        bubbles,
        detail
      });
    } else {
      e = document.createEvent("CustomEvent");
      e.initCustomEvent(type, bubbles, false, detail);
    }
    target.dispatchEvent(e);
    return e;
  }
  function forEachEvent(targets, events, iteratee) {
    forEach(targets, function(target) {
      target && forEach(events, function(events2) {
        events2.split(" ").forEach(function(eventNS) {
          var fragment = eventNS.split(".");
          iteratee(target, fragment[0], fragment[1]);
        });
      });
    });
  }
  function destroy() {
    listeners.forEach(function(data2) {
      data2[4]();
    });
    empty(listeners);
  }
  return {
    bind: bind3,
    unbind,
    dispatch: dispatch2,
    destroy
  };
}
var EVENT_MOUNTED = "mounted";
var EVENT_READY = "ready";
var EVENT_MOVE = "move";
var EVENT_MOVED = "moved";
var EVENT_CLICK = "click";
var EVENT_ACTIVE = "active";
var EVENT_INACTIVE = "inactive";
var EVENT_VISIBLE = "visible";
var EVENT_HIDDEN = "hidden";
var EVENT_REFRESH = "refresh";
var EVENT_UPDATED = "updated";
var EVENT_RESIZE = "resize";
var EVENT_RESIZED = "resized";
var EVENT_DRAG = "drag";
var EVENT_DRAGGING = "dragging";
var EVENT_DRAGGED = "dragged";
var EVENT_SCROLL = "scroll";
var EVENT_SCROLLED = "scrolled";
var EVENT_OVERFLOW = "overflow";
var EVENT_DESTROY = "destroy";
var EVENT_ARROWS_MOUNTED = "arrows:mounted";
var EVENT_ARROWS_UPDATED = "arrows:updated";
var EVENT_PAGINATION_MOUNTED = "pagination:mounted";
var EVENT_PAGINATION_UPDATED = "pagination:updated";
var EVENT_NAVIGATION_MOUNTED = "navigation:mounted";
var EVENT_AUTOPLAY_PLAY = "autoplay:play";
var EVENT_AUTOPLAY_PLAYING = "autoplay:playing";
var EVENT_AUTOPLAY_PAUSE = "autoplay:pause";
var EVENT_LAZYLOAD_LOADED = "lazyload:loaded";
var EVENT_SLIDE_KEYDOWN = "sk";
var EVENT_SHIFTED = "sh";
var EVENT_END_INDEX_CHANGED = "ei";
function EventInterface(Splide2) {
  var bus = Splide2 ? Splide2.event.bus : document.createDocumentFragment();
  var binder = EventBinder();
  function on2(events, callback) {
    binder.bind(bus, toArray(events).join(" "), function(e) {
      callback.apply(callback, isArray2(e.detail) ? e.detail : []);
    });
  }
  function emit(event) {
    binder.dispatch(bus, event, slice(arguments, 1));
  }
  if (Splide2) {
    Splide2.event.on(EVENT_DESTROY, binder.destroy);
  }
  return assign(binder, {
    bus,
    on: on2,
    off: apply(binder.unbind, bus),
    emit
  });
}
function RequestInterval(interval, onInterval, onUpdate, limit) {
  var now = Date.now;
  var startTime;
  var rate = 0;
  var id;
  var paused = true;
  var count = 0;
  function update() {
    if (!paused) {
      rate = interval ? min((now() - startTime) / interval, 1) : 1;
      onUpdate && onUpdate(rate);
      if (rate >= 1) {
        onInterval();
        startTime = now();
        if (limit && ++count >= limit) {
          return pause();
        }
      }
      id = raf(update);
    }
  }
  function start2(resume) {
    resume || cancel();
    startTime = now() - (resume ? rate * interval : 0);
    paused = false;
    id = raf(update);
  }
  function pause() {
    paused = true;
  }
  function rewind() {
    startTime = now();
    rate = 0;
    if (onUpdate) {
      onUpdate(rate);
    }
  }
  function cancel() {
    id && cancelAnimationFrame(id);
    rate = 0;
    id = 0;
    paused = true;
  }
  function set3(time) {
    interval = time;
  }
  function isPaused() {
    return paused;
  }
  return {
    start: start2,
    rewind,
    pause,
    cancel,
    set: set3,
    isPaused
  };
}
function State(initialState) {
  var state = initialState;
  function set3(value) {
    state = value;
  }
  function is(states) {
    return includes(toArray(states), state);
  }
  return {
    set: set3,
    is
  };
}
function Throttle(func, duration) {
  var interval = RequestInterval(duration || 0, func, null, 1);
  return function() {
    interval.isPaused() && interval.start();
  };
}
function Media(Splide2, Components2, options) {
  var state = Splide2.state;
  var breakpoints = options.breakpoints || {};
  var reducedMotion = options.reducedMotion || {};
  var binder = EventBinder();
  var queries = [];
  function setup() {
    var isMin = options.mediaQuery === "min";
    ownKeys2(breakpoints).sort(function(n, m) {
      return isMin ? +n - +m : +m - +n;
    }).forEach(function(key) {
      register(breakpoints[key], "(" + (isMin ? "min" : "max") + "-width:" + key + "px)");
    });
    register(reducedMotion, MEDIA_PREFERS_REDUCED_MOTION);
    update();
  }
  function destroy(completely) {
    if (completely) {
      binder.destroy();
    }
  }
  function register(options2, query2) {
    var queryList = matchMedia(query2);
    binder.bind(queryList, "change", update);
    queries.push([options2, queryList]);
  }
  function update() {
    var destroyed = state.is(DESTROYED);
    var direction = options.direction;
    var merged = queries.reduce(function(merged2, entry) {
      return merge(merged2, entry[1].matches ? entry[0] : {});
    }, {});
    omit(options);
    set3(merged);
    if (options.destroy) {
      Splide2.destroy(options.destroy === "completely");
    } else if (destroyed) {
      destroy(true);
      Splide2.mount();
    } else {
      direction !== options.direction && Splide2.refresh();
    }
  }
  function reduce(enable) {
    if (matchMedia(MEDIA_PREFERS_REDUCED_MOTION).matches) {
      enable ? merge(options, reducedMotion) : omit(options, ownKeys2(reducedMotion));
    }
  }
  function set3(opts, base, notify) {
    merge(options, opts);
    base && merge(Object.getPrototypeOf(options), opts);
    if (notify || !state.is(CREATED)) {
      Splide2.emit(EVENT_UPDATED, options);
    }
  }
  return {
    setup,
    destroy,
    reduce,
    set: set3
  };
}
var ARROW = "Arrow";
var ARROW_LEFT = ARROW + "Left";
var ARROW_RIGHT = ARROW + "Right";
var ARROW_UP = ARROW + "Up";
var ARROW_DOWN = ARROW + "Down";
var RTL = "rtl";
var TTB = "ttb";
var ORIENTATION_MAP = {
  width: ["height"],
  left: ["top", "right"],
  right: ["bottom", "left"],
  x: ["y"],
  X: ["Y"],
  Y: ["X"],
  ArrowLeft: [ARROW_UP, ARROW_RIGHT],
  ArrowRight: [ARROW_DOWN, ARROW_LEFT]
};
function Direction(Splide2, Components2, options) {
  function resolve(prop, axisOnly, direction) {
    direction = direction || options.direction;
    var index = direction === RTL && !axisOnly ? 1 : direction === TTB ? 0 : -1;
    return ORIENTATION_MAP[prop] && ORIENTATION_MAP[prop][index] || prop.replace(/width|left|right/i, function(match, offset) {
      var replacement = ORIENTATION_MAP[match.toLowerCase()][index] || match;
      return offset > 0 ? replacement.charAt(0).toUpperCase() + replacement.slice(1) : replacement;
    });
  }
  function orient(value) {
    return value * (options.direction === RTL ? 1 : -1);
  }
  return {
    resolve,
    orient
  };
}
var ROLE = "role";
var TAB_INDEX = "tabindex";
var DISABLED = "disabled";
var ARIA_PREFIX = "aria-";
var ARIA_CONTROLS = ARIA_PREFIX + "controls";
var ARIA_CURRENT = ARIA_PREFIX + "current";
var ARIA_SELECTED = ARIA_PREFIX + "selected";
var ARIA_LABEL = ARIA_PREFIX + "label";
var ARIA_LABELLEDBY = ARIA_PREFIX + "labelledby";
var ARIA_HIDDEN = ARIA_PREFIX + "hidden";
var ARIA_ORIENTATION = ARIA_PREFIX + "orientation";
var ARIA_ROLEDESCRIPTION = ARIA_PREFIX + "roledescription";
var ARIA_LIVE = ARIA_PREFIX + "live";
var ARIA_BUSY = ARIA_PREFIX + "busy";
var ARIA_ATOMIC = ARIA_PREFIX + "atomic";
var ALL_ATTRIBUTES = [ROLE, TAB_INDEX, DISABLED, ARIA_CONTROLS, ARIA_CURRENT, ARIA_LABEL, ARIA_LABELLEDBY, ARIA_HIDDEN, ARIA_ORIENTATION, ARIA_ROLEDESCRIPTION];
var CLASS_PREFIX = PROJECT_CODE + "__";
var STATUS_CLASS_PREFIX = "is-";
var CLASS_ROOT = PROJECT_CODE;
var CLASS_TRACK = CLASS_PREFIX + "track";
var CLASS_LIST = CLASS_PREFIX + "list";
var CLASS_SLIDE = CLASS_PREFIX + "slide";
var CLASS_CLONE = CLASS_SLIDE + "--clone";
var CLASS_CONTAINER = CLASS_SLIDE + "__container";
var CLASS_ARROWS = CLASS_PREFIX + "arrows";
var CLASS_ARROW = CLASS_PREFIX + "arrow";
var CLASS_ARROW_PREV = CLASS_ARROW + "--prev";
var CLASS_ARROW_NEXT = CLASS_ARROW + "--next";
var CLASS_PAGINATION = CLASS_PREFIX + "pagination";
var CLASS_PAGINATION_PAGE = CLASS_PAGINATION + "__page";
var CLASS_PROGRESS = CLASS_PREFIX + "progress";
var CLASS_PROGRESS_BAR = CLASS_PROGRESS + "__bar";
var CLASS_TOGGLE = CLASS_PREFIX + "toggle";
var CLASS_TOGGLE_PLAY = CLASS_TOGGLE + "__play";
var CLASS_TOGGLE_PAUSE = CLASS_TOGGLE + "__pause";
var CLASS_SPINNER = CLASS_PREFIX + "spinner";
var CLASS_SR = CLASS_PREFIX + "sr";
var CLASS_INITIALIZED = STATUS_CLASS_PREFIX + "initialized";
var CLASS_ACTIVE = STATUS_CLASS_PREFIX + "active";
var CLASS_PREV = STATUS_CLASS_PREFIX + "prev";
var CLASS_NEXT = STATUS_CLASS_PREFIX + "next";
var CLASS_VISIBLE = STATUS_CLASS_PREFIX + "visible";
var CLASS_LOADING = STATUS_CLASS_PREFIX + "loading";
var CLASS_FOCUS_IN = STATUS_CLASS_PREFIX + "focus-in";
var CLASS_OVERFLOW = STATUS_CLASS_PREFIX + "overflow";
var STATUS_CLASSES = [CLASS_ACTIVE, CLASS_VISIBLE, CLASS_PREV, CLASS_NEXT, CLASS_LOADING, CLASS_FOCUS_IN, CLASS_OVERFLOW];
var CLASSES = {
  slide: CLASS_SLIDE,
  clone: CLASS_CLONE,
  arrows: CLASS_ARROWS,
  arrow: CLASS_ARROW,
  prev: CLASS_ARROW_PREV,
  next: CLASS_ARROW_NEXT,
  pagination: CLASS_PAGINATION,
  page: CLASS_PAGINATION_PAGE,
  spinner: CLASS_SPINNER
};
function closest(from, selector) {
  if (isFunction(from.closest)) {
    return from.closest(selector);
  }
  var elm = from;
  while (elm && elm.nodeType === 1) {
    if (matches(elm, selector)) {
      break;
    }
    elm = elm.parentElement;
  }
  return elm;
}
var FRICTION = 5;
var LOG_INTERVAL = 200;
var POINTER_DOWN_EVENTS = "touchstart mousedown";
var POINTER_MOVE_EVENTS = "touchmove mousemove";
var POINTER_UP_EVENTS = "touchend touchcancel mouseup click";
function Elements(Splide2, Components2, options) {
  var _EventInterface = EventInterface(Splide2), on2 = _EventInterface.on, bind3 = _EventInterface.bind;
  var root = Splide2.root;
  var i18n = options.i18n;
  var elements = {};
  var slides = [];
  var rootClasses = [];
  var trackClasses = [];
  var track2;
  var list;
  var isUsingKey;
  function setup() {
    collect();
    init();
    update();
  }
  function mount() {
    on2(EVENT_REFRESH, destroy);
    on2(EVENT_REFRESH, setup);
    on2(EVENT_UPDATED, update);
    bind3(document, POINTER_DOWN_EVENTS + " keydown", function(e) {
      isUsingKey = e.type === "keydown";
    }, {
      capture: true
    });
    bind3(root, "focusin", function() {
      toggleClass(root, CLASS_FOCUS_IN, !!isUsingKey);
    });
  }
  function destroy(completely) {
    var attrs = ALL_ATTRIBUTES.concat("style");
    empty(slides);
    removeClass(root, rootClasses);
    removeClass(track2, trackClasses);
    removeAttribute([track2, list], attrs);
    removeAttribute(root, completely ? attrs : ["style", ARIA_ROLEDESCRIPTION]);
  }
  function update() {
    removeClass(root, rootClasses);
    removeClass(track2, trackClasses);
    rootClasses = getClasses(CLASS_ROOT);
    trackClasses = getClasses(CLASS_TRACK);
    addClass(root, rootClasses);
    addClass(track2, trackClasses);
    setAttribute(root, ARIA_LABEL, options.label);
    setAttribute(root, ARIA_LABELLEDBY, options.labelledby);
  }
  function collect() {
    track2 = find("." + CLASS_TRACK);
    list = child(track2, "." + CLASS_LIST);
    assert(track2 && list, "A track/list element is missing.");
    push(slides, children(list, "." + CLASS_SLIDE + ":not(." + CLASS_CLONE + ")"));
    forOwn({
      arrows: CLASS_ARROWS,
      pagination: CLASS_PAGINATION,
      prev: CLASS_ARROW_PREV,
      next: CLASS_ARROW_NEXT,
      bar: CLASS_PROGRESS_BAR,
      toggle: CLASS_TOGGLE
    }, function(className, key) {
      elements[key] = find("." + className);
    });
    assign(elements, {
      root,
      track: track2,
      list,
      slides
    });
  }
  function init() {
    var id = root.id || uniqueId(PROJECT_CODE);
    var role = options.role;
    root.id = id;
    track2.id = track2.id || id + "-track";
    list.id = list.id || id + "-list";
    if (!getAttribute(root, ROLE) && root.tagName !== "SECTION" && role) {
      setAttribute(root, ROLE, role);
    }
    setAttribute(root, ARIA_ROLEDESCRIPTION, i18n.carousel);
    setAttribute(list, ROLE, "presentation");
  }
  function find(selector) {
    var elm = query(root, selector);
    return elm && closest(elm, "." + CLASS_ROOT) === root ? elm : void 0;
  }
  function getClasses(base) {
    return [base + "--" + options.type, base + "--" + options.direction, options.drag && base + "--draggable", options.isNavigation && base + "--nav", base === CLASS_ROOT && CLASS_ACTIVE];
  }
  return assign(elements, {
    setup,
    mount,
    destroy
  });
}
var SLIDE = "slide";
var LOOP = "loop";
var FADE = "fade";
function Slide$1(Splide2, index, slideIndex, slide) {
  var event = EventInterface(Splide2);
  var on2 = event.on, emit = event.emit, bind3 = event.bind;
  var Components = Splide2.Components, root = Splide2.root, options = Splide2.options;
  var isNavigation = options.isNavigation, updateOnMove = options.updateOnMove, i18n = options.i18n, pagination = options.pagination, slideFocus = options.slideFocus;
  var resolve = Components.Direction.resolve;
  var styles = getAttribute(slide, "style");
  var label = getAttribute(slide, ARIA_LABEL);
  var isClone = slideIndex > -1;
  var container = child(slide, "." + CLASS_CONTAINER);
  var destroyed;
  function mount() {
    if (!isClone) {
      slide.id = root.id + "-slide" + pad(index + 1);
      setAttribute(slide, ROLE, pagination ? "tabpanel" : "group");
      setAttribute(slide, ARIA_ROLEDESCRIPTION, i18n.slide);
      setAttribute(slide, ARIA_LABEL, label || format(i18n.slideLabel, [index + 1, Splide2.length]));
    }
    listen();
  }
  function listen() {
    bind3(slide, "click", apply(emit, EVENT_CLICK, self));
    bind3(slide, "keydown", apply(emit, EVENT_SLIDE_KEYDOWN, self));
    on2([EVENT_MOVED, EVENT_SHIFTED, EVENT_SCROLLED], update);
    on2(EVENT_NAVIGATION_MOUNTED, initNavigation);
    if (updateOnMove) {
      on2(EVENT_MOVE, onMove);
    }
  }
  function destroy() {
    destroyed = true;
    event.destroy();
    removeClass(slide, STATUS_CLASSES);
    removeAttribute(slide, ALL_ATTRIBUTES);
    setAttribute(slide, "style", styles);
    setAttribute(slide, ARIA_LABEL, label || "");
  }
  function initNavigation() {
    var controls = Splide2.splides.map(function(target) {
      var Slide2 = target.splide.Components.Slides.getAt(index);
      return Slide2 ? Slide2.slide.id : "";
    }).join(" ");
    setAttribute(slide, ARIA_LABEL, format(i18n.slideX, (isClone ? slideIndex : index) + 1));
    setAttribute(slide, ARIA_CONTROLS, controls);
    setAttribute(slide, ROLE, slideFocus ? "button" : "");
    slideFocus && removeAttribute(slide, ARIA_ROLEDESCRIPTION);
  }
  function onMove() {
    if (!destroyed) {
      update();
    }
  }
  function update() {
    if (!destroyed) {
      var curr = Splide2.index;
      updateActivity();
      updateVisibility();
      toggleClass(slide, CLASS_PREV, index === curr - 1);
      toggleClass(slide, CLASS_NEXT, index === curr + 1);
    }
  }
  function updateActivity() {
    var active = isActive();
    if (active !== hasClass(slide, CLASS_ACTIVE)) {
      toggleClass(slide, CLASS_ACTIVE, active);
      setAttribute(slide, ARIA_CURRENT, isNavigation && active || "");
      emit(active ? EVENT_ACTIVE : EVENT_INACTIVE, self);
    }
  }
  function updateVisibility() {
    var visible = isVisible();
    var hidden = !visible && (!isActive() || isClone);
    if (!Splide2.state.is([MOVING, SCROLLING])) {
      setAttribute(slide, ARIA_HIDDEN, hidden || "");
    }
    setAttribute(queryAll(slide, options.focusableNodes || ""), TAB_INDEX, hidden ? -1 : "");
    if (slideFocus) {
      setAttribute(slide, TAB_INDEX, hidden ? -1 : 0);
    }
    if (visible !== hasClass(slide, CLASS_VISIBLE)) {
      toggleClass(slide, CLASS_VISIBLE, visible);
      emit(visible ? EVENT_VISIBLE : EVENT_HIDDEN, self);
    }
    if (!visible && document.activeElement === slide) {
      var Slide2 = Components.Slides.getAt(Splide2.index);
      Slide2 && focus(Slide2.slide);
    }
  }
  function style$1(prop, value, useContainer) {
    style(useContainer && container || slide, prop, value);
  }
  function isActive() {
    var curr = Splide2.index;
    return curr === index || options.cloneStatus && curr === slideIndex;
  }
  function isVisible() {
    if (Splide2.is(FADE)) {
      return isActive();
    }
    var trackRect = rect(Components.Elements.track);
    var slideRect = rect(slide);
    var left = resolve("left", true);
    var right = resolve("right", true);
    return floor(trackRect[left]) <= ceil(slideRect[left]) && floor(slideRect[right]) <= ceil(trackRect[right]);
  }
  function isWithin(from, distance) {
    var diff = abs(from - index);
    if (!isClone && (options.rewind || Splide2.is(LOOP))) {
      diff = min(diff, Splide2.length - diff);
    }
    return diff <= distance;
  }
  var self = {
    index,
    slideIndex,
    slide,
    container,
    isClone,
    mount,
    destroy,
    update,
    style: style$1,
    isWithin
  };
  return self;
}
function Slides(Splide2, Components2, options) {
  var _EventInterface2 = EventInterface(Splide2), on2 = _EventInterface2.on, emit = _EventInterface2.emit, bind3 = _EventInterface2.bind;
  var _Components2$Elements = Components2.Elements, slides = _Components2$Elements.slides, list = _Components2$Elements.list;
  var Slides2 = [];
  function mount() {
    init();
    on2(EVENT_REFRESH, destroy);
    on2(EVENT_REFRESH, init);
  }
  function init() {
    slides.forEach(function(slide, index) {
      register(slide, index, -1);
    });
  }
  function destroy() {
    forEach$12(function(Slide2) {
      Slide2.destroy();
    });
    empty(Slides2);
  }
  function update() {
    forEach$12(function(Slide2) {
      Slide2.update();
    });
  }
  function register(slide, index, slideIndex) {
    var object = Slide$1(Splide2, index, slideIndex, slide);
    object.mount();
    Slides2.push(object);
    Slides2.sort(function(Slide1, Slide2) {
      return Slide1.index - Slide2.index;
    });
  }
  function get3(excludeClones) {
    return excludeClones ? filter(function(Slide2) {
      return !Slide2.isClone;
    }) : Slides2;
  }
  function getIn(page) {
    var Controller2 = Components2.Controller;
    var index = Controller2.toIndex(page);
    var max3 = Controller2.hasFocus() ? 1 : options.perPage;
    return filter(function(Slide2) {
      return between(Slide2.index, index, index + max3 - 1);
    });
  }
  function getAt(index) {
    return filter(index)[0];
  }
  function add2(items, index) {
    forEach(items, function(slide) {
      if (isString2(slide)) {
        slide = parseHtml(slide);
      }
      if (isHTMLElement(slide)) {
        var ref = slides[index];
        ref ? before(slide, ref) : append(list, slide);
        addClass(slide, options.classes.slide);
        observeImages(slide, apply(emit, EVENT_RESIZE));
      }
    });
    emit(EVENT_REFRESH);
  }
  function remove$1(matcher) {
    remove(filter(matcher).map(function(Slide2) {
      return Slide2.slide;
    }));
    emit(EVENT_REFRESH);
  }
  function forEach$12(iteratee, excludeClones) {
    get3(excludeClones).forEach(iteratee);
  }
  function filter(matcher) {
    return Slides2.filter(isFunction(matcher) ? matcher : function(Slide2) {
      return isString2(matcher) ? matches(Slide2.slide, matcher) : includes(toArray(matcher), Slide2.index);
    });
  }
  function style2(prop, value, useContainer) {
    forEach$12(function(Slide2) {
      Slide2.style(prop, value, useContainer);
    });
  }
  function observeImages(elm, callback) {
    var images = queryAll(elm, "img");
    var length = images.length;
    if (length) {
      images.forEach(function(img) {
        bind3(img, "load error", function() {
          if (!--length) {
            callback();
          }
        });
      });
    } else {
      callback();
    }
  }
  function getLength(excludeClones) {
    return excludeClones ? slides.length : Slides2.length;
  }
  function isEnough() {
    return Slides2.length > options.perPage;
  }
  return {
    mount,
    destroy,
    update,
    register,
    get: get3,
    getIn,
    getAt,
    add: add2,
    remove: remove$1,
    forEach: forEach$12,
    filter,
    style: style2,
    getLength,
    isEnough
  };
}
function Layout(Splide2, Components2, options) {
  var _EventInterface3 = EventInterface(Splide2), on2 = _EventInterface3.on, bind3 = _EventInterface3.bind, emit = _EventInterface3.emit;
  var Slides2 = Components2.Slides;
  var resolve = Components2.Direction.resolve;
  var _Components2$Elements2 = Components2.Elements, root = _Components2$Elements2.root, track2 = _Components2$Elements2.track, list = _Components2$Elements2.list;
  var getAt = Slides2.getAt, styleSlides = Slides2.style;
  var vertical;
  var rootRect;
  var overflow;
  function mount() {
    init();
    bind3(window, "resize load", Throttle(apply(emit, EVENT_RESIZE)));
    on2([EVENT_UPDATED, EVENT_REFRESH], init);
    on2(EVENT_RESIZE, resize);
  }
  function init() {
    vertical = options.direction === TTB;
    style(root, "maxWidth", unit(options.width));
    style(track2, resolve("paddingLeft"), cssPadding(false));
    style(track2, resolve("paddingRight"), cssPadding(true));
    resize(true);
  }
  function resize(force) {
    var newRect = rect(root);
    if (force || rootRect.width !== newRect.width || rootRect.height !== newRect.height) {
      style(track2, "height", cssTrackHeight());
      styleSlides(resolve("marginRight"), unit(options.gap));
      styleSlides("width", cssSlideWidth());
      styleSlides("height", cssSlideHeight(), true);
      rootRect = newRect;
      emit(EVENT_RESIZED);
      if (overflow !== (overflow = isOverflow())) {
        toggleClass(root, CLASS_OVERFLOW, overflow);
        emit(EVENT_OVERFLOW, overflow);
      }
    }
  }
  function cssPadding(right) {
    var padding = options.padding;
    var prop = resolve(right ? "right" : "left");
    return padding && unit(padding[prop] || (isObject3(padding) ? 0 : padding)) || "0px";
  }
  function cssTrackHeight() {
    var height = "";
    if (vertical) {
      height = cssHeight();
      assert(height, "height or heightRatio is missing.");
      height = "calc(" + height + " - " + cssPadding(false) + " - " + cssPadding(true) + ")";
    }
    return height;
  }
  function cssHeight() {
    return unit(options.height || rect(list).width * options.heightRatio);
  }
  function cssSlideWidth() {
    return options.autoWidth ? null : unit(options.fixedWidth) || (vertical ? "" : cssSlideSize());
  }
  function cssSlideHeight() {
    return unit(options.fixedHeight) || (vertical ? options.autoHeight ? null : cssSlideSize() : cssHeight());
  }
  function cssSlideSize() {
    var gap = unit(options.gap);
    return "calc((100%" + (gap && " + " + gap) + ")/" + (options.perPage || 1) + (gap && " - " + gap) + ")";
  }
  function listSize() {
    return rect(list)[resolve("width")];
  }
  function slideSize(index, withoutGap) {
    var Slide2 = getAt(index || 0);
    return Slide2 ? rect(Slide2.slide)[resolve("width")] + (withoutGap ? 0 : getGap()) : 0;
  }
  function totalSize(index, withoutGap) {
    var Slide2 = getAt(index);
    if (Slide2) {
      var right = rect(Slide2.slide)[resolve("right")];
      var left = rect(list)[resolve("left")];
      return abs(right - left) + (withoutGap ? 0 : getGap());
    }
    return 0;
  }
  function sliderSize(withoutGap) {
    return totalSize(Splide2.length - 1) - totalSize(0) + slideSize(0, withoutGap);
  }
  function getGap() {
    var Slide2 = getAt(0);
    return Slide2 && parseFloat(style(Slide2.slide, resolve("marginRight"))) || 0;
  }
  function getPadding(right) {
    return parseFloat(style(track2, resolve("padding" + (right ? "Right" : "Left")))) || 0;
  }
  function isOverflow() {
    return Splide2.is(FADE) || sliderSize(true) > listSize();
  }
  return {
    mount,
    resize,
    listSize,
    slideSize,
    sliderSize,
    totalSize,
    getPadding,
    isOverflow
  };
}
var MULTIPLIER = 2;
function Clones(Splide2, Components2, options) {
  var event = EventInterface(Splide2);
  var on2 = event.on;
  var Elements2 = Components2.Elements, Slides2 = Components2.Slides;
  var resolve = Components2.Direction.resolve;
  var clones = [];
  var cloneCount;
  function mount() {
    on2(EVENT_REFRESH, remount);
    on2([EVENT_UPDATED, EVENT_RESIZE], observe);
    if (cloneCount = computeCloneCount()) {
      generate(cloneCount);
      Components2.Layout.resize(true);
    }
  }
  function remount() {
    destroy();
    mount();
  }
  function destroy() {
    remove(clones);
    empty(clones);
    event.destroy();
  }
  function observe() {
    var count = computeCloneCount();
    if (cloneCount !== count) {
      if (cloneCount < count || !count) {
        event.emit(EVENT_REFRESH);
      }
    }
  }
  function generate(count) {
    var slides = Slides2.get().slice();
    var length = slides.length;
    if (length) {
      while (slides.length < count) {
        push(slides, slides);
      }
      push(slides.slice(-count), slides.slice(0, count)).forEach(function(Slide2, index) {
        var isHead = index < count;
        var clone2 = cloneDeep(Slide2.slide, index);
        isHead ? before(clone2, slides[0].slide) : append(Elements2.list, clone2);
        push(clones, clone2);
        Slides2.register(clone2, index - count + (isHead ? 0 : length), Slide2.index);
      });
    }
  }
  function cloneDeep(elm, index) {
    var clone2 = elm.cloneNode(true);
    addClass(clone2, options.classes.clone);
    clone2.id = Splide2.root.id + "-clone" + pad(index + 1);
    return clone2;
  }
  function computeCloneCount() {
    var clones2 = options.clones;
    if (!Splide2.is(LOOP)) {
      clones2 = 0;
    } else if (isUndefined(clones2)) {
      var fixedSize = options[resolve("fixedWidth")] && Components2.Layout.slideSize(0);
      var fixedCount = fixedSize && ceil(rect(Elements2.track)[resolve("width")] / fixedSize);
      clones2 = fixedCount || options[resolve("autoWidth")] && Splide2.length || options.perPage * MULTIPLIER;
    }
    return clones2;
  }
  return {
    mount,
    destroy
  };
}
function Move(Splide2, Components2, options) {
  var _EventInterface4 = EventInterface(Splide2), on2 = _EventInterface4.on, emit = _EventInterface4.emit;
  var set3 = Splide2.state.set;
  var _Components2$Layout = Components2.Layout, slideSize = _Components2$Layout.slideSize, getPadding = _Components2$Layout.getPadding, totalSize = _Components2$Layout.totalSize, listSize = _Components2$Layout.listSize, sliderSize = _Components2$Layout.sliderSize;
  var _Components2$Directio = Components2.Direction, resolve = _Components2$Directio.resolve, orient = _Components2$Directio.orient;
  var _Components2$Elements3 = Components2.Elements, list = _Components2$Elements3.list, track2 = _Components2$Elements3.track;
  var Transition;
  function mount() {
    Transition = Components2.Transition;
    on2([EVENT_MOUNTED, EVENT_RESIZED, EVENT_UPDATED, EVENT_REFRESH], reposition);
  }
  function reposition() {
    if (!Components2.Controller.isBusy()) {
      Components2.Scroll.cancel();
      jump(Splide2.index);
      Components2.Slides.update();
    }
  }
  function move(dest, index, prev, callback) {
    if (dest !== index && canShift(dest > prev)) {
      cancel();
      translate(shift(getPosition(), dest > prev), true);
    }
    set3(MOVING);
    emit(EVENT_MOVE, index, prev, dest);
    Transition.start(index, function() {
      set3(IDLE);
      emit(EVENT_MOVED, index, prev, dest);
      callback && callback();
    });
  }
  function jump(index) {
    translate(toPosition(index, true));
  }
  function translate(position, preventLoop) {
    if (!Splide2.is(FADE)) {
      var destination = preventLoop ? position : loop2(position);
      style(list, "transform", "translate" + resolve("X") + "(" + destination + "px)");
      position !== destination && emit(EVENT_SHIFTED);
    }
  }
  function loop2(position) {
    if (Splide2.is(LOOP)) {
      var index = toIndex(position);
      var exceededMax = index > Components2.Controller.getEnd();
      var exceededMin = index < 0;
      if (exceededMin || exceededMax) {
        position = shift(position, exceededMax);
      }
    }
    return position;
  }
  function shift(position, backwards) {
    var excess = position - getLimit(backwards);
    var size2 = sliderSize();
    position -= orient(size2 * (ceil(abs(excess) / size2) || 1)) * (backwards ? 1 : -1);
    return position;
  }
  function cancel() {
    translate(getPosition(), true);
    Transition.cancel();
  }
  function toIndex(position) {
    var Slides2 = Components2.Slides.get();
    var index = 0;
    var minDistance = Infinity;
    for (var i = 0; i < Slides2.length; i++) {
      var slideIndex = Slides2[i].index;
      var distance = abs(toPosition(slideIndex, true) - position);
      if (distance <= minDistance) {
        minDistance = distance;
        index = slideIndex;
      } else {
        break;
      }
    }
    return index;
  }
  function toPosition(index, trimming) {
    var position = orient(totalSize(index - 1) - offset(index));
    return trimming ? trim(position) : position;
  }
  function getPosition() {
    var left = resolve("left");
    return rect(list)[left] - rect(track2)[left] + orient(getPadding(false));
  }
  function trim(position) {
    if (options.trimSpace && Splide2.is(SLIDE)) {
      position = clamp(position, 0, orient(sliderSize(true) - listSize()));
    }
    return position;
  }
  function offset(index) {
    var focus2 = options.focus;
    return focus2 === "center" ? (listSize() - slideSize(index, true)) / 2 : +focus2 * slideSize(index) || 0;
  }
  function getLimit(max3) {
    return toPosition(max3 ? Components2.Controller.getEnd() : 0, !!options.trimSpace);
  }
  function canShift(backwards) {
    var shifted = orient(shift(getPosition(), backwards));
    return backwards ? shifted >= 0 : shifted <= list[resolve("scrollWidth")] - rect(track2)[resolve("width")];
  }
  function exceededLimit(max3, position) {
    position = isUndefined(position) ? getPosition() : position;
    var exceededMin = max3 !== true && orient(position) < orient(getLimit(false));
    var exceededMax = max3 !== false && orient(position) > orient(getLimit(true));
    return exceededMin || exceededMax;
  }
  return {
    mount,
    move,
    jump,
    translate,
    shift,
    cancel,
    toIndex,
    toPosition,
    getPosition,
    getLimit,
    exceededLimit,
    reposition
  };
}
function Controller(Splide2, Components2, options) {
  var _EventInterface5 = EventInterface(Splide2), on2 = _EventInterface5.on, emit = _EventInterface5.emit;
  var Move2 = Components2.Move;
  var getPosition = Move2.getPosition, getLimit = Move2.getLimit, toPosition = Move2.toPosition;
  var _Components2$Slides = Components2.Slides, isEnough = _Components2$Slides.isEnough, getLength = _Components2$Slides.getLength;
  var omitEnd = options.omitEnd;
  var isLoop = Splide2.is(LOOP);
  var isSlide = Splide2.is(SLIDE);
  var getNext = apply(getAdjacent, false);
  var getPrev = apply(getAdjacent, true);
  var currIndex = options.start || 0;
  var endIndex;
  var prevIndex = currIndex;
  var slideCount;
  var perMove;
  var perPage;
  function mount() {
    init();
    on2([EVENT_UPDATED, EVENT_REFRESH, EVENT_END_INDEX_CHANGED], init);
    on2(EVENT_RESIZED, onResized);
  }
  function init() {
    slideCount = getLength(true);
    perMove = options.perMove;
    perPage = options.perPage;
    endIndex = getEnd();
    var index = clamp(currIndex, 0, omitEnd ? endIndex : slideCount - 1);
    if (index !== currIndex) {
      currIndex = index;
      Move2.reposition();
    }
  }
  function onResized() {
    if (endIndex !== getEnd()) {
      emit(EVENT_END_INDEX_CHANGED);
    }
  }
  function go(control, allowSameIndex, callback) {
    if (!isBusy()) {
      var dest = parse(control);
      var index = loop2(dest);
      if (index > -1 && (allowSameIndex || index !== currIndex)) {
        setIndex(index);
        Move2.move(dest, index, prevIndex, callback);
      }
    }
  }
  function scroll(destination, duration, snap, callback) {
    Components2.Scroll.scroll(destination, duration, snap, function() {
      var index = loop2(Move2.toIndex(getPosition()));
      setIndex(omitEnd ? min(index, endIndex) : index);
      callback && callback();
    });
  }
  function parse(control) {
    var index = currIndex;
    if (isString2(control)) {
      var _ref = control.match(/([+\-<>])(\d+)?/) || [], indicator = _ref[1], number = _ref[2];
      if (indicator === "+" || indicator === "-") {
        index = computeDestIndex(currIndex + +("" + indicator + (+number || 1)), currIndex);
      } else if (indicator === ">") {
        index = number ? toIndex(+number) : getNext(true);
      } else if (indicator === "<") {
        index = getPrev(true);
      }
    } else {
      index = isLoop ? control : clamp(control, 0, endIndex);
    }
    return index;
  }
  function getAdjacent(prev, destination) {
    var number = perMove || (hasFocus() ? 1 : perPage);
    var dest = computeDestIndex(currIndex + number * (prev ? -1 : 1), currIndex, !(perMove || hasFocus()));
    if (dest === -1 && isSlide) {
      if (!approximatelyEqual(getPosition(), getLimit(!prev), 1)) {
        return prev ? 0 : endIndex;
      }
    }
    return destination ? dest : loop2(dest);
  }
  function computeDestIndex(dest, from, snapPage) {
    if (isEnough() || hasFocus()) {
      var index = computeMovableDestIndex(dest);
      if (index !== dest) {
        from = dest;
        dest = index;
        snapPage = false;
      }
      if (dest < 0 || dest > endIndex) {
        if (!perMove && (between(0, dest, from, true) || between(endIndex, from, dest, true))) {
          dest = toIndex(toPage(dest));
        } else {
          if (isLoop) {
            dest = snapPage ? dest < 0 ? -(slideCount % perPage || perPage) : slideCount : dest;
          } else if (options.rewind) {
            dest = dest < 0 ? endIndex : 0;
          } else {
            dest = -1;
          }
        }
      } else {
        if (snapPage && dest !== from) {
          dest = toIndex(toPage(from) + (dest < from ? -1 : 1));
        }
      }
    } else {
      dest = -1;
    }
    return dest;
  }
  function computeMovableDestIndex(dest) {
    if (isSlide && options.trimSpace === "move" && dest !== currIndex) {
      var position = getPosition();
      while (position === toPosition(dest, true) && between(dest, 0, Splide2.length - 1, !options.rewind)) {
        dest < currIndex ? --dest : ++dest;
      }
    }
    return dest;
  }
  function loop2(index) {
    return isLoop ? (index + slideCount) % slideCount || 0 : index;
  }
  function getEnd() {
    var end = slideCount - (hasFocus() || isLoop && perMove ? 1 : perPage);
    while (omitEnd && end-- > 0) {
      if (toPosition(slideCount - 1, true) !== toPosition(end, true)) {
        end++;
        break;
      }
    }
    return clamp(end, 0, slideCount - 1);
  }
  function toIndex(page) {
    return clamp(hasFocus() ? page : perPage * page, 0, endIndex);
  }
  function toPage(index) {
    return hasFocus() ? min(index, endIndex) : floor((index >= endIndex ? slideCount - 1 : index) / perPage);
  }
  function toDest(destination) {
    var closest2 = Move2.toIndex(destination);
    return isSlide ? clamp(closest2, 0, endIndex) : closest2;
  }
  function setIndex(index) {
    if (index !== currIndex) {
      prevIndex = currIndex;
      currIndex = index;
    }
  }
  function getIndex(prev) {
    return prev ? prevIndex : currIndex;
  }
  function hasFocus() {
    return !isUndefined(options.focus) || options.isNavigation;
  }
  function isBusy() {
    return Splide2.state.is([MOVING, SCROLLING]) && !!options.waitForTransition;
  }
  return {
    mount,
    go,
    scroll,
    getNext,
    getPrev,
    getAdjacent,
    getEnd,
    setIndex,
    getIndex,
    toIndex,
    toPage,
    toDest,
    hasFocus,
    isBusy
  };
}
var XML_NAME_SPACE = "http://www.w3.org/2000/svg";
var PATH = "m15.5 0.932-4.3 4.38 14.5 14.6-14.5 14.5 4.3 4.4 14.6-14.6 4.4-4.3-4.4-4.4-14.6-14.6z";
var SIZE = 40;
function Arrows(Splide2, Components2, options) {
  var event = EventInterface(Splide2);
  var on2 = event.on, bind3 = event.bind, emit = event.emit;
  var classes = options.classes, i18n = options.i18n;
  var Elements2 = Components2.Elements, Controller2 = Components2.Controller;
  var placeholder = Elements2.arrows, track2 = Elements2.track;
  var wrapper = placeholder;
  var prev = Elements2.prev;
  var next = Elements2.next;
  var created;
  var wrapperClasses;
  var arrows = {};
  function mount() {
    init();
    on2(EVENT_UPDATED, remount);
  }
  function remount() {
    destroy();
    mount();
  }
  function init() {
    var enabled = options.arrows;
    if (enabled && !(prev && next)) {
      createArrows();
    }
    if (prev && next) {
      assign(arrows, {
        prev,
        next
      });
      display(wrapper, enabled ? "" : "none");
      addClass(wrapper, wrapperClasses = CLASS_ARROWS + "--" + options.direction);
      if (enabled) {
        listen();
        update();
        setAttribute([prev, next], ARIA_CONTROLS, track2.id);
        emit(EVENT_ARROWS_MOUNTED, prev, next);
      }
    }
  }
  function destroy() {
    event.destroy();
    removeClass(wrapper, wrapperClasses);
    if (created) {
      remove(placeholder ? [prev, next] : wrapper);
      prev = next = null;
    } else {
      removeAttribute([prev, next], ALL_ATTRIBUTES);
    }
  }
  function listen() {
    on2([EVENT_MOUNTED, EVENT_MOVED, EVENT_REFRESH, EVENT_SCROLLED, EVENT_END_INDEX_CHANGED], update);
    bind3(next, "click", apply(go, ">"));
    bind3(prev, "click", apply(go, "<"));
  }
  function go(control) {
    Controller2.go(control, true);
  }
  function createArrows() {
    wrapper = placeholder || create("div", classes.arrows);
    prev = createArrow(true);
    next = createArrow(false);
    created = true;
    append(wrapper, [prev, next]);
    !placeholder && before(wrapper, track2);
  }
  function createArrow(prev2) {
    var arrow = '<button class="' + classes.arrow + " " + (prev2 ? classes.prev : classes.next) + '" type="button"><svg xmlns="' + XML_NAME_SPACE + '" viewBox="0 0 ' + SIZE + " " + SIZE + '" width="' + SIZE + '" height="' + SIZE + '" focusable="false"><path d="' + (options.arrowPath || PATH) + '" />';
    return parseHtml(arrow);
  }
  function update() {
    if (prev && next) {
      var index = Splide2.index;
      var prevIndex = Controller2.getPrev();
      var nextIndex = Controller2.getNext();
      var prevLabel = prevIndex > -1 && index < prevIndex ? i18n.last : i18n.prev;
      var nextLabel = nextIndex > -1 && index > nextIndex ? i18n.first : i18n.next;
      prev.disabled = prevIndex < 0;
      next.disabled = nextIndex < 0;
      setAttribute(prev, ARIA_LABEL, prevLabel);
      setAttribute(next, ARIA_LABEL, nextLabel);
      emit(EVENT_ARROWS_UPDATED, prev, next, prevIndex, nextIndex);
    }
  }
  return {
    arrows,
    mount,
    destroy,
    update
  };
}
var INTERVAL_DATA_ATTRIBUTE = DATA_ATTRIBUTE + "-interval";
function Autoplay(Splide2, Components2, options) {
  var _EventInterface6 = EventInterface(Splide2), on2 = _EventInterface6.on, bind3 = _EventInterface6.bind, emit = _EventInterface6.emit;
  var interval = RequestInterval(options.interval, Splide2.go.bind(Splide2, ">"), onAnimationFrame);
  var isPaused = interval.isPaused;
  var Elements2 = Components2.Elements, _Components2$Elements4 = Components2.Elements, root = _Components2$Elements4.root, toggle = _Components2$Elements4.toggle;
  var autoplay = options.autoplay;
  var hovered;
  var focused;
  var stopped = autoplay === "pause";
  function mount() {
    if (autoplay) {
      listen();
      toggle && setAttribute(toggle, ARIA_CONTROLS, Elements2.track.id);
      stopped || play();
      update();
    }
  }
  function listen() {
    if (options.pauseOnHover) {
      bind3(root, "mouseenter mouseleave", function(e) {
        hovered = e.type === "mouseenter";
        autoToggle();
      });
    }
    if (options.pauseOnFocus) {
      bind3(root, "focusin focusout", function(e) {
        focused = e.type === "focusin";
        autoToggle();
      });
    }
    if (toggle) {
      bind3(toggle, "click", function() {
        stopped ? play() : pause(true);
      });
    }
    on2([EVENT_MOVE, EVENT_SCROLL, EVENT_REFRESH], interval.rewind);
    on2(EVENT_MOVE, onMove);
  }
  function play() {
    if (isPaused() && Components2.Slides.isEnough()) {
      interval.start(!options.resetProgress);
      focused = hovered = stopped = false;
      update();
      emit(EVENT_AUTOPLAY_PLAY);
    }
  }
  function pause(stop2) {
    if (stop2 === void 0) {
      stop2 = true;
    }
    stopped = !!stop2;
    update();
    if (!isPaused()) {
      interval.pause();
      emit(EVENT_AUTOPLAY_PAUSE);
    }
  }
  function autoToggle() {
    if (!stopped) {
      hovered || focused ? pause(false) : play();
    }
  }
  function update() {
    if (toggle) {
      toggleClass(toggle, CLASS_ACTIVE, !stopped);
      setAttribute(toggle, ARIA_LABEL, options.i18n[stopped ? "play" : "pause"]);
    }
  }
  function onAnimationFrame(rate) {
    var bar = Elements2.bar;
    bar && style(bar, "width", rate * 100 + "%");
    emit(EVENT_AUTOPLAY_PLAYING, rate);
  }
  function onMove(index) {
    var Slide2 = Components2.Slides.getAt(index);
    interval.set(Slide2 && +getAttribute(Slide2.slide, INTERVAL_DATA_ATTRIBUTE) || options.interval);
  }
  return {
    mount,
    destroy: interval.cancel,
    play,
    pause,
    isPaused
  };
}
function Cover(Splide2, Components2, options) {
  var _EventInterface7 = EventInterface(Splide2), on2 = _EventInterface7.on;
  function mount() {
    if (options.cover) {
      on2(EVENT_LAZYLOAD_LOADED, apply(toggle, true));
      on2([EVENT_MOUNTED, EVENT_UPDATED, EVENT_REFRESH], apply(cover, true));
    }
  }
  function cover(cover2) {
    Components2.Slides.forEach(function(Slide2) {
      var img = child(Slide2.container || Slide2.slide, "img");
      if (img && img.src) {
        toggle(cover2, img, Slide2);
      }
    });
  }
  function toggle(cover2, img, Slide2) {
    Slide2.style("background", cover2 ? 'center/cover no-repeat url("' + img.src + '")' : "", true);
    display(img, cover2 ? "none" : "");
  }
  return {
    mount,
    destroy: apply(cover, false)
  };
}
var BOUNCE_DIFF_THRESHOLD = 10;
var BOUNCE_DURATION = 600;
var FRICTION_FACTOR = 0.6;
var BASE_VELOCITY = 1.5;
var MIN_DURATION = 800;
function Scroll(Splide2, Components2, options) {
  var _EventInterface8 = EventInterface(Splide2), on2 = _EventInterface8.on, emit = _EventInterface8.emit;
  var set3 = Splide2.state.set;
  var Move2 = Components2.Move;
  var getPosition = Move2.getPosition, getLimit = Move2.getLimit, exceededLimit = Move2.exceededLimit, translate = Move2.translate;
  var isSlide = Splide2.is(SLIDE);
  var interval;
  var callback;
  var friction = 1;
  function mount() {
    on2(EVENT_MOVE, clear2);
    on2([EVENT_UPDATED, EVENT_REFRESH], cancel);
  }
  function scroll(destination, duration, snap, onScrolled, noConstrain) {
    var from = getPosition();
    clear2();
    if (snap && (!isSlide || !exceededLimit())) {
      var size2 = Components2.Layout.sliderSize();
      var offset = sign(destination) * size2 * floor(abs(destination) / size2) || 0;
      destination = Move2.toPosition(Components2.Controller.toDest(destination % size2)) + offset;
    }
    var noDistance = approximatelyEqual(from, destination, 1);
    friction = 1;
    duration = noDistance ? 0 : duration || max(abs(destination - from) / BASE_VELOCITY, MIN_DURATION);
    callback = onScrolled;
    interval = RequestInterval(duration, onEnd, apply(update, from, destination, noConstrain), 1);
    set3(SCROLLING);
    emit(EVENT_SCROLL);
    interval.start();
  }
  function onEnd() {
    set3(IDLE);
    callback && callback();
    emit(EVENT_SCROLLED);
  }
  function update(from, to, noConstrain, rate) {
    var position = getPosition();
    var target = from + (to - from) * easing(rate);
    var diff = (target - position) * friction;
    translate(position + diff);
    if (isSlide && !noConstrain && exceededLimit()) {
      friction *= FRICTION_FACTOR;
      if (abs(diff) < BOUNCE_DIFF_THRESHOLD) {
        scroll(getLimit(exceededLimit(true)), BOUNCE_DURATION, false, callback, true);
      }
    }
  }
  function clear2() {
    if (interval) {
      interval.cancel();
    }
  }
  function cancel() {
    if (interval && !interval.isPaused()) {
      clear2();
      onEnd();
    }
  }
  function easing(t) {
    var easingFunc = options.easingFunc;
    return easingFunc ? easingFunc(t) : 1 - Math.pow(1 - t, 4);
  }
  return {
    mount,
    destroy: clear2,
    scroll,
    cancel
  };
}
var SCROLL_LISTENER_OPTIONS = {
  passive: false,
  capture: true
};
function Drag(Splide2, Components2, options) {
  var _EventInterface9 = EventInterface(Splide2), on2 = _EventInterface9.on, emit = _EventInterface9.emit, bind3 = _EventInterface9.bind, unbind = _EventInterface9.unbind;
  var state = Splide2.state;
  var Move2 = Components2.Move, Scroll2 = Components2.Scroll, Controller2 = Components2.Controller, track2 = Components2.Elements.track, reduce = Components2.Media.reduce;
  var _Components2$Directio2 = Components2.Direction, resolve = _Components2$Directio2.resolve, orient = _Components2$Directio2.orient;
  var getPosition = Move2.getPosition, exceededLimit = Move2.exceededLimit;
  var basePosition;
  var baseEvent;
  var prevBaseEvent;
  var isFree;
  var dragging;
  var exceeded = false;
  var clickPrevented;
  var disabled;
  var target;
  function mount() {
    bind3(track2, POINTER_MOVE_EVENTS, noop, SCROLL_LISTENER_OPTIONS);
    bind3(track2, POINTER_UP_EVENTS, noop, SCROLL_LISTENER_OPTIONS);
    bind3(track2, POINTER_DOWN_EVENTS, onPointerDown, SCROLL_LISTENER_OPTIONS);
    bind3(track2, "click", onClick, {
      capture: true
    });
    bind3(track2, "dragstart", prevent);
    on2([EVENT_MOUNTED, EVENT_UPDATED], init);
  }
  function init() {
    var drag = options.drag;
    disable(!drag);
    isFree = drag === "free";
  }
  function onPointerDown(e) {
    clickPrevented = false;
    if (!disabled) {
      var isTouch = isTouchEvent(e);
      if (isDraggable(e.target) && (isTouch || !e.button)) {
        if (!Controller2.isBusy()) {
          target = isTouch ? track2 : window;
          dragging = state.is([MOVING, SCROLLING]);
          prevBaseEvent = null;
          bind3(target, POINTER_MOVE_EVENTS, onPointerMove, SCROLL_LISTENER_OPTIONS);
          bind3(target, POINTER_UP_EVENTS, onPointerUp, SCROLL_LISTENER_OPTIONS);
          Move2.cancel();
          Scroll2.cancel();
          save(e);
        } else {
          prevent(e, true);
        }
      }
    }
  }
  function onPointerMove(e) {
    if (!state.is(DRAGGING)) {
      state.set(DRAGGING);
      emit(EVENT_DRAG);
    }
    if (e.cancelable) {
      if (dragging) {
        Move2.translate(basePosition + constrain(diffCoord(e)));
        var expired = diffTime(e) > LOG_INTERVAL;
        var hasExceeded = exceeded !== (exceeded = exceededLimit());
        if (expired || hasExceeded) {
          save(e);
        }
        clickPrevented = true;
        emit(EVENT_DRAGGING);
        prevent(e);
      } else if (isSliderDirection(e)) {
        dragging = shouldStart(e);
        prevent(e);
      }
    }
  }
  function onPointerUp(e) {
    if (state.is(DRAGGING)) {
      state.set(IDLE);
      emit(EVENT_DRAGGED);
    }
    if (dragging) {
      move(e);
      prevent(e);
    }
    unbind(target, POINTER_MOVE_EVENTS, onPointerMove);
    unbind(target, POINTER_UP_EVENTS, onPointerUp);
    dragging = false;
  }
  function onClick(e) {
    if (!disabled && clickPrevented) {
      prevent(e, true);
    }
  }
  function save(e) {
    prevBaseEvent = baseEvent;
    baseEvent = e;
    basePosition = getPosition();
  }
  function move(e) {
    var velocity = computeVelocity(e);
    var destination = computeDestination(velocity);
    var rewind = options.rewind && options.rewindByDrag;
    reduce(false);
    if (isFree) {
      Controller2.scroll(destination, 0, options.snap);
    } else if (Splide2.is(FADE)) {
      Controller2.go(orient(sign(velocity)) < 0 ? rewind ? "<" : "-" : rewind ? ">" : "+");
    } else if (Splide2.is(SLIDE) && exceeded && rewind) {
      Controller2.go(exceededLimit(true) ? ">" : "<");
    } else {
      Controller2.go(Controller2.toDest(destination), true);
    }
    reduce(true);
  }
  function shouldStart(e) {
    var thresholds = options.dragMinThreshold;
    var isObj = isObject3(thresholds);
    var mouse = isObj && thresholds.mouse || 0;
    var touch = (isObj ? thresholds.touch : +thresholds) || 10;
    return abs(diffCoord(e)) > (isTouchEvent(e) ? touch : mouse);
  }
  function isSliderDirection(e) {
    return abs(diffCoord(e)) > abs(diffCoord(e, true));
  }
  function computeVelocity(e) {
    if (Splide2.is(LOOP) || !exceeded) {
      var time = diffTime(e);
      if (time && time < LOG_INTERVAL) {
        return diffCoord(e) / time;
      }
    }
    return 0;
  }
  function computeDestination(velocity) {
    return getPosition() + sign(velocity) * min(abs(velocity) * (options.flickPower || 600), isFree ? Infinity : Components2.Layout.listSize() * (options.flickMaxPages || 1));
  }
  function diffCoord(e, orthogonal) {
    return coordOf(e, orthogonal) - coordOf(getBaseEvent(e), orthogonal);
  }
  function diffTime(e) {
    return timeOf(e) - timeOf(getBaseEvent(e));
  }
  function getBaseEvent(e) {
    return baseEvent === e && prevBaseEvent || baseEvent;
  }
  function coordOf(e, orthogonal) {
    return (isTouchEvent(e) ? e.changedTouches[0] : e)["page" + resolve(orthogonal ? "Y" : "X")];
  }
  function constrain(diff) {
    return diff / (exceeded && Splide2.is(SLIDE) ? FRICTION : 1);
  }
  function isDraggable(target2) {
    var noDrag = options.noDrag;
    return !matches(target2, "." + CLASS_PAGINATION_PAGE + ", ." + CLASS_ARROW) && (!noDrag || !matches(target2, noDrag));
  }
  function isTouchEvent(e) {
    return typeof TouchEvent !== "undefined" && e instanceof TouchEvent;
  }
  function isDragging() {
    return dragging;
  }
  function disable(value) {
    disabled = value;
  }
  return {
    mount,
    disable,
    isDragging
  };
}
var NORMALIZATION_MAP = {
  Spacebar: " ",
  Right: ARROW_RIGHT,
  Left: ARROW_LEFT,
  Up: ARROW_UP,
  Down: ARROW_DOWN
};
function normalizeKey(key) {
  key = isString2(key) ? key : key.key;
  return NORMALIZATION_MAP[key] || key;
}
var KEYBOARD_EVENT = "keydown";
function Keyboard(Splide2, Components2, options) {
  var _EventInterface10 = EventInterface(Splide2), on2 = _EventInterface10.on, bind3 = _EventInterface10.bind, unbind = _EventInterface10.unbind;
  var root = Splide2.root;
  var resolve = Components2.Direction.resolve;
  var target;
  var disabled;
  function mount() {
    init();
    on2(EVENT_UPDATED, destroy);
    on2(EVENT_UPDATED, init);
    on2(EVENT_MOVE, onMove);
  }
  function init() {
    var keyboard = options.keyboard;
    if (keyboard) {
      target = keyboard === "global" ? window : root;
      bind3(target, KEYBOARD_EVENT, onKeydown);
    }
  }
  function destroy() {
    unbind(target, KEYBOARD_EVENT);
  }
  function disable(value) {
    disabled = value;
  }
  function onMove() {
    var _disabled = disabled;
    disabled = true;
    nextTick2(function() {
      disabled = _disabled;
    });
  }
  function onKeydown(e) {
    if (!disabled) {
      var key = normalizeKey(e);
      if (key === resolve(ARROW_LEFT)) {
        Splide2.go("<");
      } else if (key === resolve(ARROW_RIGHT)) {
        Splide2.go(">");
      }
    }
  }
  return {
    mount,
    destroy,
    disable
  };
}
var SRC_DATA_ATTRIBUTE = DATA_ATTRIBUTE + "-lazy";
var SRCSET_DATA_ATTRIBUTE = SRC_DATA_ATTRIBUTE + "-srcset";
var IMAGE_SELECTOR = "[" + SRC_DATA_ATTRIBUTE + "], [" + SRCSET_DATA_ATTRIBUTE + "]";
function LazyLoad(Splide2, Components2, options) {
  var _EventInterface11 = EventInterface(Splide2), on2 = _EventInterface11.on, off = _EventInterface11.off, bind3 = _EventInterface11.bind, emit = _EventInterface11.emit;
  var isSequential = options.lazyLoad === "sequential";
  var events = [EVENT_MOVED, EVENT_SCROLLED];
  var entries = [];
  function mount() {
    if (options.lazyLoad) {
      init();
      on2(EVENT_REFRESH, init);
    }
  }
  function init() {
    empty(entries);
    register();
    if (isSequential) {
      loadNext();
    } else {
      off(events);
      on2(events, check);
      check();
    }
  }
  function register() {
    Components2.Slides.forEach(function(Slide2) {
      queryAll(Slide2.slide, IMAGE_SELECTOR).forEach(function(img) {
        var src = getAttribute(img, SRC_DATA_ATTRIBUTE);
        var srcset = getAttribute(img, SRCSET_DATA_ATTRIBUTE);
        if (src !== img.src || srcset !== img.srcset) {
          var className = options.classes.spinner;
          var parent = img.parentElement;
          var spinner = child(parent, "." + className) || create("span", className, parent);
          entries.push([img, Slide2, spinner]);
          img.src || display(img, "none");
        }
      });
    });
  }
  function check() {
    entries = entries.filter(function(data2) {
      var distance = options.perPage * ((options.preloadPages || 1) + 1) - 1;
      return data2[1].isWithin(Splide2.index, distance) ? load(data2) : true;
    });
    entries.length || off(events);
  }
  function load(data2) {
    var img = data2[0];
    addClass(data2[1].slide, CLASS_LOADING);
    bind3(img, "load error", apply(onLoad, data2));
    setAttribute(img, "src", getAttribute(img, SRC_DATA_ATTRIBUTE));
    setAttribute(img, "srcset", getAttribute(img, SRCSET_DATA_ATTRIBUTE));
    removeAttribute(img, SRC_DATA_ATTRIBUTE);
    removeAttribute(img, SRCSET_DATA_ATTRIBUTE);
  }
  function onLoad(data2, e) {
    var img = data2[0], Slide2 = data2[1];
    removeClass(Slide2.slide, CLASS_LOADING);
    if (e.type !== "error") {
      remove(data2[2]);
      display(img, "");
      emit(EVENT_LAZYLOAD_LOADED, img, Slide2);
      emit(EVENT_RESIZE);
    }
    isSequential && loadNext();
  }
  function loadNext() {
    entries.length && load(entries.shift());
  }
  return {
    mount,
    destroy: apply(empty, entries),
    check
  };
}
function Pagination(Splide2, Components2, options) {
  var event = EventInterface(Splide2);
  var on2 = event.on, emit = event.emit, bind3 = event.bind;
  var Slides2 = Components2.Slides, Elements2 = Components2.Elements, Controller2 = Components2.Controller;
  var hasFocus = Controller2.hasFocus, getIndex = Controller2.getIndex, go = Controller2.go;
  var resolve = Components2.Direction.resolve;
  var placeholder = Elements2.pagination;
  var items = [];
  var list;
  var paginationClasses;
  function mount() {
    destroy();
    on2([EVENT_UPDATED, EVENT_REFRESH, EVENT_END_INDEX_CHANGED], mount);
    var enabled = options.pagination;
    placeholder && display(placeholder, enabled ? "" : "none");
    if (enabled) {
      on2([EVENT_MOVE, EVENT_SCROLL, EVENT_SCROLLED], update);
      createPagination();
      update();
      emit(EVENT_PAGINATION_MOUNTED, {
        list,
        items
      }, getAt(Splide2.index));
    }
  }
  function destroy() {
    if (list) {
      remove(placeholder ? slice(list.children) : list);
      removeClass(list, paginationClasses);
      empty(items);
      list = null;
    }
    event.destroy();
  }
  function createPagination() {
    var length = Splide2.length;
    var classes = options.classes, i18n = options.i18n, perPage = options.perPage;
    var max3 = hasFocus() ? Controller2.getEnd() + 1 : ceil(length / perPage);
    list = placeholder || create("ul", classes.pagination, Elements2.track.parentElement);
    addClass(list, paginationClasses = CLASS_PAGINATION + "--" + getDirection());
    setAttribute(list, ROLE, "tablist");
    setAttribute(list, ARIA_LABEL, i18n.select);
    setAttribute(list, ARIA_ORIENTATION, getDirection() === TTB ? "vertical" : "");
    for (var i = 0; i < max3; i++) {
      var li = create("li", null, list);
      var button = create("button", {
        class: classes.page,
        type: "button"
      }, li);
      var controls = Slides2.getIn(i).map(function(Slide2) {
        return Slide2.slide.id;
      });
      var text = !hasFocus() && perPage > 1 ? i18n.pageX : i18n.slideX;
      bind3(button, "click", apply(onClick, i));
      if (options.paginationKeyboard) {
        bind3(button, "keydown", apply(onKeydown, i));
      }
      setAttribute(li, ROLE, "presentation");
      setAttribute(button, ROLE, "tab");
      setAttribute(button, ARIA_CONTROLS, controls.join(" "));
      setAttribute(button, ARIA_LABEL, format(text, i + 1));
      setAttribute(button, TAB_INDEX, -1);
      items.push({
        li,
        button,
        page: i
      });
    }
  }
  function onClick(page) {
    go(">" + page, true);
  }
  function onKeydown(page, e) {
    var length = items.length;
    var key = normalizeKey(e);
    var dir = getDirection();
    var nextPage = -1;
    if (key === resolve(ARROW_RIGHT, false, dir)) {
      nextPage = ++page % length;
    } else if (key === resolve(ARROW_LEFT, false, dir)) {
      nextPage = (--page + length) % length;
    } else if (key === "Home") {
      nextPage = 0;
    } else if (key === "End") {
      nextPage = length - 1;
    }
    var item = items[nextPage];
    if (item) {
      focus(item.button);
      go(">" + nextPage);
      prevent(e, true);
    }
  }
  function getDirection() {
    return options.paginationDirection || options.direction;
  }
  function getAt(index) {
    return items[Controller2.toPage(index)];
  }
  function update() {
    var prev = getAt(getIndex(true));
    var curr = getAt(getIndex());
    if (prev) {
      var button = prev.button;
      removeClass(button, CLASS_ACTIVE);
      removeAttribute(button, ARIA_SELECTED);
      setAttribute(button, TAB_INDEX, -1);
    }
    if (curr) {
      var _button = curr.button;
      addClass(_button, CLASS_ACTIVE);
      setAttribute(_button, ARIA_SELECTED, true);
      setAttribute(_button, TAB_INDEX, "");
    }
    emit(EVENT_PAGINATION_UPDATED, {
      list,
      items
    }, prev, curr);
  }
  return {
    items,
    mount,
    destroy,
    getAt,
    update
  };
}
var TRIGGER_KEYS = [" ", "Enter"];
function Sync(Splide2, Components2, options) {
  var isNavigation = options.isNavigation, slideFocus = options.slideFocus;
  var events = [];
  function mount() {
    Splide2.splides.forEach(function(target) {
      if (!target.isParent) {
        sync(Splide2, target.splide);
        sync(target.splide, Splide2);
      }
    });
    if (isNavigation) {
      navigate();
    }
  }
  function destroy() {
    events.forEach(function(event) {
      event.destroy();
    });
    empty(events);
  }
  function remount() {
    destroy();
    mount();
  }
  function sync(splide, target) {
    var event = EventInterface(splide);
    event.on(EVENT_MOVE, function(index, prev, dest) {
      target.go(target.is(LOOP) ? dest : index);
    });
    events.push(event);
  }
  function navigate() {
    var event = EventInterface(Splide2);
    var on2 = event.on;
    on2(EVENT_CLICK, onClick);
    on2(EVENT_SLIDE_KEYDOWN, onKeydown);
    on2([EVENT_MOUNTED, EVENT_UPDATED], update);
    events.push(event);
    event.emit(EVENT_NAVIGATION_MOUNTED, Splide2.splides);
  }
  function update() {
    setAttribute(Components2.Elements.list, ARIA_ORIENTATION, options.direction === TTB ? "vertical" : "");
  }
  function onClick(Slide2) {
    Splide2.go(Slide2.index);
  }
  function onKeydown(Slide2, e) {
    if (includes(TRIGGER_KEYS, normalizeKey(e))) {
      onClick(Slide2);
      prevent(e);
    }
  }
  return {
    setup: apply(Components2.Media.set, {
      slideFocus: isUndefined(slideFocus) ? isNavigation : slideFocus
    }, true),
    mount,
    destroy,
    remount
  };
}
function Wheel(Splide2, Components2, options) {
  var _EventInterface12 = EventInterface(Splide2), bind3 = _EventInterface12.bind;
  var lastTime = 0;
  function mount() {
    if (options.wheel) {
      bind3(Components2.Elements.track, "wheel", onWheel, SCROLL_LISTENER_OPTIONS);
    }
  }
  function onWheel(e) {
    if (e.cancelable) {
      var deltaY = e.deltaY;
      var backwards = deltaY < 0;
      var timeStamp = timeOf(e);
      var _min = options.wheelMinThreshold || 0;
      var sleep = options.wheelSleep || 0;
      if (abs(deltaY) > _min && timeStamp - lastTime > sleep) {
        Splide2.go(backwards ? "<" : ">");
        lastTime = timeStamp;
      }
      shouldPrevent(backwards) && prevent(e);
    }
  }
  function shouldPrevent(backwards) {
    return !options.releaseWheel || Splide2.state.is(MOVING) || Components2.Controller.getAdjacent(backwards) !== -1;
  }
  return {
    mount
  };
}
var SR_REMOVAL_DELAY = 90;
function Live(Splide2, Components2, options) {
  var _EventInterface13 = EventInterface(Splide2), on2 = _EventInterface13.on;
  var track2 = Components2.Elements.track;
  var enabled = options.live && !options.isNavigation;
  var sr = create("span", CLASS_SR);
  var interval = RequestInterval(SR_REMOVAL_DELAY, apply(toggle, false));
  function mount() {
    if (enabled) {
      disable(!Components2.Autoplay.isPaused());
      setAttribute(track2, ARIA_ATOMIC, true);
      sr.textContent = "\u2026";
      on2(EVENT_AUTOPLAY_PLAY, apply(disable, true));
      on2(EVENT_AUTOPLAY_PAUSE, apply(disable, false));
      on2([EVENT_MOVED, EVENT_SCROLLED], apply(toggle, true));
    }
  }
  function toggle(active) {
    setAttribute(track2, ARIA_BUSY, active);
    if (active) {
      append(track2, sr);
      interval.start();
    } else {
      remove(sr);
      interval.cancel();
    }
  }
  function destroy() {
    removeAttribute(track2, [ARIA_LIVE, ARIA_ATOMIC, ARIA_BUSY]);
    remove(sr);
  }
  function disable(disabled) {
    if (enabled) {
      setAttribute(track2, ARIA_LIVE, disabled ? "off" : "polite");
    }
  }
  return {
    mount,
    disable,
    destroy
  };
}
var ComponentConstructors = /* @__PURE__ */ Object.freeze({
  __proto__: null,
  Media,
  Direction,
  Elements,
  Slides,
  Layout,
  Clones,
  Move,
  Controller,
  Arrows,
  Autoplay,
  Cover,
  Scroll,
  Drag,
  Keyboard,
  LazyLoad,
  Pagination,
  Sync,
  Wheel,
  Live
});
var I18N = {
  prev: "Previous slide",
  next: "Next slide",
  first: "Go to first slide",
  last: "Go to last slide",
  slideX: "Go to slide %s",
  pageX: "Go to page %s",
  play: "Start autoplay",
  pause: "Pause autoplay",
  carousel: "carousel",
  slide: "slide",
  select: "Select a slide to show",
  slideLabel: "%s of %s"
};
var DEFAULTS = {
  type: "slide",
  role: "region",
  speed: 400,
  perPage: 1,
  cloneStatus: true,
  arrows: true,
  pagination: true,
  paginationKeyboard: true,
  interval: 5e3,
  pauseOnHover: true,
  pauseOnFocus: true,
  resetProgress: true,
  easing: "cubic-bezier(0.25, 1, 0.5, 1)",
  drag: true,
  direction: "ltr",
  trimSpace: true,
  focusableNodes: "a, button, textarea, input, select, iframe",
  live: true,
  classes: CLASSES,
  i18n: I18N,
  reducedMotion: {
    speed: 0,
    rewindSpeed: 0,
    autoplay: "pause"
  }
};
function Fade(Splide2, Components2, options) {
  var Slides2 = Components2.Slides;
  function mount() {
    EventInterface(Splide2).on([EVENT_MOUNTED, EVENT_REFRESH], init);
  }
  function init() {
    Slides2.forEach(function(Slide2) {
      Slide2.style("transform", "translateX(-" + 100 * Slide2.index + "%)");
    });
  }
  function start2(index, done) {
    Slides2.style("transition", "opacity " + options.speed + "ms " + options.easing);
    nextTick2(done);
  }
  return {
    mount,
    start: start2,
    cancel: noop
  };
}
function Slide(Splide2, Components2, options) {
  var Move2 = Components2.Move, Controller2 = Components2.Controller, Scroll2 = Components2.Scroll;
  var list = Components2.Elements.list;
  var transition2 = apply(style, list, "transition");
  var endCallback;
  function mount() {
    EventInterface(Splide2).bind(list, "transitionend", function(e) {
      if (e.target === list && endCallback) {
        cancel();
        endCallback();
      }
    });
  }
  function start2(index, done) {
    var destination = Move2.toPosition(index, true);
    var position = Move2.getPosition();
    var speed = getSpeed(index);
    if (abs(destination - position) >= 1 && speed >= 1) {
      if (options.useScroll) {
        Scroll2.scroll(destination, speed, false, done);
      } else {
        transition2("transform " + speed + "ms " + options.easing);
        Move2.translate(destination, true);
        endCallback = done;
      }
    } else {
      Move2.jump(index);
      done();
    }
  }
  function cancel() {
    transition2("");
    Scroll2.cancel();
  }
  function getSpeed(index) {
    var rewindSpeed = options.rewindSpeed;
    if (Splide2.is(SLIDE) && rewindSpeed) {
      var prev = Controller2.getIndex(true);
      var end = Controller2.getEnd();
      if (prev === 0 && index >= end || prev >= end && index === 0) {
        return rewindSpeed;
      }
    }
    return options.speed;
  }
  return {
    mount,
    start: start2,
    cancel
  };
}
var _Splide = /* @__PURE__ */ function() {
  function _Splide2(target, options) {
    this.event = EventInterface();
    this.Components = {};
    this.state = State(CREATED);
    this.splides = [];
    this._o = {};
    this._E = {};
    var root = isString2(target) ? query(document, target) : target;
    assert(root, root + " is invalid.");
    this.root = root;
    options = merge({
      label: getAttribute(root, ARIA_LABEL) || "",
      labelledby: getAttribute(root, ARIA_LABELLEDBY) || ""
    }, DEFAULTS, _Splide2.defaults, options || {});
    try {
      merge(options, JSON.parse(getAttribute(root, DATA_ATTRIBUTE)));
    } catch (e) {
      assert(false, "Invalid JSON");
    }
    this._o = Object.create(merge({}, options));
  }
  var _proto = _Splide2.prototype;
  _proto.mount = function mount(Extensions, Transition) {
    var _this = this;
    var state = this.state, Components2 = this.Components;
    assert(state.is([CREATED, DESTROYED]), "Already mounted!");
    state.set(CREATED);
    this._C = Components2;
    this._T = Transition || this._T || (this.is(FADE) ? Fade : Slide);
    this._E = Extensions || this._E;
    var Constructors = assign({}, ComponentConstructors, this._E, {
      Transition: this._T
    });
    forOwn(Constructors, function(Component, key) {
      var component = Component(_this, Components2, _this._o);
      Components2[key] = component;
      component.setup && component.setup();
    });
    forOwn(Components2, function(component) {
      component.mount && component.mount();
    });
    this.emit(EVENT_MOUNTED);
    addClass(this.root, CLASS_INITIALIZED);
    state.set(IDLE);
    this.emit(EVENT_READY);
    return this;
  };
  _proto.sync = function sync(splide) {
    this.splides.push({
      splide
    });
    splide.splides.push({
      splide: this,
      isParent: true
    });
    if (this.state.is(IDLE)) {
      this._C.Sync.remount();
      splide.Components.Sync.remount();
    }
    return this;
  };
  _proto.go = function go(control) {
    this._C.Controller.go(control);
    return this;
  };
  _proto.on = function on2(events, callback) {
    this.event.on(events, callback);
    return this;
  };
  _proto.off = function off(events) {
    this.event.off(events);
    return this;
  };
  _proto.emit = function emit(event) {
    var _this$event;
    (_this$event = this.event).emit.apply(_this$event, [event].concat(slice(arguments, 1)));
    return this;
  };
  _proto.add = function add2(slides, index) {
    this._C.Slides.add(slides, index);
    return this;
  };
  _proto.remove = function remove2(matcher) {
    this._C.Slides.remove(matcher);
    return this;
  };
  _proto.is = function is(type) {
    return this._o.type === type;
  };
  _proto.refresh = function refresh() {
    this.emit(EVENT_REFRESH);
    return this;
  };
  _proto.destroy = function destroy(completely) {
    if (completely === void 0) {
      completely = true;
    }
    var event = this.event, state = this.state;
    if (state.is(CREATED)) {
      EventInterface(this).on(EVENT_READY, this.destroy.bind(this, completely));
    } else {
      forOwn(this._C, function(component) {
        component.destroy && component.destroy(completely);
      }, true);
      event.emit(EVENT_DESTROY);
      event.destroy();
      completely && empty(this.splides);
      state.set(DESTROYED);
    }
    return this;
  };
  _createClass(_Splide2, [{
    key: "options",
    get: function get3() {
      return this._o;
    },
    set: function set3(options) {
      this._C.Media.set(options, true, true);
    }
  }, {
    key: "length",
    get: function get3() {
      return this._C.Slides.getLength(true);
    }
  }, {
    key: "index",
    get: function get3() {
      return this._C.Controller.getIndex();
    }
  }]);
  return _Splide2;
}();
var Splide = _Splide;
Splide.defaults = {};
Splide.STATES = STATES;

// node_modules/@splidejs/splide-extension-auto-scroll/dist/js/splide-extension-auto-scroll.esm.js
function empty2(array) {
  array.length = 0;
}
function slice$1(arrayLike, start2, end) {
  return Array.prototype.slice.call(arrayLike, start2, end);
}
function apply$1(func) {
  return func.bind.apply(func, [null].concat(slice$1(arguments, 1)));
}
function raf2(func) {
  return requestAnimationFrame(func);
}
function typeOf$1(type, subject) {
  return typeof subject === type;
}
var isArray$1 = Array.isArray;
apply$1(typeOf$1, "function");
apply$1(typeOf$1, "string");
apply$1(typeOf$1, "undefined");
function toArray$1(value) {
  return isArray$1(value) ? value : [value];
}
function forEach$1(values, iteratee) {
  toArray$1(values).forEach(iteratee);
}
var ownKeys$1 = Object.keys;
function forOwn$1(object, iteratee, right) {
  if (object) {
    var keys = ownKeys$1(object);
    keys = right ? keys.reverse() : keys;
    for (var i = 0; i < keys.length; i++) {
      var key = keys[i];
      if (key !== "__proto__") {
        if (iteratee(object[key], key) === false) {
          break;
        }
      }
    }
  }
  return object;
}
function assign$1(object) {
  slice$1(arguments, 1).forEach(function(source) {
    forOwn$1(source, function(value, key) {
      object[key] = source[key];
    });
  });
  return object;
}
var min$1 = Math.min;
function EventBinder2() {
  var listeners = [];
  function bind3(targets, events, callback, options) {
    forEachEvent(targets, events, function(target, event, namespace) {
      var isEventTarget = "addEventListener" in target;
      var remover = isEventTarget ? target.removeEventListener.bind(target, event, callback, options) : target["removeListener"].bind(target, callback);
      isEventTarget ? target.addEventListener(event, callback, options) : target["addListener"](callback);
      listeners.push([target, event, namespace, callback, remover]);
    });
  }
  function unbind(targets, events, callback) {
    forEachEvent(targets, events, function(target, event, namespace) {
      listeners = listeners.filter(function(listener) {
        if (listener[0] === target && listener[1] === event && listener[2] === namespace && (!callback || listener[3] === callback)) {
          listener[4]();
          return false;
        }
        return true;
      });
    });
  }
  function dispatch2(target, type, detail) {
    var e;
    var bubbles = true;
    if (typeof CustomEvent === "function") {
      e = new CustomEvent(type, {
        bubbles,
        detail
      });
    } else {
      e = document.createEvent("CustomEvent");
      e.initCustomEvent(type, bubbles, false, detail);
    }
    target.dispatchEvent(e);
    return e;
  }
  function forEachEvent(targets, events, iteratee) {
    forEach$1(targets, function(target) {
      target && forEach$1(events, function(events2) {
        events2.split(" ").forEach(function(eventNS) {
          var fragment = eventNS.split(".");
          iteratee(target, fragment[0], fragment[1]);
        });
      });
    });
  }
  function destroy() {
    listeners.forEach(function(data2) {
      data2[4]();
    });
    empty2(listeners);
  }
  return {
    bind: bind3,
    unbind,
    dispatch: dispatch2,
    destroy
  };
}
var EVENT_MOVE2 = "move";
var EVENT_MOVED2 = "moved";
var EVENT_UPDATED2 = "updated";
var EVENT_DRAG2 = "drag";
var EVENT_DRAGGED2 = "dragged";
var EVENT_SCROLL2 = "scroll";
var EVENT_SCROLLED2 = "scrolled";
var EVENT_DESTROY2 = "destroy";
function EventInterface2(Splide2) {
  var bus = Splide2 ? Splide2.event.bus : document.createDocumentFragment();
  var binder = EventBinder2();
  function on2(events, callback) {
    binder.bind(bus, toArray$1(events).join(" "), function(e) {
      callback.apply(callback, isArray$1(e.detail) ? e.detail : []);
    });
  }
  function emit(event) {
    binder.dispatch(bus, event, slice$1(arguments, 1));
  }
  if (Splide2) {
    Splide2.event.on(EVENT_DESTROY2, binder.destroy);
  }
  return assign$1(binder, {
    bus,
    on: on2,
    off: apply$1(binder.unbind, bus),
    emit
  });
}
function RequestInterval2(interval, onInterval, onUpdate, limit) {
  var now = Date.now;
  var startTime;
  var rate = 0;
  var id;
  var paused = true;
  var count = 0;
  function update() {
    if (!paused) {
      rate = interval ? min$1((now() - startTime) / interval, 1) : 1;
      onUpdate && onUpdate(rate);
      if (rate >= 1) {
        onInterval();
        startTime = now();
        if (limit && ++count >= limit) {
          return pause();
        }
      }
      raf2(update);
    }
  }
  function start2(resume) {
    !resume && cancel();
    startTime = now() - (resume ? rate * interval : 0);
    paused = false;
    raf2(update);
  }
  function pause() {
    paused = true;
  }
  function rewind() {
    startTime = now();
    rate = 0;
    if (onUpdate) {
      onUpdate(rate);
    }
  }
  function cancel() {
    id && cancelAnimationFrame(id);
    rate = 0;
    id = 0;
    paused = true;
  }
  function set3(time) {
    interval = time;
  }
  function isPaused() {
    return paused;
  }
  return {
    start: start2,
    rewind,
    pause,
    cancel,
    set: set3,
    isPaused
  };
}
function Throttle2(func, duration) {
  var interval;
  function throttled() {
    if (!interval) {
      interval = RequestInterval2(duration || 0, function() {
        func();
        interval = null;
      }, null, 1);
      interval.start();
    }
  }
  return throttled;
}
var CLASS_ACTIVE2 = "is-active";
var SLIDE2 = "slide";
var FADE2 = "fade";
function slice2(arrayLike, start2, end) {
  return Array.prototype.slice.call(arrayLike, start2, end);
}
function apply2(func) {
  return func.bind(null, ...slice2(arguments, 1));
}
function typeOf2(type, subject) {
  return typeof subject === type;
}
function isObject4(subject) {
  return !isNull2(subject) && typeOf2("object", subject);
}
var isArray3 = Array.isArray;
apply2(typeOf2, "function");
apply2(typeOf2, "string");
var isUndefined2 = apply2(typeOf2, "undefined");
function isNull2(subject) {
  return subject === null;
}
function toArray2(value) {
  return isArray3(value) ? value : [value];
}
function forEach2(values, iteratee) {
  toArray2(values).forEach(iteratee);
}
function toggleClass2(elm, classes, add2) {
  if (elm) {
    forEach2(classes, (name) => {
      if (name) {
        elm.classList[add2 ? "add" : "remove"](name);
      }
    });
  }
}
var ownKeys3 = Object.keys;
function forOwn2(object, iteratee, right) {
  if (object) {
    let keys = ownKeys3(object);
    keys = right ? keys.reverse() : keys;
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      if (key !== "__proto__") {
        if (iteratee(object[key], key) === false) {
          break;
        }
      }
    }
  }
  return object;
}
function assign2(object) {
  slice2(arguments, 1).forEach((source) => {
    forOwn2(source, (value, key) => {
      object[key] = source[key];
    });
  });
  return object;
}
function removeAttribute2(elms, attrs) {
  forEach2(elms, (elm) => {
    forEach2(attrs, (attr) => {
      elm && elm.removeAttribute(attr);
    });
  });
}
function setAttribute2(elms, attrs, value) {
  if (isObject4(attrs)) {
    forOwn2(attrs, (value2, name) => {
      setAttribute2(elms, name, value2);
    });
  } else {
    forEach2(elms, (elm) => {
      isNull2(value) || value === "" ? removeAttribute2(elm, attrs) : elm.setAttribute(attrs, String(value));
    });
  }
}
var { min: min2, max: max2, floor: floor2, ceil: ceil2, abs: abs2 } = Math;
function clamp2(number, x, y) {
  const minimum = min2(x, y);
  const maximum = max2(x, y);
  return min2(max2(minimum, number), maximum);
}
var DEFAULTS2 = {
  speed: 1,
  autoStart: true,
  pauseOnHover: true,
  pauseOnFocus: true
};
var I18N2 = {
  startScroll: "Start auto scroll",
  pauseScroll: "Pause auto scroll"
};
function AutoScroll(Splide2, Components2, options) {
  const { on: on2, off, bind: bind3, unbind } = EventInterface2(Splide2);
  const { translate, getPosition, toIndex, getLimit } = Components2.Move;
  const { setIndex, getIndex } = Components2.Controller;
  const { orient } = Components2.Direction;
  const { toggle } = Components2.Elements;
  const { Live: Live2 } = Components2;
  const { root } = Splide2;
  const throttledUpdateArrows = Throttle2(Components2.Arrows.update, 500);
  let autoScrollOptions = {};
  let interval;
  let stopped;
  let hovered;
  let focused;
  let busy;
  let currPosition;
  function setup() {
    const { autoScroll } = options;
    autoScrollOptions = assign2({}, DEFAULTS2, isObject4(autoScroll) ? autoScroll : {});
  }
  function mount() {
    if (!Splide2.is(FADE2)) {
      if (!interval && options.autoScroll !== false) {
        interval = RequestInterval2(0, move);
        listen();
        autoStart();
      }
    }
  }
  function destroy() {
    if (interval) {
      interval.cancel();
      interval = null;
      currPosition = void 0;
      off([EVENT_MOVE2, EVENT_DRAG2, EVENT_SCROLL2, EVENT_MOVED2, EVENT_SCROLLED2]);
      unbind(root, "mouseenter mouseleave focusin focusout");
      unbind(toggle, "click");
    }
  }
  function listen() {
    if (autoScrollOptions.pauseOnHover) {
      bind3(root, "mouseenter mouseleave", (e) => {
        hovered = e.type === "mouseenter";
        autoToggle();
      });
    }
    if (autoScrollOptions.pauseOnFocus) {
      bind3(root, "focusin focusout", (e) => {
        focused = e.type === "focusin";
        autoToggle();
      });
    }
    if (autoScrollOptions.useToggleButton) {
      bind3(toggle, "click", () => {
        stopped ? play() : pause();
      });
    }
    on2(EVENT_UPDATED2, update);
    on2([EVENT_MOVE2, EVENT_DRAG2, EVENT_SCROLL2], () => {
      busy = true;
      pause(false);
    });
    on2([EVENT_MOVED2, EVENT_DRAGGED2, EVENT_SCROLLED2], () => {
      busy = false;
      autoToggle();
    });
  }
  function update() {
    const { autoScroll } = options;
    if (autoScroll !== false) {
      autoScrollOptions = assign2({}, autoScrollOptions, isObject4(autoScroll) ? autoScroll : {});
      mount();
    } else {
      destroy();
    }
    if (interval && !isUndefined2(currPosition)) {
      translate(currPosition);
    }
  }
  function autoStart() {
    if (autoScrollOptions.autoStart) {
      if (document.readyState === "complete") {
        play();
      } else {
        bind3(window, "load", play);
      }
    }
  }
  function play() {
    if (isPaused()) {
      interval.start(true);
      Live2.disable(true);
      focused = hovered = stopped = false;
      updateButton();
    }
  }
  function pause(stop2 = true) {
    if (!stopped) {
      stopped = stop2;
      updateButton();
      if (!isPaused()) {
        interval.pause();
        Live2.disable(false);
      }
    }
  }
  function autoToggle() {
    if (!stopped) {
      hovered || focused || busy ? pause(false) : play();
    }
  }
  function move() {
    const position = getPosition();
    const destination = computeDestination(position);
    if (position !== destination) {
      translate(destination);
      updateIndex(currPosition = getPosition());
    } else {
      pause(false);
      if (autoScrollOptions.rewind) {
        Splide2.go(autoScrollOptions.speed > 0 ? 0 : Components2.Controller.getEnd());
      }
    }
    throttledUpdateArrows();
  }
  function computeDestination(position) {
    const speed = autoScrollOptions.speed || 1;
    position += orient(speed);
    if (Splide2.is(SLIDE2)) {
      position = clamp2(position, getLimit(false), getLimit(true));
    }
    return position;
  }
  function updateIndex(position) {
    const { length } = Splide2;
    const index = (toIndex(position) + length) % length;
    if (index !== getIndex()) {
      setIndex(index);
      Components2.Slides.update();
      Components2.Pagination.update();
      options.lazyLoad === "nearby" && Components2.LazyLoad.check();
    }
  }
  function updateButton() {
    if (toggle) {
      const key = stopped ? "startScroll" : "pauseScroll";
      toggleClass2(toggle, CLASS_ACTIVE2, !stopped);
      setAttribute2(toggle, "aria-label", options.i18n[key] || I18N2[key]);
    }
  }
  function isPaused() {
    return !interval || interval.isPaused();
  }
  return {
    setup,
    mount,
    destroy,
    play,
    pause,
    isPaused
  };
}

// src/scripts/components/splide-default.js
var DefaultSplide = class {
  params = {
    classes: {
      arrows: "splide__arrows slider-arrows",
      arrow: "splide__arrow slider-arrow",
      prev: "splide__arrow--prev slider-arrow-prev",
      next: "splide__arrow--next slider-arrow-next",
      pagination: "splide__pagination slider-pagination",
      page: "splide__pagination__page slider-page"
    },
    direction: "ltr",
    arrows: true,
    pagination: true,
    type: "loop",
    drag: true,
    snap: true,
    perPage: 1,
    perMove: 1,
    label: "Slider",
    rewind: false,
    rewindSpeed: 800,
    speed: 400,
    width: "100%",
    height: "auto",
    start: 0,
    gap: "0em",
    padding: "0em",
    autoplay: false,
    interval: 5e3,
    lazyLoad: "nearby",
    omitEnd: false,
    mediaQuery: "min",
    hasProgress: false,
    keyboard: true,
    allowTouchMove: true,
    flickPower: 200,
    flickMaxPages: 1,
    trackOverflow: "hidden",
    breakpoints: {
      767: {
        flickPower: 500
      }
    }
  };
  override;
  splide_element;
  splide_instance;
  id;
  navigation;
  constructor({
    desktop_slide_count,
    mobile_slide_count,
    direction,
    elm,
    override
  }) {
    this.override = override || {};
    this.desktop_slide_count = desktop_slide_count || 3;
    this.mobile_slide_count = mobile_slide_count || 1;
    this.direction = direction || "ltr";
    this.splide_element = elm;
    this.id = this.splide_element.dataset.id || this.splide_element.id || "splide";
    this.setParams(override || {});
    if (this.override.sync) {
      this.navigation = this.override.sync;
    }
    if (this.splide_element.dataset.scroll_speed) {
      this.params.autoScroll = {
        ...this.params.autoScroll || {},
        speed: this.splide_element.dataset.scroll_speed
      };
    }
  }
  setParams = (params) => {
    if (this.splide_element.dataset.desktop_slide_count && this.splide_element.dataset.mobile_slide_count) {
      this.desktop_slide_count = this.splide_element.dataset.desktop_slide_count;
      this.mobile_slide_count = this.splide_element.dataset.mobile_slide_count;
    }
    if (this.splide_element.dataset.direction) {
      this.direction = this.splide_element.dataset.direction;
    }
    ["mobile", "desktop"].forEach((device) => {
      const capitalizedDevice = device.charAt(0).toUpperCase() + device.slice(1);
      let amount = this.splide_element.dataset[`paddingRight${capitalizedDevice}`] || 0;
      this[`${device}_padding`] = {
        right: amount
      };
    });
    this.params.perPage = Math.floor(Number(this.mobile_slide_count));
    this.params.padding = this.mobile_padding;
    if (this.splide_element.dataset.gap) {
      this.params.gap = this.splide_element.dataset.gap;
    }
    const breakpointsOverride = params.breakpoints;
    delete params.breakpoints;
    this.params = { ...this.params, ...params };
    this.params.breakpoints = {
      768: {
        effect: "slide",
        allowTouchMove: false,
        navigation: false,
        perPage: Math.max(1, Math.floor(Number(this.desktop_slide_count))),
        padding: this.desktop_padding,
        gap: this.splide_element.dataset.gap || this.params.gap
      }
    };
    this.params.direction = this.direction;
    if (breakpointsOverride) {
      this.setBreakpoints(breakpointsOverride);
    }
  };
  setEventListeners = () => {
    this.splide_instance.on("mounted", this.afterLoad);
    this.splide_instance.on("resize", this.onResize);
    this.splide_instance.on("moved", this.onMoved);
    this.splide_instance.on("scrolled", this.onScrolled);
    this.splide_instance.on("destroy", this.onDestroy);
  };
  setBreakpoints = (breakpoints) => {
    for (const [key, value] of Object.entries(breakpoints)) {
      this.params.breakpoints[key] = {
        ...this.params.breakpoints[key],
        ...value
      };
    }
  };
  createSplide = () => {
    this.splide_instance = new Splide(this.splide_element, this.params);
    if (this.navigation) {
      this.splide_instance.sync(this.navigation.splide_instance);
    }
    this.setEventListeners();
    this.mount();
    const event = new CustomEvent(`swanky:splide:initialized${this.id}`, {
      detail: {
        splide_element: this.splide_element,
        splide_instance: this.splide_instance
      }
    });
    window.dispatchEvent(event);
  };
  mount = () => {
    const mountOptions = this.params.autoScroll ? { AutoScroll } : void 0;
    if (this.params.isNavigation !== true) {
      this.splide_instance.mount(mountOptions);
    }
    if (this.navigation) {
      this.navigation.splide_instance.mount(mountOptions);
    }
    const event = new CustomEvent(`swanky:splide:mounted${this.id}`, {
      bubbles: true,
      detail: {
        element: this.splide_element
      }
    });
    this.splide_element.dispatchEvent(event);
  };
  onMoved = (index) => {
    this.toggleMedia(index);
    const event = new CustomEvent(`swanky:splide:moved${this.id}`, {
      bubbles: true,
      detail: {
        element: this.splide_element
      }
    });
    this.splide_element.dispatchEvent(event);
  };
  onScrolled = (index) => {
    this.toggleMedia(index);
    const event = new CustomEvent(`swanky:splide:moved${this.id}`, {
      bubbles: true,
      detail: {
        element: this.splide_element
      }
    });
    this.splide_element.dispatchEvent(event);
  };
  onResize = () => {
  };
  afterLoad = () => {
    const track2 = this.splide_element.querySelector(".splide__track");
    if (track2 && this.override.trackOverflow) {
      track2.style.overflow = this.override.trackOverflow;
      if (this.override.trackOverflow === "visible") {
        const section = track2.closest("section");
        if (section) {
          section.style.overflow = "hidden";
        }
      }
    }
  };
  getSlide = (index) => {
    return this.splide_instance.Components.Slides.getAt(index);
  };
  onMove = () => {
  };
  goTo = (index) => {
    this.splide_instance.go(index);
  };
  addSlide = (slide) => {
    this.splide_instance.add(slide);
  };
  removeSlide = (index) => {
    this.splide_instance.remove(index);
  };
  refresh = () => {
    this.splide_instance.refresh();
  };
  onDestroy = () => {
    const event = new CustomEvent(`swanky:splide:destroyed${this.id}`, {
      detail: {
        splide_element: this.splide_element,
        splide_instance: this.splide_instance
      }
    });
    window.dispatchEvent(event);
  };
  toggleMedia = (index) => {
    const slides = this.splide_instance.Components.Slides.get();
    const currentSlideInstance = this.getSlide(index);
    slides.forEach((slideInstance) => {
      const slide = slideInstance.slide;
      const video = slide.querySelector("video");
      const model = slide.querySelector("model-media");
      let action = "pause";
      if (currentSlideInstance == slideInstance) {
        action = "play";
      }
      if (video) {
        video[action]();
      }
      if (model) {
        model[action]();
      }
    });
  };
  init = () => {
    this.createSplide();
  };
};

// src/scripts/islands/header.js
var header = (data2 = {
  isBurgerMenuOpen: false
  // Set the default value for isBurgerMenuOpen
}) => ({
  isBurgerMenuOpen: data2.isBurgerMenuOpen,
  isSearchExpanded: data2.isSearchExpanded,
  init() {
    this.setEventListeners();
    this.bodyClass();
    this.positionStickyHeader();
    this.updateHeaderHeights();
    this.positionFloatingHeader();
  },
  setEventListeners() {
    window.addEventListener("resize", () => {
      this.closeBurgerMenu();
      this.positionStickyHeader();
      this.updateHeaderHeights();
    });
    window.addEventListener("scroll", () => {
      this.updateHeaderHeights();
    });
    window.addEventListener("megamenu-active", () => {
      this.toggleDesktopMenu(true);
    });
    window.addEventListener("megamenu-inactive", () => {
      this.toggleDesktopMenu(false);
    });
    window.addEventListener("load", () => {
      this.bodyClass();
    });
  },
  positionFloatingHeader() {
    const headerWrapper = document.querySelector(".header__wrapper.floating");
    if (!headerWrapper) return;
    const headerHeight = getComputedStyle(document.documentElement).getPropertyValue("--header-height").trim();
    if (headerHeight && headerHeight !== "0px") {
      requestAnimationFrame(() => {
        headerWrapper.style.position = "absolute";
      });
    }
  },
  positionStickyHeader() {
    const header2 = document.querySelector("header");
    const headerIsSticky = document.querySelector(".header").classList.contains("sticky");
    let headerTopAmount = 0;
    const announcementBar = document.querySelector(".announcement-bar");
    const announcementBarHeight = announcementBar?.offsetHeight;
    const announcementBarIsSticky = document.querySelector(".announcement-bar__wrapper")?.classList.contains("sticky");
    let headerIsBeforeAnnouncementBar = false;
    if (announcementBar) {
      const comparison = header2.compareDocumentPosition(announcementBar);
      if (comparison === 2) {
        headerIsBeforeAnnouncementBar = false;
      } else if (comparison === 4) {
        headerIsBeforeAnnouncementBar = true;
      }
    }
    if (headerIsBeforeAnnouncementBar == false) {
      if (announcementBarIsSticky) {
        announcementBar.style.top = "0px";
        headerTopAmount += announcementBarHeight;
      }
      if (headerIsSticky) {
        header2.style.top = `${headerTopAmount}px`;
      }
    } else {
      if (headerIsSticky) {
        header2.style.top = "0px";
      }
      if (announcementBarIsSticky) {
        announcementBar.style.top = `${header2.offsetHeight}px`;
      }
    }
  },
  updateHeaderHeights() {
    const header2 = document.querySelector(".header__wrapper");
    const headerGroup = document.querySelector(".header-group");
    const announcementWrapper = document.querySelector(
      ".announcement-bar__wrapper"
    );
    const headerElement = document.querySelector(".header");
    if (!header2 || !headerGroup) return;
    let announcementBarHeight = 0;
    for (const child2 of headerGroup.children) {
      if (child2 === header2 || child2.contains(header2)) break;
      announcementBarHeight += child2.offsetHeight;
    }
    const announcementBarIsSticky = announcementWrapper?.classList.contains("sticky");
    const headerMarginTop = parseInt(getComputedStyle(header2).marginTop) || 0;
    const headerIsFloating = header2.classList.contains("floating");
    const headerIsSticky = headerElement?.classList.contains("sticky");
    const headerHeight = header2.offsetHeight + (headerIsFloating ? headerMarginTop : 0);
    const nav = document.querySelector(".header__nav-main");
    const logo = document.querySelector(".logo");
    let topRowHeight = headerHeight;
    if (nav && logo) {
      const navRect = nav.getBoundingClientRect();
      const logoRect = logo.getBoundingClientRect();
      if (navRect.top >= logoRect.bottom - 1) {
        topRowHeight = logoRect.bottom - header2.getBoundingClientRect().top;
      }
    }
    const headerGroupHeight = announcementBarHeight + topRowHeight;
    let totalHeaderHeight = 0;
    if (announcementBarIsSticky && headerIsSticky) {
      totalHeaderHeight = announcementBarHeight + headerHeight;
    } else if (announcementBarIsSticky && !headerIsSticky) {
      totalHeaderHeight = announcementBarHeight;
    } else if (!announcementBarIsSticky && headerIsSticky) {
      totalHeaderHeight = window.scrollY === 0 ? headerGroupHeight : headerHeight;
    } else {
      totalHeaderHeight = window.scrollY === 0 ? headerGroupHeight : 0;
    }
    document.documentElement.style.setProperty(
      "--header-group-height-dynamic",
      `${Math.max(0, totalHeaderHeight - 2)}px`
    );
    document.documentElement.style.setProperty(
      "--header-height",
      `${headerHeight}px`
    );
    document.documentElement.style.setProperty(
      "--announcement-bar-height",
      `${announcementBarHeight}px`
    );
  },
  closeBurgerMenu() {
    this.isBurgerMenuOpen = false;
  },
  toggleIsBurgerMenu() {
    this.isBurgerMenuOpen = !this.isBurgerMenuOpen;
    const header2 = document.querySelector(".header");
    if (this.isBurgerMenuOpen) {
      document.body.classList.add("overflow-hidden");
      header2.classList.add("mobile-menu-open");
    } else {
      document.body.classList.remove("overflow-hidden");
      header2.classList.remove("mobile-menu-open");
    }
  },
  toggleDesktopMenu(isActive) {
    const header2 = document.querySelector(".header");
    setTimeout(() => {
      header2.classList.toggle("megamenu-active", isActive);
    }, 40);
  },
  bodyClass() {
    const header2 = document.querySelector(".header");
    const headerType = header2?.dataset.headerType;
    if (headerType) {
      document.body.classList.add(`header_type--${headerType}`);
    }
  }
});
var header_default = header;

// src/scripts/islands/navigation.js
var navigation = (data2 = {
  isMegamenuAccordionOpen: {},
  // Set the default value for isMegamenuAccordionOpen
  isMegamenuActive: false
  // Set the default value for isMegamenuActive
}) => ({
  isMegamenuAccordionOpen: data2.isMegamenuAccordionOpen,
  isMegamenuActive: data2.isMegamenuActive,
  init() {
    this.megamenuAccordionState();
    this.setEventListeners();
  },
  setEventListeners() {
  },
  megamenuAccordionState() {
    for (let key in this.isMegamenuAccordionOpen) {
      this.isMegamenuAccordionOpen[key] = false;
    }
  },
  toggleMegamenuAccordion(title) {
    for (let key in this.isMegamenuAccordionOpen) {
      if (key !== title) {
        this.isMegamenuAccordionOpen[key] = false;
      }
    }
    this.isMegamenuAccordionOpen[title] = !this.isMegamenuAccordionOpen[title];
  }
});
var navigation_default = navigation;

// src/scripts/components/product-card.js
var productCard = {
  getProductCard: async function(productHandle, variantId = null) {
    const variantString = variantId ? `&variant=${variantId}` : "";
    const rawProductCard = await get_section_default({
      sectionId: "product-card",
      uri: `products/${productHandle}?view=card${variantString}`,
      pageAware: false
    });
    const productCard2 = getSectionDOM({
      html: rawProductCard,
      selector: ".product-card"
    });
    return productCard2;
  }
};
var product_card_default = productCard;

// src/scripts/components/product-quickbuy.js
var productQuickbuy = {
  loadQuickBuy: async function(productHandle, variantId = null) {
    const productQuickbuy2 = await this.getProductQuickbuy(
      productHandle,
      variantId
    );
    const quickbuyModal = document.getElementById("quickbuy-content");
    quickbuyModal.innerHTML = productQuickbuy2.body.innerHTML;
  },
  getProductQuickbuy: async function(productHandle, variantId = null) {
    const variantString = variantId ? `&variant=${variantId}` : "";
    const rawProductQuickbuy = await get_section_default({
      sectionId: "product-quickbuy",
      uri: `products/${productHandle}?view=quickbuy${variantString}`,
      pageAware: false
    });
    const productQuickbuy2 = getSectionDOM({
      html: rawProductQuickbuy,
      selector: ".product-quickbuy"
    });
    return productQuickbuy2;
  }
};
var product_quickbuy_default = productQuickbuy;

// src/scripts/utilities/dispatch-hydration.js
var debug2 = false;
function dispatchHydrationEvent(componentInstance, eventName = "component:hydrated") {
  componentInstance.$nextTick(() => {
    const eventDetail = {
      element: componentInstance.$el
      // The root element of the component
    };
    const event = new CustomEvent(eventName, {
      bubbles: true,
      detail: eventDetail
    });
    if (debug2) {
      console.log("Dispatching event:", event);
    }
    componentInstance.$el.dispatchEvent(event);
  });
}

// src/scripts/utilities/product-backorder.js
var toStringValue = (value) => value == null ? "" : String(value);
var parseIsoDate = (value) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  const match = toStringValue(value).trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const [, year, month, day] = match;
  return new Date(
    Date.UTC(Number(year), Number(month) - 1, Number(day), 12, 0, 0)
  );
};
var isoOf = (date) => date.toISOString().slice(0, 10);
var formatUkDate = (date) => {
  const dateObj = date instanceof Date ? date : parseIsoDate(date);
  if (!dateObj) return "";
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC"
  }).format(dateObj);
};
var renderTemplate = (template, variables) => Object.entries(variables || {}).reduce(
  (result, [key, value]) => result.replaceAll(`{${key}}`, toStringValue(value)),
  toStringValue(template)
);
var toCount = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? Math.floor(number) : 0;
};
var computeBackorderState = ({
  config,
  variantId,
  quantityRequested,
  now = /* @__PURE__ */ new Date()
}) => {
  const safeConfig = config || {};
  const variantIdStr = String(variantId);
  const variant = (safeConfig.variants || {})[variantIdStr] || {};
  const requestedQty = Math.max(Number(quantityRequested) || 1, 1);
  const onHandQty = Math.max(Number(variant.stock) || 0, 0);
  const allowBackorder = variant.policy === "continue";
  const isTracked = variant.tracked !== false;
  const incomingQty = toCount(variant.incomingQty);
  const incomingDate = parseIsoDate(variant.incomingDate);
  const isDated = !!incomingDate && isoOf(incomingDate) >= isoOf(now);
  const leadTimeMessage = toStringValue(safeConfig.leadTimeMessage).trim();
  const messages = safeConfig.messages || {};
  const hasCap = incomingQty > 0;
  const totalAvailable = onHandQty + incomingQty;
  const debug5 = {
    state: null,
    reason: "",
    inputs: {
      variantId: variantIdStr,
      requestedQty,
      onHandQty,
      inventoryTracked: isTracked,
      incomingQty,
      incomingDateIso: incomingDate ? isoOf(incomingDate) : null,
      hasLeadTimeMessage: !!leadTimeMessage,
      inventoryPolicy: variant.policy || "(not set)",
      allowBackorder
    }
  };
  const done = (state, reason2, extra) => {
    debug5.state = state;
    debug5.reason = reason2;
    return {
      state,
      message: "",
      isBackorder: false,
      canPurchase: true,
      maxQty: null,
      debug: debug5,
      ...extra
    };
  };
  if (!isTracked) {
    return done("in_stock", "inventory is not tracked, so always purchasable");
  }
  if (requestedQty <= onHandQty) {
    return done(
      "in_stock",
      `requestedQty ${requestedQty} <= onHand ${onHandQty}`
    );
  }
  if (!allowBackorder) {
    return done(
      "unavailable",
      `requestedQty ${requestedQty} > onHand ${onHandQty} and inventory policy is not continue`,
      {
        message: toStringValue(messages.outOfStock),
        canPurchase: false,
        maxQty: onHandQty
      }
    );
  }
  if (hasCap && requestedQty > totalAvailable) {
    return done(
      "insufficient",
      `requestedQty ${requestedQty} > onHand ${onHandQty} + incoming ${incomingQty}`,
      {
        message: renderTemplate(messages.insufficient, {
          available: totalAvailable
        }),
        canPurchase: false,
        maxQty: totalAvailable
      }
    );
  }
  const template = isDated ? messages.backorderDated : leadTimeMessage || messages.backorderUndated;
  let reason = "backorder with no incoming date \u2014 using the generic lead-time message";
  if (isDated) {
    reason = `backorder against incoming PO of ${incomingQty || "unstated"} due ${debug5.inputs.incomingDateIso}`;
  } else if (incomingDate) {
    reason = `incoming date ${debug5.inputs.incomingDateIso} has passed \u2014 using the generic lead-time message`;
  }
  return done("backorder", reason, {
    message: renderTemplate(template, { date: formatUkDate(incomingDate) }),
    isBackorder: true,
    maxQty: hasCap ? totalAvailable : null
  });
};

// src/scripts/islands/product.js
var product = (data2 = {
  productId: null,
  productHandle: null,
  sectionId: null,
  productVariants: null,
  selectedVariant: null,
  isQuickBuy: false,
  isProductCard: false,
  lastViewed: null,
  isLoading: false,
  isHighVariant: false,
  requiresAgeVerification: false
}) => ({
  sectionPrefix: "shopify-section-",
  sectionId: data2.sectionId,
  isInitialLoad: true,
  isProductCard: data2.isProductCard,
  isQuickBuy: data2.isQuickBuy,
  isLoading: data2.isLoading,
  requiresAgeVerification: data2.requiresAgeVerification ?? false,
  lastViewed: Alpine.store("lastViewed"),
  productId: data2.productId,
  productHandle: data2.productHandle,
  // Init-only properties
  productVariants: [],
  currentVariant: {},
  variantSelector: null,
  variantId: null,
  // >= 250 variants: resolved server-side. See docs/high-variant-support.md.
  isHighVariant: data2.isHighVariant || false,
  optionValues: [],
  resolvedVariant: null,
  resolveSequence: 0,
  specSequence: 0,
  isResolving: false,
  quantityToAdd: 1,
  option1: null,
  option2: null,
  option3: null,
  available: null,
  isCombinationUnavailable: false,
  // Backorder / incoming-PO state. See docs/backorder-lead-times.md.
  backorderConfig: null,
  backorder: null,
  // Keyed by variant id, so switching variant needs no second /cart.js read.
  cartQuantities: {},
  price: null,
  compareAtPrice: null,
  // Product images zoom data
  zoomOpen: false,
  zoomSrc: "",
  zoomAlt: "",
  zoomScale: 1,
  init() {
    this.getProductData();
    this.getView();
    this.setEventListeners();
    this.setInitialOptions();
    this.setCurrentVariant(this.currentVariant.id);
    this.$watch("variantSelector", () => this.onVariantChange());
    ["option1", "option2", "option3"].forEach(
      (option) => this.$watch(option, () => this.onOptionChange())
    );
    this.$watch("quantityToAdd", (value) => {
      this.updateBackorder();
      this.$dispatch(`swanky:product${this.productId}:quantity-changed`, {
        quantity: value,
        sectionId: this.sectionId
      });
    });
    if (this.backorderConfig) {
      this.refreshCartQty();
      window.addEventListener("cart-updated", () => this.refreshCartQty());
    }
    this.onVariantChange();
    dispatchHydrationEvent(this);
  },
  setEventListeners() {
    window.addEventListener(
      `swanky:product${this.productId}:quantity-changed`,
      (event) => {
        if (event.detail.sectionId != this.sectionId) {
          this.quantityToAdd = event.detail.quantity;
        }
      }
    );
  },
  getProductData() {
    const variantsScript = this.$el.querySelector(
      '[data-product-json="variants"]'
    );
    const selectedScript = this.$el.querySelector(
      '[data-product-json="selected-variant"]'
    );
    if (variantsScript) {
      try {
        this.productVariants = JSON.parse(variantsScript.textContent);
      } catch (e) {
        console.error(
          `Error parsing product variants for product ${this.productId}`,
          e
        );
        this.productVariants = [];
      }
    } else {
      console.warn(`No variants JSON found for product ${this.productId}`);
      this.productVariants = [];
    }
    if (selectedScript) {
      try {
        this.currentVariant = JSON.parse(selectedScript.textContent);
      } catch (e) {
        console.error(
          `Error parsing selected variant for product ${this.productId}`,
          e
        );
        this.currentVariant = this.productVariants[0] || {};
      }
    } else {
      this.currentVariant = this.productVariants[0] || {};
    }
    this.syncOptionValues(this.$el);
    this.backorderConfig = this.parseSectionJSON(this.$el, "backorder");
    this.resolvedVariant = this.parseSectionJSON(this.$el, "resolved-variant");
    if (this.isHighVariant && this.hasVariantSelectionParam() && this.resolvedVariant?.id) {
      this.currentVariant = this.resolvedVariant;
    }
    this.selectedVariant = this.currentVariant;
    if (!this.currentVariant.id) {
      console.warn(`No current variant found for product ${this.productId}`);
    }
  },
  getView() {
    if (this.isProductCard) return "card";
    if (this.isQuickBuy) return "quickbuy";
    return null;
  },
  get sectionElement() {
    if (this.isProductCard) {
      return document.querySelector(
        `[data-product-card-id="${this.productId}"]`
      );
    } else {
      return document.getElementById(`${this.sectionPrefix}${this.sectionId}`);
    }
  },
  addToCart() {
    this.isLoading = true;
    let formData = new FormData();
    formData.append("id", this.variantId);
    formData.append("quantity", this.quantityToAdd);
    const proceed = () => {
      Cart.addToCart(formData).then(() => {
        this.isLoading = false;
      }).catch(() => {
        this.isLoading = false;
      });
    };
    if (this.requiresAgeVerification) {
      console.log(this.requiresAgeVerification);
      if (window.location.pathname !== "/cart") {
        window.dispatchEvent(new CustomEvent("cart-drawer:temporarily-close"));
      }
      document.querySelectorAll("dialog[open]").forEach((d) => d.close());
      document.body.style.overflow = "";
      let resolved = false;
      const onClose = () => {
        if (!resolved) this.isLoading = false;
      };
      window.addEventListener("age-verification:closed", onClose, {
        once: true
      });
      requestAnimationFrame(() => {
        window.dispatchEvent(
          new CustomEvent("age-verification:require-confirmation", {
            detail: {
              callback: () => {
                resolved = true;
                window.removeEventListener("age-verification:closed", onClose);
                proceed();
              }
            }
          })
        );
      });
      return;
    }
    proceed();
  },
  optionUpdate(option, value) {
    this[option] = value;
  },
  onOptionChange() {
    const variantId = this.getMasterId();
    if (variantId) {
      this.setCurrentVariant(variantId);
      return;
    }
    if (this.isHighVariant) {
      if (this.optionsMatchCurrentVariant()) return;
      this.resolveVariantViaOptionValues();
      return;
    }
    this.setCurrentVariant(null);
  },
  onVariantChange() {
    this.variantId = this.variantSelector ? Number(this.variantSelector) : null;
    this.currentVariant = this.variantId ? this.findVariant(this.variantId) : null;
    if (this.currentVariant) {
      this.setAvailabilty();
      if (!this.isInitialLoad) {
        if (this.isProductCard) {
          this.renderProductCard();
        } else if (this.isQuickBuy) {
          this.renderProductQuickBuy();
        } else {
          this.setURL();
          this.renderProductInfo();
        }
      }
      this.isInitialLoad = false;
      this.updateCheckedOptions();
    } else {
      this.setUnavilable();
    }
    this.dispatchEvents();
  },
  updateCheckedOptions() {
    if (!this.currentVariant) return;
    const options = ["option1", "option2", "option3"];
    options.forEach((option) => {
      if (this.currentVariant[option]) {
        this[option] = this.currentVariant[option];
      }
    });
  },
  isOptionAvailableForPosition(position, value) {
    if (this.isHighVariant) {
      const optionValue = this.findOptionValue(position, value);
      if (!optionValue) return true;
      return Boolean(optionValue.exists || optionValue.available);
    }
    return this.productVariants.some(
      (variant) => variant.options.every((opt, i) => {
        const pos = i + 1;
        if (pos === position) return opt === value;
        if (pos < position) return opt === this[`option${pos}`];
        return true;
      })
    );
  },
  dispatchEvents() {
    this.$dispatch(`swanky:product${this.productId}:variant-changed`, {
      productId: this.productId,
      variantId: this.variantId,
      available: this.available,
      isCombinationUnavailable: this.isCombinationUnavailable,
      isResolving: this.isResolving,
      backorder: this.backorder
    });
  },
  getVariantById(id) {
    return this.productVariants.find((variant) => {
      return variant.id === id;
    });
  },
  findVariant(id) {
    const variant = this.getVariantById(id);
    if (variant) return variant;
    if (this.isHighVariant && this.resolvedVariant?.id === id) {
      return this.resolvedVariant;
    }
    return null;
  },
  findOptionValue(position, value) {
    return this.optionValues.find(
      (optionValue) => optionValue.position === position && optionValue.value === value
    );
  },
  hasVariantSelectionParam() {
    const params = new URLSearchParams(window.location.search);
    return params.has("variant") || params.has("option_values");
  },
  optionsMatchCurrentVariant() {
    if (!this.currentVariant) return false;
    return [1, 2, 3].every(
      (position) => (this[`option${position}`] || null) === (this.currentVariant[`option${position}`] || null)
    );
  },
  selectedOptionValueIds() {
    return [1, 2, 3].map((position) => {
      const value = this[`option${position}`];
      if (!value) return null;
      return this.findOptionValue(position, value)?.id ?? null;
    }).filter((id) => id !== null);
  },
  resolveVariantViaOptionValues() {
    const optionValueIds = this.selectedOptionValueIds();
    if (!optionValueIds.length) return;
    const sequence = ++this.resolveSequence;
    this.isResolving = true;
    this.dispatchEvents();
    getSection({
      sectionId: this.sectionId,
      uri: `?option_values=${optionValueIds.join(",")}`,
      pageAware: true,
      view: this.getView()
    }).then((returnedSectionAsString) => {
      if (sequence !== this.resolveSequence) return;
      const informationHTML = new DOMParser().parseFromString(
        returnedSectionAsString,
        "text/html"
      );
      const resolvedVariant = this.parseSectionJSON(
        informationHTML,
        "resolved-variant"
      );
      this.resolvedVariant = resolvedVariant;
      this.variantId = resolvedVariant ? resolvedVariant.id : null;
      this.currentVariant = resolvedVariant;
      this.syncOptionValues(informationHTML);
      this.mergeBackorderConfig(informationHTML);
      if (resolvedVariant) {
        this.setAvailabilty();
        this.applySectionHTML(informationHTML, this.productInfoTargets());
        this.setURL();
        this.updateCheckedOptions();
        this.refreshSpecifications();
      } else {
        this.setUnavilable();
      }
      this.isResolving = false;
      this.dispatchEvents();
    }).catch((error2) => {
      if (sequence !== this.resolveSequence) return;
      console.error(
        `Error resolving option values for product ${this.productId}`,
        error2
      );
      this.isResolving = false;
      this.dispatchEvents();
    });
  },
  // Past 250 variants the initial config cannot contain the server-resolved
  // variant. Fold in the re-rendered section's entry rather than replacing the
  // map, so previously resolved variants stay cached.
  mergeBackorderConfig(root) {
    if (!this.backorderConfig) return;
    const incoming = this.parseSectionJSON(root, "backorder");
    if (!incoming?.variants) return;
    this.backorderConfig = {
      ...this.backorderConfig,
      ...incoming,
      variants: { ...this.backorderConfig.variants, ...incoming.variants }
    };
  },
  parseSectionJSON(root, key) {
    const script = root.querySelector(`[data-product-json="${key}"]`);
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      console.error(
        `Error parsing ${key} JSON for product ${this.productId}`,
        e
      );
      return null;
    }
  },
  syncOptionValues(root) {
    const optionValues = this.parseSectionJSON(root, "option-values");
    if (optionValues) this.optionValues = optionValues;
  },
  getMasterId() {
    const variant = this.productVariants.find((variant2) => {
      return !variant2.options.map((option, index) => {
        return this[`option${index + 1}`] === option;
      }).includes(false);
    });
    if (variant) {
      return variant.id;
    } else {
      return null;
    }
  },
  setInitialOptions() {
    this.updateCheckedOptions();
  },
  setCurrentVariant(variantId) {
    this.variantId = variantId;
    this.variantSelector = this.variantId;
  },
  setQuantity(number) {
    const next = Number(this.quantityToAdd) + Number(number);
    const maxAddable = this.backorderMaxAddable;
    if (maxAddable != null && next > maxAddable && this.backorder) {
      this.$store.errors.add(this.backorderCapText);
    }
    this.quantityToAdd = maxAddable == null ? Math.max(1, next) : Math.min(Math.max(1, next), Math.max(1, maxAddable));
  },
  setUnavilable() {
    this.available = false;
    this.isCombinationUnavailable = true;
    this.updateBackorder();
  },
  setAvailabilty() {
    this.available = this.currentVariant.available;
    this.isCombinationUnavailable = false;
    this.updateBackorder();
  },
  // ── Backorder / incoming PO ───────────────────────────────────────────────
  // "Continue selling when out of stock" has no ceiling, so the cap is enforced
  // here and at every other quantity surface. A UX guard, not a guarantee: a
  // direct /cart/add POST bypasses it and Elucid stays the authority.
  updateBackorder() {
    if (!this.backorderConfig || !this.variantId) {
      this.backorder = null;
      this.dispatchBackorder();
      return;
    }
    this.backorder = computeBackorderState({
      config: this.backorderConfig,
      variantId: this.variantId,
      quantityRequested: (Number(this.quantityToAdd) || 1) + this.cartQtyForVariant
    });
    this.dispatchBackorder();
  },
  // Its own event, not variant-changed: purchase-options clears the selected
  // selling plan on that one and the gallery re-homes to the variant slide.
  dispatchBackorder() {
    this.$dispatch(`swanky:product${this.productId}:backorder-changed`, {
      backorder: this.backorder
    });
  },
  async refreshCartQty() {
    if (!this.backorderConfig) return;
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`);
      if (!response.ok) throw new Error(`cart.js returned ${response.status}`);
      const cart = await response.json();
      this.cartQuantities = (cart.items || []).reduce((totals, item) => {
        totals[item.variant_id] = (totals[item.variant_id] ?? 0) + item.quantity;
        return totals;
      }, {});
    } catch (error2) {
      console.error(
        `Could not read cart quantities for ${this.productId}`,
        error2
      );
      this.cartQuantities = {};
    }
    this.updateBackorder();
  },
  // The cap is on the total held, not on a single add.
  get cartQtyForVariant() {
    return this.cartQuantities[this.variantId] ?? 0;
  },
  // Plain text for the error list, which does not render the richtext markup.
  get backorderCapText() {
    const html = this.backorderConfig.messages.insufficient.replace(
      "{available}",
      this.backorder.maxQty
    );
    return new DOMParser().parseFromString(html, "text/html").body.textContent;
  },
  // Null while the engine says in stock (no message), which leaves the status line to the
  // inventory notice's own states. Otherwise the message split into its bold
  // lead-in and the rest.
  get leadTime() {
    if (!this.backorder?.message) return null;
    const doc = new DOMParser().parseFromString(
      this.backorder.message,
      "text/html"
    );
    const label = doc.querySelector("strong")?.textContent.trim() ?? "";
    const text = doc.body.textContent.replace(label, "").replace(/^[\s\-–—|:]+/, "").trim();
    return { state: this.backorder.state, label, text };
  },
  // null when uncapped.
  get backorderMaxAddable() {
    const maxQty = this.backorder?.maxQty;
    if (maxQty == null) return null;
    return Math.max(0, maxQty - this.cartQtyForVariant);
  },
  setURL() {
    const newURL = `${window.location.origin}${window.location.pathname}?variant=${this.variantId}`;
    window.history.replaceState({}, "", newURL);
  },
  // FUTUREFOCUS: This might be worth moving to a utility file
  getinformationHTML(sectionId, elementsToUpdate) {
    getSection({
      sectionId,
      uri: `?variant=${this.variantId}`,
      pageAware: true,
      view: this.getView()
    }).then((returnedSectionAsString) => {
      const informationHTML = new DOMParser().parseFromString(
        returnedSectionAsString,
        "text/html"
      );
      this.applySectionHTML(informationHTML, elementsToUpdate);
      this.syncOptionValues(informationHTML);
    });
  },
  applySectionHTML(newHTML, elementsToUpdate) {
    elementsToUpdate.map((element) => {
      reRenderSectionComponent({
        selector: element,
        section: this.sectionElement,
        newHTML
      });
    });
  },
  openQuickBuy() {
    product_quickbuy_default.loadQuickBuy(this.productHandle, this.variantId).then(() => {
      this.$dispatch("quickbuy-toggle");
    });
  },
  getQuickbuyHTML(elementsToUpdate) {
    product_quickbuy_default.getProductQuickbuy(this.productHandle, this.variantId).then((productQuickbuyHTML) => {
      elementsToUpdate.map((element) => {
        reRenderSectionComponent({
          selector: element,
          section: this.sectionElement,
          newHTML: productQuickbuyHTML
        });
      });
    });
  },
  getCardHTML(cardElement, elementsToUpdate) {
    product_card_default.getProductCard(this.productHandle, this.variantId).then((productCardHTML) => {
      elementsToUpdate.map((element) => {
        reRenderSectionComponent({
          selector: element,
          section: cardElement,
          newHTML: productCardHTML
        });
      });
    });
  },
  productInfoTargets() {
    return [
      "product-gallery-grid",
      ".product-badges",
      ".product-guarantee-badge-wrapper",
      ".price",
      ".information__sku",
      ".description",
      ".product-buy-buttons",
      ".pickup__availability",
      "share-button",
      ".product__inventory-notice"
    ];
  },
  renderProductInfo() {
    this.getinformationHTML(this.sectionId, this.productInfoTargets());
    this.refreshSpecifications();
  },
  // The spec table is in another section, so productInfoTargets cannot reach it.
  refreshSpecifications() {
    const table = document.querySelector("[data-spec-section]");
    if (!table) return;
    const sequence = ++this.specSequence;
    getSection({
      sectionId: table.dataset.specSection,
      uri: `?variant=${this.variantId}`,
      pageAware: true
    }).then((returnedSectionAsString) => {
      if (sequence !== this.specSequence) return;
      const specHTML = new DOMParser().parseFromString(
        returnedSectionAsString,
        "text/html"
      );
      const source = specHTML.querySelector("[data-spec-section]");
      const target = document.querySelector("[data-spec-section]");
      if (source && target) target.innerHTML = source.innerHTML;
    }).catch((error2) => {
      console.error(
        `Error refreshing specifications for product ${this.productId}`,
        error2
      );
    });
  },
  renderProductCard() {
    const cardElement = this.$el;
    const elementsToUpdate = [
      ".card__media",
      ".card__title",
      ".card__reviews",
      ".card__price",
      ".product-badges"
    ];
    this.getCardHTML(cardElement, elementsToUpdate);
  },
  renderProductQuickBuy() {
    const elementsToUpdate = [
      ".quickbuy__content",
      ".quickbuy__buy",
      ".quickbuy__details"
    ];
    this.getQuickbuyHTML(elementsToUpdate);
  },
  openZoom({ zoomSrc, zoomAlt }) {
    if (!zoomSrc) return console.warn("No image src provided to zoom");
    const img = new Image();
    img.src = zoomSrc;
    this.$nextTick(() => {
      this.zoomSrc = zoomSrc;
      this.zoomAlt = zoomAlt || "";
      this.zoomOpen = true;
      document.body.style.overflow = "hidden";
    });
    img.onerror = () => {
      console.warn("Failed to load zoom image:", zoomSrc);
    };
  },
  closeZoom() {
    this.zoomOpen = false;
    this.zoomSrc = "";
    this.zoomAlt = "";
    document.body.style.overflow = "";
  },
  toggleScale() {
    this.zoomScale = this.zoomScale === 1 ? 2 : 1;
    const overlay = this.$refs.zoomOverlay;
    overlay.classList.toggle("image--zoomed", this.zoomScale === 2);
    this.initDrag();
  },
  initDrag() {
    const clamp3 = (v, min3, max3) => Math.min(Math.max(v, min3), max3);
    let startX, startY, currentX = 0, currentY = 0;
    const resistance = 0.5;
    const image = this.$refs.zoomImage;
    const maxDrag = 100;
    const minDrag = -maxDrag;
    const onTouchStart = (e) => {
      if (this.zoomScale !== 2) return;
      const touch = e.touches[0];
      startX = touch.clientX - currentX;
      startY = touch.clientY - currentY;
      image.style.transition = "none";
    };
    const onTouchMove = (e) => {
      if (this.zoomScale !== 2) return e.preventDefault();
      const touch = e.touches[0];
      currentX = clamp3((touch.clientX - startX) * resistance, minDrag, maxDrag);
      currentY = clamp3((touch.clientY - startY) * resistance, minDrag, maxDrag);
      image.style.transform = `scale(${this.zoomScale}) translate(${currentX}px, ${currentY}px)`;
    };
    const onTouchEnd = () => {
      if (this.zoomScale !== 2) return;
      image.style.transition = "transform 0.3s ease";
    };
    image.addEventListener("touchstart", onTouchStart);
    image.addEventListener("touchmove", onTouchMove);
    image.addEventListener("touchend", onTouchEnd);
  }
});
var product_default = product;

// src/scripts/islands/buy-buttons.js
var buyButtons = (data2 = {
  qty: 1,
  productId: null,
  isGiftCard: false,
  variantId: false,
  isAvailable: true,
  isCombinationUnavailable: false,
  isLoading: false
}) => ({
  qty: data2.qty,
  productId: data2.productId,
  productTitle: data2.productTitle,
  isGiftCard: data2.isGiftCard,
  variantId: data2.variantId,
  isAvailable: data2.isAvailable,
  isCombinationUnavailable: data2.isCombinationUnavailable,
  isLoading: data2.isLoading,
  requiresAgeVerification: data2.requiresAgeVerification ?? false,
  isResolving: false,
  // Null when the stock status block is not on the template.
  backorder: null,
  // Drives hiding the dynamic checkout buttons. See isBypassingWarranty.
  hasWarrantySelected: false,
  init() {
    this.hasWarrantySelected = this.selectedWarrantyIds().length > 0;
    document.addEventListener("change", (event) => {
      if (!event.target?.matches?.(`input[data-warranty-for="${this.productId}"]`)) {
        return;
      }
      this.hasWarrantySelected = this.selectedWarrantyIds().length > 0;
    });
    window.addEventListener(
      `swanky:product${this.productId}:variant-changed`,
      (event) => {
        this.variantId = event.detail.variantId;
        this.isAvailable = event.detail.available ?? false;
        this.isCombinationUnavailable = event.detail.isCombinationUnavailable ?? false;
        this.isResolving = event.detail.isResolving ?? false;
        this.backorder = event.detail.backorder ?? null;
      }
    );
    window.addEventListener(
      `swanky:product${this.productId}:backorder-changed`,
      (event) => {
        this.backorder = event.detail.backorder ?? null;
      }
    );
    this.interceptBuyNow();
  },
  interceptBuyNow() {
    if (window.__ageVerificationBuyNowBound) return;
    window.__ageVerificationBuyNowBound = true;
    let bypassAgeCheck2 = false;
    window.addEventListener("age-verification:closed", () => {
      bypassAgeCheck2 = false;
    });
    window.addEventListener("age-verification:cancelled", () => {
      this.isLoading = false;
      bypassAgeCheck2 = false;
    });
    window.addEventListener("age-verification:confirmed", () => {
      bypassAgeCheck2 = true;
      requestAnimationFrame(() => {
        bypassAgeCheck2 = false;
      });
    });
    document.addEventListener(
      "click",
      (e) => {
        const button = e.target.closest(".shopify-payment-button__button");
        if (!button) return;
        if (bypassAgeCheck2) return;
        const container = button.closest("[data-requires-age-verification]");
        const requiresVerification = container?.dataset.requiresAgeVerification === "true";
        if (!requiresVerification) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        window.dispatchEvent(
          new CustomEvent("age-verification:require-confirmation", {
            detail: {
              callback: () => {
                bypassAgeCheck2 = true;
                requestAnimationFrame(() => {
                  const event = new MouseEvent("click", {
                    view: window,
                    bubbles: true,
                    cancelable: true
                  });
                  button.dispatchEvent(event);
                  requestAnimationFrame(() => {
                    bypassAgeCheck2 = false;
                  });
                });
              }
            }
          })
        );
      },
      true
    );
  },
  get isBlockedByBackorder() {
    return this.backorder ? this.backorder.canPurchase === false : false;
  },
  // Over the cap the button stays live so the click can explain itself in the
  // error toast, the way Shopify's own stock error does; disabled only when
  // nothing at all can be added.
  get isBackorderSoldOut() {
    return this.isBlockedByBackorder && !(this.backorder.maxQty > 0);
  },
  // The dynamic checkout buttons post the product form straight to checkout,
  // so addToCart never runs and the selected cover is silently dropped — the
  // customer pays without the thing they asked for. Shopify's buttons take a
  // single line item and cannot carry the warranty, so the only honest option
  // is to hide them and route the sale through add to basket.
  get isBypassingWarranty() {
    return this.hasWarrantySelected;
  },
  cartData() {
    const form = this.$refs.product_form || document.querySelector('[x-ref="product_form"]');
    return new FormData(form);
  },
  validateGiftCardForm() {
    const form = this.$refs.product_form;
    const requiredFields = form.querySelectorAll("[required]");
    let isValid = true;
    requiredFields.forEach((field) => {
      const valid = field.checkValidity();
      field.classList.toggle("field--error", !valid);
      if (!valid) isValid = false;
    });
    const sendDateField = form.querySelector('[name="properties[Send on]"]');
    if (sendDateField && sendDateField.value) {
      const selected = new Date(sendDateField.value);
      const today = /* @__PURE__ */ new Date();
      const maxDate = /* @__PURE__ */ new Date();
      maxDate.setDate(maxDate.getDate() + 90);
      if (selected > maxDate) {
        isValid = false;
        sendDateField.classList.add("field--error");
        sendDateField.setCustomValidity(
          "Gift cards can only be scheduled up to 90 days in advance."
        );
      } else {
        sendDateField.setCustomValidity("");
      }
    }
    if (!isValid) {
      const firstInvalid = form.querySelector(":invalid");
      if (firstInvalid) {
        firstInvalid.focus();
        firstInvalid.reportValidity?.();
      }
    }
    return isValid;
  },
  // Warranty and service-plan radios rendered by the warranty options block.
  // Scoped by product id rather than by the form: `content_for 'blocks'`
  // renders every PDP block as a flat sibling and the form lives inside
  // `_add-to-cart`, so a warranty block ordered after it is outside the form
  // and neither FormData nor a form-scoped query can see it.
  //
  // Filtering drops the "no cover" radio, whose value is empty.
  selectedWarrantyIds() {
    return Array.from(
      document.querySelectorAll(
        `input[data-warranty-for="${this.productId}"]:checked`
      ),
      (input) => Number(input.value)
    ).filter(Boolean);
  },
  addToCart() {
    if (this.isResolving) return;
    if (this.isBlockedByBackorder) {
      this.$store.errors.add(this.backorderCapText);
      return;
    }
    const requiresVerification = this.requiresAgeVerification;
    this.isLoading = true;
    const valid = this.isGiftCard ? this.validateGiftCardForm() : true;
    if (!valid) {
      this.isLoading = false;
      return;
    }
    const proceed = () => {
      const done = () => {
        this.isLoading = false;
      };
      const warrantyIds = this.selectedWarrantyIds();
      const isBackorder = this.backorder?.isBackorder === true;
      const qty = Number(this.qty) || 1;
      const inStockQty = isBackorder ? Math.min(
        qty,
        Math.max(
          0,
          this.backorder.debug.inputs.onHandQty - this.cartQtyForVariant
        )
      ) : 0;
      const isSplit = inStockQty > 0;
      if (!warrantyIds.length && !isSplit) {
        const data3 = this.cartData();
        if (isBackorder) data3.append("properties[_backorder]", "true");
        Cart.addToCart(data3).then(done).catch(done);
        return;
      }
      const variantId = Number(this.variantId);
      const items = [
        ...isSplit ? [{ id: variantId, quantity: inStockQty }] : [],
        {
          id: variantId,
          quantity: qty - inStockQty,
          ...isBackorder ? { properties: { _backorder: "true" } } : {}
        },
        ...warrantyIds.map((id) => ({
          id,
          quantity: qty,
          // Underscore prefix keeps these off the customer-facing cart line
          // while still reaching the order, so fulfilment/admin can tie cover
          // to its product - _covers_variant for exact matching, and the
          // readable _covers_product since a bare variant id means nothing
          // when scanning an order in Shopify Admin.
          properties: {
            _covers_variant: String(this.variantId),
            _covers_product: this.productTitle
          }
        }))
      ];
      Cart.addItems(items).then(done).catch(done);
    };
    if (requiresVerification) {
      if (document.querySelector("dialog.quickbuy-drawer[open]")) {
        window.dispatchEvent(new Event("quickbuy-toggle"));
      }
      if (window.location.pathname !== "/cart") {
        window.dispatchEvent(new CustomEvent("cart-drawer:temporarily-close"));
      }
      document.querySelectorAll("dialog[open]").forEach((d) => d.close());
      document.body.style.overflow = "";
      let resolved = false;
      const onClose = () => {
        if (!resolved) this.isLoading = false;
      };
      window.addEventListener("age-verification:closed", onClose, {
        once: true
      });
      requestAnimationFrame(() => {
        window.dispatchEvent(
          new CustomEvent("age-verification:require-confirmation", {
            detail: {
              callback: () => {
                resolved = true;
                window.removeEventListener("age-verification:closed", onClose);
                proceed();
              }
            }
          })
        );
      });
      return;
    }
    proceed();
  }
});
var buy_buttons_default = buyButtons;

// src/scripts/islands/custom-range.js
var customRange = (data2 = {
  minLimit: null,
  min: null,
  max: null,
  maxLimit: null,
  step: null
}) => ({
  minLimit: Number.parseInt(data2.minLimit),
  min: Number.parseInt(data2.min),
  max: Number.parseInt(data2.max),
  maxLimit: Number.parseInt(data2.maxLimit),
  step: Number.parseInt(data2.step),
  sendPriceRange() {
    this.$dispatch("price-range-changed", { min: this.min, max: this.max });
  },
  updateMin(value) {
    this.min = Math.min(value, this.max - this.step);
  },
  updateMax(value) {
    this.max = Math.max(value, this.min + this.step);
  }
});
var custom_range_default = customRange;

// src/scripts/islands/note.js
var debug3 = false;
var note = (data2 = {
  noteAccordion: false,
  note: ""
}) => ({
  noteAccordion: data2.noteAccordion,
  note: data2.note,
  toggleAccordion() {
    this.noteAccordion = !this.noteAccordion;
  },
  closeAccordion() {
    this.noteAccordion = false;
  },
  async saveNote() {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await fetch("/cart/update.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json"
          },
          body: JSON.stringify({
            note: this.note
          })
        });
        resolve(await result.json());
        if (!result.ok) {
          Alpine.store("errors").add(result.message);
        }
      } catch (error2) {
        reject(error2);
      }
      if (debug3) {
        console.log("Note Logic sent");
        console.log("Note:" + this.note);
      }
    });
  },
  saveNoteAndClose() {
    this.saveNote();
    this.closeAccordion();
  }
});
var note_default = note;

// src/scripts/islands/purchase-options.js
var purchaseOptions = (data2 = {
  sellingPlan: null,
  required: null,
  error: null,
  productId: null
}) => ({
  productId: data2.productId,
  error: data2.error,
  required: data2.required,
  sellingPlan: data2.sellingPlan,
  init() {
    window.addEventListener(
      `swanky:product${this.productId}:variant-changed`,
      async (e) => {
        this.selectPlan(null);
      }
    );
  },
  disableForm() {
    const form = this.$refs.product_form;
    if (!form) return;
    const submitButton = form.querySelector('[type="submit"]');
    if (!submitButton) return;
    if (this.required && !this.sellingPlan) {
      submitButton.disabled = true;
      submitButton.classList.add("disabled");
    } else {
      submitButton.disabled = false;
      submitButton.classList.remove("disabled");
    }
  },
  selectPlan(planId) {
    this.sellingPlan = planId;
  }
});
var purchase_options_default = purchaseOptions;

// src/scripts/islands/inventory-notice.js
var debug4 = false;
function parseLabelledMessage(html, fallbackLabel) {
  if (!html) return { label: fallbackLabel, text: "" };
  const doc = new DOMParser().parseFromString(html, "text/html");
  const strong = doc.querySelector("strong");
  if (!strong) {
    return { label: fallbackLabel, text: doc.body.textContent.trim() };
  }
  const label = strong.textContent.trim();
  const text = doc.body.textContent.replace(label, "").replace(/^[\s\-–—|:]+/, "").trim();
  return { label, text };
}
var inventoryNotice = (data2 = {}) => ({
  now: data2.now,
  preorderDate: data2.preorderDate,
  shippingDate: data2.shippingDate,
  isPreorder: data2.isPreorder,
  available: data2.available,
  // messages
  preorderActiveMessage: data2.preorderActiveMessage,
  preorderAvailableMessage: data2.preorderAvailableMessage,
  inStockMessage: "",
  defaultInStockLabel: "",
  outOfStockMessage: data2.outOfStockMessage,
  state: "loading",
  message: "",
  label: "",
  init() {
    if (debug4) {
      console.log("Inventory Notice Init:", {
        now: this.now,
        preorderDate: this.preorderDate,
        shippingDate: this.shippingDate,
        isPreorder: this.isPreorder,
        available: this.available
      });
    }
    const configEl = this.$el.querySelector(
      "[data-inventory-notice-config]"
    );
    if (configEl) {
      try {
        const config = JSON.parse(configEl.textContent);
        this.inStockMessage = config.inStockMessage;
        this.defaultInStockLabel = config.defaultInStockLabel;
      } catch (e) {
        console.error("[InventoryNotice] Invalid config JSON", e);
      }
    }
    this.evaluate();
    window.addEventListener(
      `swanky:product${this.productId}:variant-changed`,
      (event) => {
        const variant = event.detail.variant;
        if (!variant) return;
        this.available = variant.available;
        if (variant.preorder_date) {
          this.preorderDate = variant.preorder_date;
        }
        if (variant.shipping_date) {
          this.shippingDate = variant.shipping_date;
        }
        if (debug4) {
          console.log("Variant updated (Alpine):", variant);
        }
        this.evaluate();
      }
    );
    this._intervalId = setInterval(() => {
      this.now = Math.floor(Date.now() / 1e3);
      this.evaluate();
    }, 1e3);
  },
  destroy() {
    clearInterval(this._intervalId);
  },
  evaluate() {
    const canPreorder = this.available;
    if (!this.preorderDate || !this.shippingDate) {
      if (this.available) {
        this.setInStock();
      } else {
        this.state = "oos";
        this.message = this.outOfStockMessage;
      }
      return;
    }
    if (this.now >= this.shippingDate) {
      this.state = "oos";
      this.message = this.outOfStockMessage;
      return;
    }
    if (this.isPreorder && this.now < this.preorderDate) {
      this.state = "coming";
      this.message = this.preorderAvailableMessage;
      return;
    }
    if (this.isPreorder && this.now >= this.preorderDate && this.now < this.shippingDate && canPreorder) {
      this.state = "preorder";
      this.message = this.preorderActiveMessage;
      return;
    }
    if (!this.available || !canPreorder) {
      this.state = "oos";
      this.message = this.outOfStockMessage;
      return;
    }
    this.setInStock();
  },
  setInStock() {
    this.state = "instock";
    const { label, text } = parseLabelledMessage(
      this.inStockMessage,
      this.defaultInStockLabel
    );
    this.label = label;
    this.message = text;
  }
});
var inventory_notice_default = inventoryNotice;

// src/scripts/islands/age-verification-modal.js
var ageVerificationModal = (data2 = {}) => ({
  open: false,
  mode: "info",
  pendingAddToCart: null,
  lastFocusedElement: null,
  openInfo() {
    this.lastFocusedElement = document.activeElement;
    this.mode = "info";
    this.open = true;
    this.$nextTick(() => {
      this.focusFirstElement();
    });
  },
  requireConfirmation(callback) {
    this.lastFocusedElement = document.activeElement;
    this.mode = "add_to_cart";
    this.pendingAddToCart = callback;
    this.open = true;
    this.$nextTick(() => {
      this.focusFirstElement();
    });
  },
  confirm() {
    if (this.mode === "add_to_cart") {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      document.body.focus?.();
    }
    if (this.mode === "add_to_cart" && this.pendingAddToCart) {
      this.pendingAddToCart();
      this.pendingAddToCart = null;
    }
    window.dispatchEvent(new CustomEvent("age-verification:confirmed"));
    this.close({
      restoreFocus: this.mode !== "add_to_cart",
      cancelled: false
    });
  },
  close({ restoreFocus = true, cancelled = false } = {}) {
    this.open = false;
    this.pendingAddToCart = null;
    window.dispatchEvent(new CustomEvent("age-verification:closed"));
    if (cancelled) {
      window.dispatchEvent(new CustomEvent("age-verification:cancelled"));
    }
  },
  getFocusableElements() {
    if (!this.$refs.dialog) return [];
    return Array.from(
      this.$refs.dialog.querySelectorAll(
        'a[href], area[href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [contenteditable], [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => {
      return el.offsetParent !== null && !el.hasAttribute("disabled");
    });
  },
  focusFirstElement() {
    const focusable = this.getFocusableElements();
    if (focusable.length) {
      focusable[0].focus();
    } else if (this.$refs.dialog) {
      this.$refs.dialog.focus();
    }
  },
  trapFocus(event) {
    if (!this.open || event.key !== "Tab") return;
    const focusable = this.getFocusableElements();
    if (!focusable.length) {
      event.preventDefault();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  },
  handleKeydown(event) {
    if (!this.open) return;
    if (event.key === "Escape") {
      event.preventDefault();
      this.close({
        restoreFocus: true,
        cancelled: true
      });
      return;
    }
    this.trapFocus(event);
  },
  init() {
    window.addEventListener("age-verification:close", () => {
      this.close({
        restoreFocus: false,
        cancelled: false
      });
    });
    window.addEventListener(
      "age-verification:require-confirmation",
      (event) => {
        this.requireConfirmation(event.detail?.callback || null);
      }
    );
    window.addEventListener("age-verification:open-info", () => {
      this.openInfo();
    });
  }
});
var age_verification_modal_default = ageVerificationModal;
var bypassAgeCheck = false;
document.addEventListener(
  "click",
  function(e) {
    const button = e.target.closest(".shopify-payment-button__button");
    if (!button) return;
    if (bypassAgeCheck) return;
    const container = button.closest("[data-requires-age-verification]");
    if (!container || container.dataset.requiresAgeVerification !== "true")
      return;
    e.preventDefault();
    e.stopImmediatePropagation();
    window.ageVerificationModal?.requireConfirmation(() => {
      bypassAgeCheck = true;
      button.blur();
      const event = new MouseEvent("click", {
        view: window,
        bubbles: true,
        cancelable: true
      });
      button.dispatchEvent(event);
      bypassAgeCheck = false;
    });
  },
  true
);
window.openAgeVerificationInfo = () => {
  if (document.querySelector("dialog.quickbuy-drawer[open]")) {
    window.dispatchEvent(new Event("quickbuy-toggle"));
  }
  if (window.location.pathname !== "/cart") {
    window.dispatchEvent(new CustomEvent("cart-drawer:temporarily-close"));
  }
  document.querySelectorAll("dialog[open]").forEach((d) => d.close());
  document.body.style.overflow = "";
  requestAnimationFrame(() => {
    window.dispatchEvent(new CustomEvent("age-verification:open-info"));
  });
};

// src/scripts/utilities/alpine-defer.js
function alpineDefer(selector = "[data-alpine-defer]") {
  const deferredElements = document.querySelectorAll(selector);
  deferredElements.forEach((element) => {
    Alpine.destroyTree(element);
    element.removeAttribute("x-ignore");
    Alpine.initTree(element);
  });
}
var alpine_defer_default = alpineDefer;

// src/scripts/theme.js
window.DefaultSplide = DefaultSplide;
window.alpineDefer = alpine_defer_default;
window.Alpine = module_default;
module_default.plugin(module_default3);
module_default.plugin(module_default4);
module_default.plugin(module_default6);
module_default.plugin(module_default5);
module_default.plugin(src_default6);
module_default.start();
module_default.data("ageVerificationModal", age_verification_modal_default);
module_default.data("header", header_default);
module_default.data("navigation", navigation_default);
module_default.data("product", product_default);
module_default.data("buyButtons", buy_buttons_default);
module_default.data("inventoryNotice", inventory_notice_default);
module_default.data("range", custom_range_default);
module_default.data("purchaseOptions", purchase_options_default);
module_default.data("note", note_default);
/*! Bundled license information:

@splidejs/splide/dist/js/splide.esm.js:
  (*!
   * Splide.js
   * Version  : 4.1.4
   * License  : MIT
   * Copyright: 2022 Naotoshi Fujita
   *)

@splidejs/splide-extension-auto-scroll/dist/js/splide-extension-auto-scroll.esm.js:
  (*!
   * @splidejs/splide-extension-auto-scroll
   * Version  : 0.5.3
   * License  : MIT
   * Copyright: 2022 Naotoshi Fujita
   *)
*/
